import styled from '@emotion/styled';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import VirtualKeyboard from '../../common/VirtualKeyboard';
import whiteDropdown from '../../images/white-dropdown.svg';
import addDisplayConditionIcon from '../../assets/images/gridicons_add-outline.svg';
import CrossIcon from '../../assets/images/cross.svg';
import { CustomButton } from '../../common/commonStyled';
import CustomPopup from '../../common/CustomPopup';
import {
  ActionIcon,
  Card,
  ChoiceButton,
  Heading,
  Icon,
  QuestionWrapper,
  SideNav,
  Text,
  Button as MuiButton,
  ActionButton
} from '../../common';
import { Checkbox, InputText, Radio, TextArea, Switch, TaskMenu } from '../../components/form';

import Dropdown from '../../components/form/TaskTypeOption';
import EditingTag from '../Project/EditingTag';
import DesignDropdown from './DesignDropdown';
import { ButtonGroup, Button, Box, Typography } from '@mui/material';
import { index, lab } from 'd3';
import { faL } from '@fortawesome/free-solid-svg-icons';
import { Title } from '@mui/icons-material';
import { width } from '@mui/system';
import 'react-responsive-modal/styles.css';
import Modal from 'react-responsive-modal';
import ConditionalDisplayLogic from './ConditionalDisplayLogic';
import LogicDisplaySection from './LogicDisplaySection';

// Edit API: choices / deleted_choice_ids as JSON (types 1, 2, 7)
const CHOICE_JSON_TYPES = [1, 2, 7];
// Edit-only API: choices / deleted_choice_ids / questionLikertScaleName as JSON (types 4, 14 — not used on create)
const LIKERT_EDIT_JSON_TYPES = [4];
const RANKING_EDIT_JSON_TYPES = [14];
const SCALE_EDIT_JSON_TYPES = [...LIKERT_EDIT_JSON_TYPES, ...RANKING_EDIT_JSON_TYPES];
const AUTOMATION_TYPES = [1, 7];

const persistId = (primary, fallback) => {
  const id = primary ?? fallback;
  return id != null && id !== '' && !isNaN(Number(id)) && Number(id) > 0 ? Number(id) : null;
};

const getDeletedChoiceIds = (initialRows = [], currentRows = []) => {
  const toSet = rows =>
    new Set((rows || []).map(c => persistId(c.choiceName_ID, c.id)).filter(Boolean).map(String));
  const initial = toSet(initialRows);
  const current = toSet(currentRows);
  return [...initial].filter(id => !current.has(id)).map(Number);
};

const resolveAutomatedFlag = (questionTypeId, index, { choice, autoIndices, autoEnabled } = {}) => {
  if (!AUTOMATION_TYPES.includes(Number(questionTypeId))) return 0;
  if (autoIndices != null) return autoEnabled && autoIndices.includes(index) ? 1 : 0;
  return Number(choice?.isAutomatedAnswer) === 1 ? 1 : 0;
};

const getChoiceText = choice => String(choice?.choiceName ?? choice?.text ?? '').trim();

/** Resolve NPS scale label/value from saved id or 0-10 index. Never returns findIndex -1. */
const resolveNpsScaleSelection = (scales, rawId) => {
  const id = Array.isArray(rawId) ? rawId[0] : rawId;
  if (id == null || id === '') return null;
  const list = scales || [];
  const asIndex = Number(id);
  if (Number.isInteger(asIndex) && asIndex >= 0 && asIndex <= 10 && list[asIndex]) {
    const byId = list.findIndex(s => String(s.questionLikertScale_ID) === String(id));
    if (byId < 0 || list[asIndex].questionLikertScale_ID == null) {
      return { label: `${asIndex}`, value: list[asIndex].questionLikertScale_ID ?? asIndex };
    }
  }
  const byIdIndex = list.findIndex(s => String(s.questionLikertScale_ID) === String(id));
  if (byIdIndex < 0) return null;
  return { label: `${byIdIndex}`, value: list[byIdIndex].questionLikertScale_ID ?? byIdIndex };
};

const getChoiceShuffleFlag = choice =>
  Number(choice?.toShuffle ?? choice?.toShuffleChoice) === 1 ? 1 : 0;

const setChoiceShuffleState = (choice, checked) => ({
  ...choice,
  toShuffle: checked ? 1 : 0,
  toShuffleChoice: checked ? 1 : 0
});

const normalizeChoiceToShuffle = (choice, shuffleQuestionEnabled = 0) => {
  if (Number(shuffleQuestionEnabled) !== 1) {
    return 0;
  }
  return getChoiceShuffleFlag(choice);
};

const SHUFFLE_VALIDATION_QUESTION_TYPES = [1, 2, 4, 7, 14];

const isCustomLikertScaleType = scaleType => scaleType === 'matrix' || scaleType === 'Custom Likert';
const isMatrixScaleType = scaleType => scaleType === 'matrix';

const getScaleText = item => String(item?.questionLikertScaleName ?? item?.text ?? '').trim();

const getScaleShuffleFlag = item =>
  Number(item?.shuffleScale ?? item?.ShuffleScale ?? item?.toShuffleScale) === 1 ? 1 : 0;

const setScaleShuffleState = (item, checked) => ({
  ...item,
  shuffleScale: checked ? 1 : 0
});

const mergeScaleShuffleFromApi = (options = [], apiRows = []) =>
  (options || []).map((opt, i) => {
    const hasExplicit = opt?.shuffleScale !== undefined && opt?.shuffleScale !== null;
    return {
      ...opt,
      shuffleScale: hasExplicit ? getScaleShuffleFlag(opt) : getScaleShuffleFlag(apiRows?.[i])
    };
  });

const normalizeScaleShuffle = (item, shuffleEnabled = 0, scaleType = '') => {
  if (!isMatrixScaleType(scaleType) || Number(shuffleEnabled) !== 1) {
    return 0;
  }
  return getScaleShuffleFlag(item);
};

const countChoicesWithText = choices =>
  (choices || []).filter(choice => getChoiceText(choice)).length;

const countShuffledChoicesWithText = choices =>
  (choices || []).filter(choice => getChoiceText(choice) && getChoiceShuffleFlag(choice) === 1).length;

const countShuffledScalesWithText = rows =>
  (rows || []).filter(item => {
    const text = getScaleText(item);
    return text && !text.startsWith('Enter Parameter') && getScaleShuffleFlag(item) === 1;
  }).length;

const getShuffleChoiceValidationMessage = (questionTypeId, shuffleQuestionEnabled, choices) => {
  if (!SHUFFLE_VALIDATION_QUESTION_TYPES.includes(Number(questionTypeId)) || Number(shuffleQuestionEnabled) !== 1) {
    return '';
  }
  if (countChoicesWithText(choices) < 2) {
    return 'A minimum of 2 choices should be present to enable shuffling.';
  }
  const shuffledCount = countShuffledChoicesWithText(choices);
  if (shuffledCount === 0) return 'Select options to shuffle.';
  if (shuffledCount === 1) return 'Select a minimum of 2 options to enable shuffling.';
  return '';
};

const getShuffleScaleValidationMessage = (questionTypeId, scaleType, shuffleEnabled, scaleRows) => {
  if (Number(questionTypeId) !== 4 || !isMatrixScaleType(scaleType) || Number(shuffleEnabled) !== 1) {
    return '';
  }
  const shuffledCount = countShuffledScalesWithText(scaleRows);
  if (shuffledCount === 0) return 'Select options to shuffle.';
  if (shuffledCount === 1) return 'Select a minimum of 2 options to enable shuffling.';
  return '';
};

const shouldShowScaleShuffle = (opt, edit, shuffleEnabled) => {
  if (Number(shuffleEnabled) !== 1) return false;
  if (edit) return true;
  return getScaleShuffleFlag(opt) === 1;
};

const buildChoiceApiObjects = ({
  questionTypeId,
  rows = [],
  isResponded = 0,
  isNewChoice,
  autoIndices,
  autoEnabled,
  shuffleEnabled = 0
}) => {
  const typeId = Number(questionTypeId);
  const toObj = (choice, index, includeId) => {
    const obj = {
      text: getChoiceText(choice),
      isAutomatedAnswer: resolveAutomatedFlag(typeId, index, { choice, autoIndices, autoEnabled }),
      toShuffleChoice: normalizeChoiceToShuffle(choice, shuffleEnabled)
    };
    if (includeId) {
      const id = persistId(choice.choiceName_ID, choice.id);
      if (id != null) obj.id = id;
    }
    return obj;
  };
  if (Number(isResponded) === 1) {
    return rows
      .map((choice, index) => (getChoiceText(choice) && isNewChoice?.(choice) ? toObj(choice, index, false) : null))
      .filter(Boolean);
  }
  return rows
    .map((choice, index) => (getChoiceText(choice) ? toObj(choice, index, true) : null))
    .filter(Boolean);
};

const buildAddQuestionChoiceObjects = ({
  rows = [],
  isResponded = 0,
  isNewChoice,
  shuffleEnabled = 0
}) => {
  const sourceRows = (rows || []).filter(choice => getChoiceText(choice));
  const filteredRows =
    Number(isResponded) === 1 ? sourceRows.filter(choice => isNewChoice?.(choice)) : sourceRows;

  return filteredRows.map(choice => ({
    choiceName: getChoiceText(choice),
    toShuffleChoice: normalizeChoiceToShuffle(choice, shuffleEnabled)
  }));
};

const hasJsonChoiceChanges = ({
  questionTypeId,
  originalRows = [],
  currentRows = [],
  shuffleEnabled = 0
}) => {
  const orig = buildChoiceApiObjects({ questionTypeId, rows: originalRows, shuffleEnabled });
  const curr = buildChoiceApiObjects({ questionTypeId, rows: currentRows, shuffleEnabled });
  return (
    JSON.stringify(orig) !== JSON.stringify(curr) || getDeletedChoiceIds(originalRows, currentRows).length > 0
  );
};

const appendJsonChoiceFields = (formData, { choices, deletedIds }) => {
  formData.append('choices', JSON.stringify(choices));
  formData.append('deleted_choice_ids', JSON.stringify(deletedIds));
};

const resolveLikertScaleType = question => question?.scaleType || question?.LikertScaleType || '';

const shouldOmitCustomLikertScaleIds = ({ original, current, originalRows = [], currentRows = [] }) => {
  const currentType = resolveLikertScaleType(current);
  if (currentType !== 'Custom Likert') return false;
  const originalType = resolveLikertScaleType(original);
  return (
    originalType !== 'Custom Likert' ||
    originalType !== currentType ||
    (originalRows?.length || 0) !== (currentRows?.length || 0)
  );
};

const buildLikertScaleApiObjects = (
  rows = [],
  { includeIds = true, shuffleEnabled = 0, scaleType = '' } = {}
) =>
  (rows || [])
    .filter(item => {
      const text = getScaleText(item);
      return text && !text.startsWith('Enter Parameter');
    })
    .map(item => {
      const obj = {
        text: item.questionLikertScaleName ?? item.text,
        shuffleScale: normalizeScaleShuffle(item, shuffleEnabled, scaleType)
      };
      if (includeIds) {
        const id = persistId(item.questionLikertScale_ID, item.id);
        if (id != null) obj.id = id;
      }
      return obj;
    });

const resolveLikertScaleRows = taskQuestion => {
  const options = taskQuestion?.likertScaleOptions;
  const apiRows = taskQuestion?.likertScaleName_array || [];
  if (options?.length) {
    return mergeScaleShuffleFromApi(options, apiRows);
  }
  return (apiRows || []).map(opt => ({
    ...opt,
    shuffleScale: getScaleShuffleFlag(opt)
  }));
};

const resolveRankingScaleRows = (taskQuestion, fallbackRows = []) => {
  const rows = taskQuestion?.likertScaleName_array || taskQuestion?.likertScaleOptions;
  return rows?.length ? rows : fallbackRows;
};

const resolveScaleRowsForEdit = (taskQuestion, questionTypeId, fallbackRows = []) =>
  Number(questionTypeId) === 14
    ? resolveRankingScaleRows(taskQuestion, fallbackRows)
    : resolveLikertScaleRows(taskQuestion);

const appendLikertEditFields = (formData, { choices, deletedChoiceIds, likertScale, likertScaleType }) => {
  appendJsonChoiceFields(formData, { choices, deletedIds: deletedChoiceIds });
  formData.append('questionLikertScaleName', JSON.stringify(likertScale));
  if (likertScaleType) formData.append('LikertScaleType', likertScaleType);
};

const hasJsonLikertEditChanges = ({
  questionTypeId,
  originalChoiceRows = [],
  currentChoiceRows = [],
  originalLikertRows = [],
  currentLikertRows = [],
  shuffleEnabled = 0,
  scaleType = ''
}) =>
  hasJsonChoiceChanges({
    questionTypeId,
    originalRows: originalChoiceRows,
    currentRows: currentChoiceRows,
    shuffleEnabled
  }) ||
  JSON.stringify(buildLikertScaleApiObjects(originalLikertRows, { shuffleEnabled, scaleType })) !==
    JSON.stringify(buildLikertScaleApiObjects(currentLikertRows, { shuffleEnabled, scaleType }));

const usesEditChoiceJson = (typeId, isEdit) =>
  isEdit && (CHOICE_JSON_TYPES.includes(typeId) || SCALE_EDIT_JSON_TYPES.includes(typeId));

const StyledCheckbox = styled.label`
  display: flex;
  align-items: center;

  .outer {
    height: ${props => props.outerHeight || '13px'};
    width: ${props => props.outerWidth || '13px'};
    border-radius: ${props => props.borderRadius || '1px'};
    border: ${props => props.border || '1px solid #e2e2e2'};
    border-color: ${props => props.color || '#e2e2e2'};
    background: ${props => props.inactiveColor || 'transparent'};
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-bottom: 1px;
    margin-right: 5px;
    // margin-left: 5px;
    // margin-top: 3px;
    cursor: pointer; // Set default cursor style to pointer
  }

  .inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-54%, -48%);
    z-index: 1;
    opacity: 0;
    color: var(--white);
    font-size: ${props => props.iconSize || '12px'};
  }

  .text {
    font-size: ${props => props.fontSize || '12px'};
    font-weight: ${props => props.fontWeight || '500'};
    color: ${props => props.color || 'var(--quadColor)'};
  }

  input[disabled] + .outer {
    cursor: not-allowed; // Change cursor style to not-allowed when checkbox is disabled
  }

  input {
    display: none;

    :checked ~ .outer {
      border: 1px solid var(--primaryColor);
      border-color: ${props => props.color || 'var(--primaryColor)'};
      background: ${props => props.color || 'var(--primaryColor)'};
    }

    :checked ~ .outer .inner {
      opacity: 1;
    }

    :checked ~ .text {
      color: ${props => props.color || 'var(--primaryColor)'};
      font-weight: ${props => props.fontWeight || '800'};
    }
  }
`;

const Tag = styled.div`
  border: 1px solid var(--greyBlue);
  padding: 3px 10px;
  display: flex;
  align-items: center;
  border-radius: 15px;

  p {
    font-size: 10px;
    font-weight: 700;
    color: var(--greyBlue);
    padding: 0;
    margin: 0;
    padding-right: 3px;
  }
  span {
    font-size: 12px;
    padding: 0;
    margin: 0;
    color: var(--greyBlue);
  }
`;
const TagButton = styled(CustomButton)`
  border: none;
  display: flex;
  align-items: center;
  color: #8a8a8a;
  padding: 4px 8px;

  span {
    font-size: 17px;
    color: #8a8a8a;
    padding-right: 3px;
  }
`;
const CustomButtont = styled(MuiButton)`
  padding: 2px 1rem;
  color: var(--white);
  buttoncolor: var(--sapGreen);
  cursor: pointer;
`;

const LikertDiv = styled.div`
  margin-top: 2rem;

  @media (max-width: 1355px) {
    margin-top: 5rem;
  }
`;

const TooltipCard = styled.div`
  z-index: 10;
  background: #ffffff;
  color: var(--greyBlue);
  font-size: smaller;
  font-weight: 600;
  border: 1px solid #efefef;
  box-sizing: border-box;
  box-shadow: 0px 12px 28px rgba(0, 0, 0, 0.15);
  margin-left: -20px;
  padding: 5px 10px;
  position: absolute;
  transform: translateX(-4%) translateY(calc(-100% - 1px)); /* Move up including the triangle height */

  &::after {
    content: '';
    position: absolute;
    bottom: -10px; /* Position triangle at the bottom of CustomCard */
    left: 50%;
    margin-left: -10px; /* Center the triangle */
    border-width: 5px;
    border-style: solid;
    border-color: #ffffff transparent transparent transparent;
  }
`;

const Tooltip = ({ active, children }) => {
  return active ? <TooltipCard>{children}</TooltipCard> : null;
};

// Styled file input wrapper to match design system
const FileInputWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin: 12px 0;
  width: 100%;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.475rem 1.125rem;
  background: transparent;
  color: var(--sapGreen);
  border: 1px solid var(--sapGreen);
  border-radius: 0.3125rem;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: box-shadow 250ms ease;
  min-width: 120px;
  text-align: center;
  height: auto;

  &:hover {
    box-shadow: 0px 2px 8px 1px rgba(0, 0, 0, 0.2);
    background: var(--sapGreen);
    color: var(--white);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.15);
  }
`;

const HiddenFileInput = styled.input`
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
`;

const FileNameDisplay = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: var(--greyBlue);
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  display: inline-block;
`;

const generateUniqueID = () =>
  `${Date.now()}-${Math.random()
    .toString(36)
    .substr(2, 9)}`;

const SHUFFLE_TAG_ACTIVE_BG = '#9EBE21';
const SHUFFLE_TAG_INACTIVE_BG = '#E0E0E0';
const SHUFFLE_TAG_INACTIVE_TEXT = '#666666';

const ChoiceShufflePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  background: ${props => (props.active ? SHUFFLE_TAG_ACTIVE_BG : SHUFFLE_TAG_INACTIVE_BG)};
  color: ${props => (props.active ? '#fff' : SHUFFLE_TAG_INACTIVE_TEXT)};
  user-select: none;
  line-height: 1;
`;

const ViewShuffleTag = styled.span`
  display: inline-block;
  width: 22px;
  height: 12px;
  border-radius: 6px;
  background: ${SHUFFLE_TAG_ACTIVE_BG};
  flex-shrink: 0;
  vertical-align: middle;
`;

const ChoiceLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  line-height: 1.25;
`;

const ChoiceOptionRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  margin: 0.25rem 0;
  gap: 4px;
`;

const shouldShowChoiceShuffle = (opt, edit, shuffleEnabled) => {
  if (Number(shuffleEnabled) !== 1) return false;
  if (edit) return true;
  return getChoiceShuffleFlag(opt) === 1;
};

const renderChoiceShuffleIndicator = (opt, oIndex, edit, onToggleChoiceShuffle) => (
  <ChoiceShufflePillIndicator
    active={getChoiceShuffleFlag(opt) === 1}
    edit={edit}
    onClick={() => onToggleChoiceShuffle?.(oIndex)}
  />
);

const ChoiceShufflePillIndicator = ({ active, edit, onClick, disabled = false }) => {
  if (!edit) {
    if (!active) return null;
    return <ViewShuffleTag title="Shuffle" />;
  }

  return (
    <ChoiceShufflePill
      active={active}
      clickable={!disabled}
      style={disabled ? { cursor: 'not-allowed', opacity: 0.65 } : undefined}
      onClick={event => {
        event.stopPropagation();
        if (disabled) return;
        onClick?.();
      }}
    >
      Shuffle
    </ChoiceShufflePill>
  );
};

const SingleChoiceQuestion = ({
  options,
  edit = false,
  handleOptions,
  onChoiceFocus,
  shuffleEnabled = 0,
  onToggleChoiceShuffle
}) => {
  const [choices, setChoices] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    setChoices(options);
  }, [options]);

  let className = 'col-md-12';
  // if (choices?.length > 4) {
  //   className = 'col-md-4';
  // }

  // Sageer's code
  useEffect(() => {
    if (edit && choices.length === 0) {
      setChoices([{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }]);
    }
  }, [edit, choices.length]);

  const handleRadioChange = index => {
    setSelectedOption(index);
  };

  const addChoice = () => {
    let tempChoices = choices?.slice();
    if (tempChoices == null) {
      tempChoices = [{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    } else {
      tempChoices = [...tempChoices, { choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    }
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleSingleChoiceOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleDeleteOption = index => {
    const tempChoices = (choices || []).filter((_, i) => i !== index);
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  return (
    <div className="options-list mt-2">
      <div className="row">
        <div className="col-md-12 px-0">
          {choices?.map((opt, oIndex) => (
            <ChoiceOptionRow key={opt.choiceName_ID}>
              <Radio
                checked={selectedOption === oIndex}
                onChange={() => handleRadioChange(oIndex)}
                className="mb-0 pb-0"
                fontSize="0.875rem"
              >
                {!edit ? (
                  <ChoiceLabelRow>
                    <span
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {opt.choiceName}
                    </span>
                    {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                      renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                  </ChoiceLabelRow>
                ) : null}
              </Radio>
              {edit && (
                <>
                  <EditOption
                    optionIndex={oIndex}
                    value={opt.choiceName}
                    handleOption={handleSingleChoiceOption}
                    handleDeleteOption={handleDeleteOption}
                    onFocus={onChoiceFocus}
                  />
                  {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                    renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                </>
              )}
            </ChoiceOptionRow>
          ))}
        </div>

        {edit && (
          <div className={className}>
            <ChoiceButton onClick={e => addChoice()}>
              <Icon className="material-icons-round">add</Icon> Add choice
            </ChoiceButton>
          </div>
        )}
      </div>
    </div>
  );
};

const MultipleChoiceQuestion = ({
  options,
  edit,
  handleOptions,
  onChoiceFocus,
  shuffleEnabled = 0,
  onToggleChoiceShuffle
}) => {
  const [choices, setChoices] = useState(options);

  useEffect(() => {
    setChoices(options);
  }, [options]);

  let className = 'col-md-12';
  // if (choices?.length > 4) {
  //   className = 'col-md-4';
  // }

  useEffect(() => {
    if (edit && choices.length === 0) {
      setChoices([{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }]);
    }
  }, [edit, choices.length]);

  const addChoice = () => {
    let tempChoices = choices?.slice();
    if (tempChoices == null) {
      tempChoices = [{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    } else {
      tempChoices = [...tempChoices, { choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    }
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleMultipleChoiceOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleDeleteOption = index => {
    const tempChoices = (choices || []).filter((_, i) => i !== index);
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  return (
    <div className="options-list mt-3">
      <div className="row">
        <div className="col-md-12 px-0">
          {choices?.map((opt, oIndex) => (
            <ChoiceOptionRow key={opt.choiceName_ID}>
              <Checkbox fontSize="0.875rem" className="mb-0 pb-0" wrapText={true}>
                {!edit ? (
                  <ChoiceLabelRow>
                    <span
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {opt.choiceName}
                    </span>
                    {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                      renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                  </ChoiceLabelRow>
                ) : null}
              </Checkbox>
              {edit && (
                <>
                  <EditOption
                    optionIndex={oIndex}
                    value={opt.choiceName}
                    handleOption={handleMultipleChoiceOption}
                    handleDeleteOption={handleDeleteOption}
                    onFocus={onChoiceFocus}
                  />
                  {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                    renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                </>
              )}
            </ChoiceOptionRow>
          ))}
        </div>
        {edit && (
          <div className={className}>
            <ChoiceButton className="mt-3" onClick={e => addChoice()}>
              <Icon className="material-icons-round">add</Icon> Add choice
            </ChoiceButton>
          </div>
        )}
      </div>
    </div>
  );
};

const SliderQuestion = ({ options, edit = false, handleOptions }) => {
  const [choices, setChoices] = useState(options || []);

  useEffect(() => {
    setChoices(options || []);
  }, [options]);

  let className = 'col-md-12';
  if (choices?.length > 4) {
    className = 'col-md-4';
  }

  const handleSliderOption = (index, value) => {
    let tempChoices = choices;
    tempChoices[index].choiceName = value;

    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  return (
    <div className="options-list mt-3">
      <div className="row">
        <div className={choices?.length > 4 ? 'col-md-12' : 'col-md-7'}>
          <div className="d-flex" style={{ flex: 1, paddingTop: 10, justifyContent: 'center', paddingBottom: 10 }}>
            <div style={{ flex: 0.3, paddingTop: 10 }}>
              <Text type="4" fontWeight="800" color="var(--headingColor)">
                Minimum Value
              </Text>
            </div>
            <div style={{ flex: 0.7 }}>
              <InputText
                type="number"
                disabled={edit ? false : true}
                showCharacters={false}
                placeholder="Add Minimum Value"
                defaultValue={choices[0].choiceName}
                onChange={e => handleSliderOption(0, e.target.value)}
                data-no-dnd="true"
              />
            </div>
          </div>
          <div className="d-flex" style={{ flex: 1, justifyContent: 'center' }}>
            <div style={{ flex: 0.3, paddingTop: 10 }}>
              <Text type="4" fontWeight="800" color="var(--headingColor)">
                Maximum Value
              </Text>
            </div>
            <div style={{ flex: 0.7 }}>
              <InputText
                type="number"
                disabled={edit ? false : true}
                showCharacters={false}
                placeholder="Add Maximum Value"
                defaultValue={choices[1].choiceName}
                onChange={e => handleSliderOption(1, e.target.value)}
                data-no-dnd="true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const tempLikerTOptions = [
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Dislike Very Much'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Dislike'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Moderately Dislike'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Neutral'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Moderately Like'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Like'
  },
  {
    questionLikertScale_ID: null,
    questionLikertScaleName: 'Like Very Much'
  }
];
const getNpsScaleOptions = () => {
  return [
    { questionLikertScale_ID: null, questionLikertScaleName: 'Not at all Likely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Unlikely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Very Unlikely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Unlikely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Unlikely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Likely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Likely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Very Likely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Quite Likely' },
    { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Likely' }
  ];
};
const getLikertScaleOptions = (scaleSize, type = 'agree-disagree') => {
  if (type === 'matrix' || type === 'Custom Likert') {
    const options = [];
    for (let i = 0; i < scaleSize; i++) {
      options.push({ questionLikertScale_ID: null, questionLikertScaleName: '', shuffleScale: 0 });
    }
    return options;
  }
  if (type === 'important-not-important') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not at all Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not so Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Important' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not at all Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not so Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Quite Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Important' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not at all Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Not so Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Unimportant' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neither Important nor Unimportant' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Quite Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Important' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Important' }
        ];
      default:
        return [];
    }
  }

  if (type === 'satisfied-dissatisfied') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Satisfied' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Satisfied' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Dissatisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Satisfied' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Completely Satisfied' }
        ];
      default:
        return [];
    }
  }

  if (type === 'likely-unlikely') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neither Likely nor Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Likely' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neither Likely nor Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Likely' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Unlikely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Quite Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Likely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely Likely' }
        ];
      default:
        return [];
    }
  }
  if (type === 'like-dislike') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike very much' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like very much' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike Very Much' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like Very Much' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike Very Much' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Midly Dislike' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Midly Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like considerably' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Like Very Much' }
        ];
      default:
        return [];
    }
  }
  if (type === 'always-never') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Never' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Rarely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Sometimes' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Usually' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Always' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Never' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Rarely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Occasionally' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Sometimes' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Usually' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Almost Always' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Always' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Never' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Almost Never' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Rarely' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Occasionally' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Sometimes' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Usually' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Frequently' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Frequently' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Almost Always' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Always' }
        ];
      default:
        return [];
    }
  }
  if (type === 'positive-negative') {
    switch (scaleSize) {
      case 5:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Positive' }
        ];
      case 7:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely positive' }
        ];
      case 10:
        return [
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Quite negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Negative' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Moderately positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Quite positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Very Positive' },
          { questionLikertScale_ID: null, questionLikertScaleName: 'Extremely positive' }
        ];
      default:
        return [];
    }
  }
  if (type === 'matrix' || type === 'Custom Likert') {
    const options = [];
    for (let i = 0; i < scaleSize; i++) {
      options.push({ questionLikertScale_ID: null, questionLikertScaleName: `Enter Parameter ${i + 1}` });
    }
    return options;
  }
  switch (scaleSize) {
    case 5:
      return [
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Agree' }
      ];
    case 7:
      return [
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Agree' }
      ];
    case 10:
      return [
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Disagree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Neutral' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Slightly Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Somewhat Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Strongly Agree' },
        { questionLikertScale_ID: null, questionLikertScaleName: 'Completely Agree' }
      ];

    default:
      return [];
  }
};

const SCALE_TYPES = [
  { value: 'agree-disagree', label: 'Agree-Disagree' },
  { value: 'satisfied-dissatisfied', label: 'Satisfied-Dissatisfied' },
  { value: 'like-dislike', label: 'Like-Dislike' },
  { value: 'positive-negative', label: 'Positive-Negative' },
  { value: 'always-never', label: 'Always-Never' },
  { value: 'likely-unlikely', label: 'Likely-Unlikely' },
  { value: 'important-not-important', label: 'Important-Not Important' },
  { value: 'matrix', label: 'Matrix' },
  { value: 'Custom Likert', label: 'Custom Likert' }
];

const LikerTQuestion = ({
  options,
  likerTOptions,
  edit = false,
  handleOptions,
  handleScaleOptions,
  initialScaleType,
  customLikertError,
  onChoiceFocus,
  onLikertCustomFocus,
  shuffleEnabled = 0,
  onToggleScaleShuffle,
  onToggleChoiceShuffle,
  scaleShuffleDisabled = false
}) => {
  const [scaleSize, setScaleSize] = useState();
  const [scaleType, setScaleType] = useState(initialScaleType || null);
  const [choices, setChoices] = useState(options || ['']);
  const [scaleOptions, setScaleOptions] = useState(likerTOptions || []);
  const [scaletypeVisible, setScaletypeVisible] = useState(false);

  // Keep local scale labels in sync when parent updates them (e.g. virtual keyboard writes to taskQuestion.likertScaleOptions).
  useEffect(() => {
    const incoming = Array.isArray(likerTOptions) ? likerTOptions : [];
    setScaleOptions(prev => {
      if (
        prev?.length === incoming.length &&
        prev.every(
          (p, i) =>
            (p?.questionLikertScaleName ?? '') === (incoming[i]?.questionLikertScaleName ?? '') &&
            getScaleShuffleFlag(p) === getScaleShuffleFlag(incoming[i])
        )
      ) {
        return prev;
      }
      return incoming;
    });
  }, [likerTOptions]);

  useEffect(() => {
    // Hydrate scale size/type from saved API data on edit open
    if (!scaleSize && likerTOptions?.length) setScaleSize(likerTOptions.length);
    if (!scaleType && initialScaleType) setScaleType(initialScaleType);
  }, [likerTOptions?.length, initialScaleType]);

  useEffect(() => {
    if (!options || options.length === 0) {
      const defaultChoice = [{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
      setChoices(defaultChoice);
      handleOptions(defaultChoice);
    } else {
      setChoices(options);
    }
  }, [options]);

  const handleScaleChange = size => {
    if (size === scaleSize) return;
    setScaleSize(size);
    setScaletypeVisible(false); // size selected — clear the error
    if (scaleType) {
      const newScaleOptions = getLikertScaleOptions(size, scaleType);
      setScaleOptions(newScaleOptions);
      handleScaleOptions(newScaleOptions, scaleType);
    }
  };

  const handleTypeChange = type => {
    if (type === scaleType) return;
    setScaleType(type);
    if (scaleSize) {
      setScaletypeVisible(false);
      const newScaleOptions = getLikertScaleOptions(scaleSize, type);
      setScaleOptions(newScaleOptions);
      handleScaleOptions(newScaleOptions, type);
    } else {
      // Type chosen but no size yet — show the error
      setScaletypeVisible(true);
      setScaleOptions([]);
      handleScaleOptions([], type);
    }
  };

  const addChoice = () => {
    let tempChoices = choices.slice();
    tempChoices = [...tempChoices, { choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleLikerTOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleDeleteOption = index => {
    const tempChoices = (choices || []).filter((_, i) => i !== index);
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleCustomScaleChange = (index, value) => {
    // Replace commas with semicolons to prevent payload corruption.The questionLikertScaleName field is sent as a comma-separated string;
    // if parameter names contain commas the server creates extra scale entries.
    const sanitizedValue = value.replace(/,/g, ';');
    const newScaleOptions = [...scaleOptions];
    newScaleOptions[index] = {
      ...newScaleOptions[index],
      questionLikertScaleName: sanitizedValue,
      shuffleScale: getScaleShuffleFlag(newScaleOptions[index])
    };
    setScaleOptions(newScaleOptions);
    handleScaleOptions(newScaleOptions, scaleType);
  };

  const handleToggleChoiceShuffleLocal = index => {
    if (scaleShuffleDisabled) return;
    const nextChecked = getChoiceShuffleFlag(choices[index]) !== 1;
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? setChoiceShuffleState(choice, nextChecked) : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleToggleScaleShuffleLocal = index => {
    if (scaleShuffleDisabled) return;
    const nextChecked = getScaleShuffleFlag(scaleOptions[index]) !== 1;
    const newScaleOptions = scaleOptions.map((opt, i) =>
      i === index ? setScaleShuffleState(opt, nextChecked) : { ...opt, shuffleScale: getScaleShuffleFlag(opt) }
    );
    setScaleOptions(newScaleOptions);
    handleScaleOptions(newScaleOptions, scaleType);
  };

  const buttonStyle = {
    border: '1px solid #8A8A8A',
    backgroundColor: 'white',
    color: 'rgba(137, 137, 137, 1)',
    marginLeft: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '13px',
    height: '28px'
  };

  const selectedButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#B3DB1A',
    color: 'white',
    border: '1px solid #B3DB1A'
  };

  return (
    <div className="options-list mt-2">
      <div className="row">
        {edit && (
          <>
            <div className="d-flex flex-column gap-3 ">
              <div className="d-flex align-items-center">
                <Text type="6" style={{ marginRight: '16px', minWidth: '100px' }}>
                  Select Scale:
                </Text>
                <div className="d-flex mt-2">
                  {[5, 7, 10].map(size => (
                    <button
                      key={size}
                      style={scaleSize === size ? selectedButtonStyle : buttonStyle}
                      onClick={() => handleScaleChange(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              <div className="d-flex align-items-center">
                <Text type="6" style={{ marginRight: '16px', width: '200px' }}>
                  Select Text Value:
                </Text>
                <div
                  className="d-flex flex-wrap mt-2"
                  style={{
                    gap: '8px',
                    justifyContent: 'flex-start',
                    marginLeft:'14px'
                  }}
                >
                  {SCALE_TYPES.map(type => (
                    <button
                      key={type.value}
                      style={{
                        ...buttonStyle,
                        ...(scaleType === type.value ? selectedButtonStyle : {}),
                        flex: '0 1 auto',
                        minWidth: '100px',
                        textAlign: 'center'
                      }}
                      onClick={() => handleTypeChange(type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {scaletypeVisible && (
                <p className="mt-1" style={{ color: 'red', fontSize: '11px', marginLeft: '8px' }}>
                  Select scale size
                </p>
              )}
              
              {(scaleType === 'matrix' || scaleType === 'Custom Likert') && (
                <div className="mt-2" style={{ marginLeft: '16px' }}>
                  <Text type="6" className="mb-2">
                    Custom Parameters:
                    {customLikertError && (
                      <span style={{ color: 'red', fontSize: '12px', marginLeft: '10px' }}>
                        {customLikertError}
                      </span>
                    )}
                  </Text>
                  {scaleOptions.map((opt, index) => {
                    // Calculate duplicates for styling
                    const allValues = scaleOptions.map(o => o.questionLikertScaleName);
                    const isDuplicate =
                      allValues.filter(val => val === opt.questionLikertScaleName).length > 1;

                    return (
                      <ChoiceOptionRow
                        key={index}
                        style={{ maxWidth: '480px' }}
                      >
                        <InputText
                          showCharacters={false}
                          fontSize="14px"
                          value={opt.questionLikertScaleName}
                          controlledValue
                          characterLimit={500}
                          width="300px"
                          //maxLength={100}
                          style={{
                            color: 'var(--greyBlue)',
                            fontWeight: '500',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            borderBottom:
                              customLikertError === 'Some Parameters are Same' && isDuplicate
                                ? '1px solid red'
                                : customLikertError &&
                                  (opt.questionLikertScaleName?.startsWith('Enter Parameter') ||
                                    !opt.questionLikertScaleName)
                                  ? '1px solid red'
                                  : opt.questionLikertScaleName &&
                                    !opt.questionLikertScaleName.startsWith('Enter Parameter')
                                    ? '1px solid green'
                                    : '1px solid var(--greyBlue)'
                          }}
                          placeholder={`Parameter ${index + 1}`}
                          data-vk-target="true"
                          data-virtualkeyboard-ignore="true"
                          onFocus={() => onLikertCustomFocus?.(index)}
                          onClick={() => onLikertCustomFocus?.(index)}
                          onChange={e => handleCustomScaleChange(index, e.target.value)}
                        />
                        {isMatrixScaleType(scaleType) &&
                          shouldShowScaleShuffle(opt, edit, shuffleEnabled) && (
                          <ChoiceShufflePillIndicator
                            active={getScaleShuffleFlag(opt) === 1}
                            edit={edit}
                            disabled={scaleShuffleDisabled}
                            onClick={() => handleToggleScaleShuffleLocal(index)}
                          />
                        )}
                      </ChoiceOptionRow>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-4">
              {choices?.map((opt, oIndex) => (
                <ChoiceOptionRow
                  key={opt.choiceName_ID ? String(opt.choiceName_ID) : `likert-choice-${oIndex}`}
                >
                  <EditOption
                    optionIndex={oIndex}
                    value={opt.choiceName}
                    handleOption={handleLikerTOption}
                    handleDeleteOption={handleDeleteOption}
                    isLikert={true}
                    vkIgnoreOutsideClick
                    onFocus={onChoiceFocus}
                  />
                  {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) && (
                    <ChoiceShufflePillIndicator
                      active={getChoiceShuffleFlag(opt) === 1}
                      edit={edit}
                      disabled={scaleShuffleDisabled}
                      onClick={() => handleToggleChoiceShuffleLocal(oIndex)}
                    />
                  )}
                </ChoiceOptionRow>
              ))}
            </div>

            <div className=" col-md-12 mt-3">
              <ButtonGroup>
                <ChoiceButton onClick={addChoice}>
                  <Icon className="material-icons-round">add</Icon> Add choice
                </ChoiceButton>
              </ButtonGroup>
            </div>
          </>
        )}
        {!edit && (
          <div className="col-md-12">
            <Box className="d-flex flex-column w-100">
              <Box className="d-flex flex-row w-100" sx={{ mb: 2, alignItems: 'center', gap: 5 }}>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400 }}>
                  {`Scale Size: ${likerTOptions?.length || 'Not Set'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400 }}>
                  {`Scale Type: ${
                    initialScaleType
                      ? initialScaleType
                          .split('-')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join('-')
                      : 'Not Set'
                  }`}
                </Typography>
              </Box>

              <Box className="d-flex flex-column w-100" sx={{ mb: 1, alignItems: 'start' }}>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400, textAlign: 'start' }}>
                  {`1 = ${likerTOptions?.[0]?.questionLikertScaleName || 'Strongly Disagree'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400, textAlign: 'start', mt: 1 }}>
                  {`${likerTOptions?.length} = ${likerTOptions?.[likerTOptions.length - 1]?.questionLikertScaleName ||
                    'Strongly Agree'}`}
                </Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: '8px 0', alignItems: 'center' }}>
                {choices?.map((opt, oIndex) => (
                  <React.Fragment key={oIndex}>
                    <Typography variant="h5" sx={{ color: '#8A8A8A', fontSize: '13px', textAlign: 'right', pr: 2, wordBreak: 'break-word', maxWidth: '200px' }}>
                      <ChoiceLabelRow>
                        <span>{opt.choiceName}</span>
                        {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) && (
                          <ChoiceShufflePillIndicator
                            active={getChoiceShuffleFlag(opt) === 1}
                            edit={edit}
                          />
                        )}
                      </ChoiceLabelRow>
                    </Typography>

                    <ButtonGroup variant="contained" aria-label="Likert scale">
                      {Array.from({ length: likerTOptions?.length }, (_, index) => (
                        <Button
                          key={index}
                          sx={{
                            backgroundColor: 'white',
                            color: '#8A8A8A',
                            minWidth: '40px',
                            padding: '6px 12px',
                            border: '1px solid #8A8A8A',
                            '&:hover': { backgroundColor: 'white' },
                            '&.MuiButton-root': { border: '1px solid #8A8A8A' },
                            '&:not(:first-of-type)': { borderLeft: 'none' }
                          }}
                        >
                          {index + 1}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

const NpsQuestion = ({
  options,
  likerTOptions,
  edit = false,
  handleOptions,
  handleScaleOptions,
  initialScaleType
}) => {
  const [scaleSize, setScaleSize] = useState();
  const [scaleType, setScaleType] = useState(initialScaleType || null);
  const [choices, setChoices] = useState(options || []);
  const [scaleOptions, setScaleOptions] = useState(likerTOptions || []);

  useEffect(() => {
    if (likerTOptions?.length && initialScaleType) {
      setScaleSize(likerTOptions.length);
      setScaleType(initialScaleType);
      handleScaleOptions(likerTOptions, initialScaleType);
    }
  }, [likerTOptions, initialScaleType]);

  const handleScaleChange = size => {
    setScaleSize(size);
    if (scaleType) {
      const newScaleOptions = getNpsScaleOptions();
      setScaleOptions(newScaleOptions);
      handleScaleOptions(newScaleOptions, scaleType);
    }
  };

  const handleTypeChange = type => {
    setScaleType(type);
    if (scaleSize) {
      const newScaleOptions = getNpsScaleOptions();
      setScaleOptions(newScaleOptions);
      handleScaleOptions(newScaleOptions, type);
    }
  };

  const addChoice = () => {
    let tempChoices = choices.slice();
    tempChoices = [...tempChoices, { choiceName_ID: '', choiceName: '' }];
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleLikerTOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleDeleteOption = index => {
    const tempChoices = (choices || []).filter((_, i) => i !== index);
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const buttonStyle = {
    border: '1px solid #8A8A8A',
    backgroundColor: 'white',
    color: 'rgba(137, 137, 137, 1)',
    marginLeft: '8px',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  const selectedButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#B3DB1A',
    color: 'white',
    border: '1px solid #B3DB1A'
  };

  return (
    <div className="options-list mt-2">
      <div className="row">
        {edit && (
          <>
            <div className="d-flex flex-column gap-3 ">
              {/* <div className="d-flex align-items-center">
                <Text type="6" style={{ marginRight: '16px', minWidth: '100px' }}>
                  Select Scale:
                </Text>
                <div className="d-flex mt-2">
                  {[11].map(size => (
                    <button
                      key={size}
                      style={scaleSize === size ? selectedButtonStyle : buttonStyle}
                      onClick={() => handleScaleChange(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div> */}

              {/* <div className="d-flex align-items-center">
                <Text type="6" style={{ marginRight: '16px', width: '200px' }}>
                  Select Text Value:
                </Text>
                <div
                  className="d-flex flex-wrap mt-2"
                  style={{
                    gap: '8px',
                    justifyContent: 'flex-start',
                  }}
                >
                  {SCALE_TYPES.map(type => (
                    <button
                      key={type.value}
                      style={{
                        ...buttonStyle,
                        ...(scaleType === type.value ? selectedButtonStyle : {}),
                        flex: '0 1 auto',
                        minWidth: '100px',
                        textAlign: 'center'
                      }}
                      onClick={() => handleTypeChange(type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div> */}
            </div>

            {/* <div className="mt-4">
              {choices?.map((opt, oIndex) => (
                <div key={oIndex} className="d-flex align-items-center my-1">
                  <EditOption
                    optionIndex={oIndex}
                    value={opt.choiceName}
                    handleOption={handleLikerTOption}
                    handleDeleteOption={handleDeleteOption}
                  />
                </div>
              ))}
            </div> */}

            {/* <div className=" col-md-12 mt-3">
              <ButtonGroup>
                <ChoiceButton onClick={addChoice}>
                  <Icon className="material-icons-round">add</Icon> Add choice
                </ChoiceButton>
              </ButtonGroup>
            </div> */}
          </>
        )}
        {/* {!edit && (
          <div className="col-md-12">
            <Box className="d-flex flex-column w-100">
              <Box className="d-flex flex-row w-100" sx={{ mb: 2, alignItems: 'center', gap: 5 }}>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400 }}>
                  {`Scale Size: ${likerTOptions?.length || 'Not Set'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400 }}>
                  {`Scale Type: ${
                    initialScaleType
                      ? initialScaleType
                          .split('-')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                          .join('-')
                      : 'Not Set'
                  }`}
                </Typography>
              </Box>

              <Box className="d-flex flex-column w-100" sx={{ mb: 1, alignItems: 'start' }}>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400, textAlign: 'start' }}>
                  {`1 = ${likerTOptions?.[0]?.questionLikertScaleName || 'Strongly Disagree'}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#8A8A8A', fontWeight: 400, textAlign: 'start', mt: 1 }}>
                  {`${likerTOptions?.length} = ${likerTOptions?.[likerTOptions.length - 1]?.questionLikertScaleName ||
                    'Strongly Agree'}`}
                </Typography>
              </Box>

              {choices?.map((opt, oIndex) => (
                <Box key={oIndex} className="d-flex align-items-center w-100" sx={{ mb: 2 }}>
                  <Typography variant="h5" sx={{ color: '#8A8A8A', fontSize: '13px', textAlign: 'right', pr: 2 }}>
                    {opt.choiceName}
                  </Typography>

                  <ButtonGroup variant="contained" aria-label="Likert scale">
                    {Array.from({ length: likerTOptions?.length }, (_, index) => (
                      <Button
                        key={index}
                        sx={{
                          backgroundColor: 'white',
                          color: '#8A8A8A',
                          minWidth: '40px',
                          padding: '6px 12px',
                          border: '1px solid #8A8A8A',
                          '&:hover': { backgroundColor: 'white' },
                          '&.MuiButton-root': { border: '1px solid #8A8A8A' },
                          '&:not(:first-of-type)': { borderLeft: 'none' }
                        }}
                      >
                        {index + 1}
                      </Button>
                    ))}
                  </ButtonGroup>
                </Box>
              ))}
            </Box>
          </div>
        )} */}
      </div>
    </div>
  );
};
const CustomTextArea = styled(TextArea)`
  ::placeholder,
  ::-webkit-input-placeholder,
  ::-moz-placeholder,
  :-ms-input-placeholder {
    color: var(--grey20);
    font-weight: 400;
    font-size: 10px;
  }
`;
const TextAnswerQuestion = ({ edit, description = '', characterLimit = 500, handleTextFields }) => {
  return (
    <div className="options-list mt-3">
      <div className="d-flex align-items-center mt-3">
        <Text className=" mr-2  mb-0 pb-0" type="6" fontSize={'12px'}>
          Character Limit :
        </Text>

        {!edit ? (
          <p
            className="mb-0 pb-0"
            style={{
              fontSize: '12px',
              fontWeight: '700',
              color: 'var(--greyBlue)'
            }}
          >
            {characterLimit}
          </p>
        ) : (
          <InputText
            type="number"
            showCharacters={false}
            value={characterLimit}
            width="50px"
            style={{
              fontSize: '12px',
              fontWeight: '700',
              color: 'var(--greyBlue)'
            }}
            onChange={e => handleTextFields('characterLimit', e.target.value)}
            data-no-dnd="true"
          />
        )}
      </div>
    </div>
  );
};

export const SingleSevenQuestion = ({
  options,
  edit = false,
  handleOptions,
  onChoiceFocus,
  shuffleEnabled = 0,
  onToggleChoiceShuffle
}) => {
  const [choices, setChoices] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    setChoices(options);
  }, [options]);

  let className = 'col-md-12';
  if (choices?.length > 4) {
    className = 'col-md-4';
  }

  const handleRadioChange = index => {
    setSelectedOption(index);
  };

  const handleSingleSevenOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  return (
    <div className="options-list mt-2">
      <div className="row">
        <div className="col-md-12 px-0">
          {choices?.length > 0 &&
            [...Array(7)].map((opt, oIndex) => (
              <ChoiceOptionRow key={oIndex}>
                <Radio
                  checked={selectedOption === oIndex}
                  onChange={() => handleRadioChange(oIndex)}
                  className="mb-0 pb-0"
                  fontSize="0.875rem"
                >
                  {!edit && choices[oIndex] ? (
                    <ChoiceLabelRow>
                      <span
                        style={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        }}
                      >
                        {choices[oIndex].choiceName}
                      </span>
                      {shouldShowChoiceShuffle(choices[oIndex], edit, shuffleEnabled) &&
                        renderChoiceShuffleIndicator(choices[oIndex], oIndex, edit, onToggleChoiceShuffle)}
                    </ChoiceLabelRow>
                  ) : null}
                </Radio>
                {edit && (
                  <>
                    <EditOption
                      optionIndex={oIndex}
                      value={choices[oIndex] && choices[oIndex].choiceName}
                      handleOption={handleSingleSevenOption}
                      handleDeleteOption={null}
                      onFocus={onChoiceFocus}
                    />
                    {choices[oIndex] &&
                      shouldShowChoiceShuffle(choices[oIndex], edit, shuffleEnabled) &&
                      renderChoiceShuffleIndicator(choices[oIndex], oIndex, edit, onToggleChoiceShuffle)}
                  </>
                )}
              </ChoiceOptionRow>
            ))}
        </div>
      </div>
    </div>
  );
};

const tempOptionsNumber = [
  {
    value: 1,
    label: 1
  },
  {
    value: 2,
    label: 2
  },
  {
    value: 3,
    label: 3
  },
  {
    value: 4,
    label: 4
  }
];
const tempOptionsNumberSize = [
  {
    value: 5,
    label: '5 Min'
  },
  {
    value: 10,
    label: '10 Min'
  }
];

const tempOptionsNumberSizeForImage = [
  {
    value: 10,
    label: '10 MB'
  },
  {
    value: 20,
    label: '20 MB'
  },
  {
    value: 30,
    label: '30 MB'
  }
];

const singleValueStyle = {
  fontSize: '14px',
  fontWeight: '300',
  color: 'var(--mediumGrey)'
};

const ImageUploadQuestion = ({ edit, numberOfFiles, maxFileSizeInMB, handleTextFields }) => {
  // let numberOfFilesIndex = tempOptionsNumber.findIndex(ton => ton.value == numberOfFiles);
  // let numberOfFilesDefaultValue =
  //   numberOfFilesIndex >= 0 ? tempOptionsNumber[numberOfFilesIndex] : { value: 1, label: 1 };

  let maxFileSizeInMBIndex = tempOptionsNumberSizeForImage.findIndex(tons => tons.value == maxFileSizeInMB);
  let maxFileSizeInMBDefaultValue =
    maxFileSizeInMBIndex >= 0 ? tempOptionsNumberSizeForImage[maxFileSizeInMBIndex] : { value: 10, label: '10 MB' };

  return (
    <div className="options-list mt-3">
      {/* <div className="d-flex align-items-center">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum number of files
            </Text>
            {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumber}
                defaultValue={numberOfFilesDefaultValue}
                onChange={e => handleTextFields('numberOfFiles', e.value)}
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6">
                {numberOfFiles}
              </Text>
            )}
          </div>
        </div>
      </div> */}

      <div className="d-flex align-items-center mt-2">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum size of file
            </Text>
            {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumberSizeForImage}
                defaultValue={maxFileSizeInMBDefaultValue}
                onChange={e => handleTextFields('maxFileSizeInMB', e.value)}
                className="ml-2"
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6" className="ml-2">
                {maxFileSizeInMB} MB
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ScreenerTypeQuestion = ({ options, edit, savedOption, handleTextFields }) => {
  const [selectedOption, setSelectedOption] = useState(savedOption);
  let screenerQuestionIndex = options.findIndex(op => op.label == selectedOption);
  let screenerQuestionDefaultValue =
    screenerQuestionIndex >= 0 ? options[screenerQuestionIndex] : { value: null, label: 'Select one' };

  return (
    <div className="options-list ">
      <div className="d-flex align-items-center my-4">
        <div style={{ width: '120px' }}>
          <Text type="6">Screener Question:</Text>
        </div>
        {edit ? (
          <Dropdown
            options={options}
            defaultValue={screenerQuestionDefaultValue}
            singleValueStyle={{
              fontSize: '12px',
              fontWeight: '700',
              color: 'var(--greyBlue)'
            }}
            optionStyle={{
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--greyBlue)'
            }}
            controlStyle={{
              borderBottom: '1.5px solid var(--grey20)'
            }}
            onChange={e => {
              handleTextFields('screenerQuestion_ID', e.value);
              setSelectedOption(e.label);
            }}
          />
        ) : (
          <Heading fontSize="12px" fontWeight="700" color="var(--greyBlue)" className="mb-0 pb-0">
            {selectedOption}
          </Heading>
        )}
      </div>
    </div>
  );
};

const RecordingQuestion = ({ edit, buttonName, tagList, options, handleTextFields, handleTags, handleDeleteTag }) => {
  const [recordingButtonName, setRecordingButtonName] = useState(buttonName);

  const onSelectTag = tag => {
    let tempTagLists = tagList;
    if (tag.tag != '') {
      tempTagLists = [...tempTagLists, tag];
      handleTags(tempTagLists);
    }
  };

  const onChangeTag = (index, tag) => {
    if (tag.tag == '') {
      let tempTagLists = tagList;
      tempTagLists.splice(index, 1);
      handleTags(tempTagLists);
    } else {
      let tempTagLists = tagList;
      tempTagLists[index] = tag;
      handleTags(tempTagLists);
    }
  };

  // const onSelectTag = tag => {
  //   let tempTagLists = tagList;
  //   tempTagLists = [...tempTagLists, tag];

  //   handleTags(tempTagLists);
  // };

  // const onChangeTag = (index, tag) => {
  //   let tempTagLists = tagList;
  //   tempTagLists[index] = tag;

  //   handleTags(tempTagLists);
  // };

  const onDeleteTag = tag => {
    let index = tagList.findIndex(o => o.tag == tag);
    let tempTagLists = tagList;
    if (index !== -1) {
      tempTagLists.splice(index, 1);
    }
    // handleDeleteTag(tempTagLists);
    handleTags(tempTagLists);
  };

  return (
    <div className="options-list ">
      <div className="d-flex align-items-center my-2">
        <div style={{ width: '120px' }}>
          <Text type="6">Button Name:</Text>
        </div>
        {edit ? (
          <InputText
            showCharacters={false}
            fontSize="12px"
            value={recordingButtonName}
            width="100px"
            style={{
              color: 'var(--greyBlue)'
            }}
            onChange={e => {
              handleTextFields('buttonName', e.target.value);
              setRecordingButtonName(e.target.value);
            }}
            data-no-dnd="true"
          />
        ) : (
          <Heading fontSize="12px" fontWeight="" color="var(--greyBlue)" className="mb-0 pb-0">
            {recordingButtonName}
          </Heading>
        )}
      </div>

      <div className="d-flex align-items-center mt-2">
        <div style={{ width: '120px' }}>
          <Text type="6">Tags:</Text>
        </div>
        {edit ? (
          <EditingTag
            tagList={tagList}
            options={options}
            singleValueStyle={{
              fontSize: '10px',
              fontWeight: '700',
              color: 'var(--greyBlue)'
            }}
            tagColor="var(--greyBlue)"
            addTagButtonColor="#8A8A8A"
            AddTagButton={() => (
              <TagButton fontSize="12px" fontWeight="300" variant="outlined" buttonColor="#8A8A8A" color="#8A8A8A">
                <Icon>add</Icon>Add Tag
              </TagButton>
            )}
            onSelectTag={onSelectTag}
            onChangeTag={onChangeTag}
            onDeleteTag={onDeleteTag}
          />
        ) : (
          <>
            {tagList &&
              tagList.map(tag => (
                <Tag className="mr-3 ">
                  <div className="d-flex align-items-center ">
                    <p>{tag.tag}</p> <Icon>close</Icon>
                  </div>
                </Tag>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

const InputTextContainer = styled.div`
  .iconContainer {
    position: absolute;
    top: 30%;
    right: 0;
  }
  span {
    font-size: 18px;
    color: var(--greyBlue);
  }
`;
const EditOption = ({
  optionIndex,
  value,
  handleOption,
  handleDeleteOption,
  isLikert,
  onFocus,
  /** Likert (question type 4) only: avoid closing VK on mousedown before focus when switching fields */
  vkIgnoreOutsideClick = false
}) => {
  const changeOption = e => {
    handleOption(optionIndex, e.target.value);
  };
  const removeOption = () => {
    handleDeleteOption(optionIndex);
  };
  const dynamicWidth = isLikert ? "500px" : "180px";

  return (
    <div style={{ position: 'relative' }}>
      <InputTextContainer>
        <TextArea
          showCharacters={false}
          fontSize="14px"
          value={value}
          width={dynamicWidth}
          minRows={1}
          maxRows={4}
          style={{
            color: 'var(--greyBlue)',
            fontWeight: '500',
            marginLeft: '3px',
            fontFamily: 'Arial, "Noto Sans", "Noto Sans Devanagari", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", sans-serif'
          }}
          useDefaultValue={true}
          onChange={e => changeOption(e)}
          onFocus={() => onFocus && onFocus(optionIndex)}
          onClick={() => onFocus && onFocus(optionIndex)}
          data-no-dnd="true"
          {...(vkIgnoreOutsideClick ? { 'data-virtualkeyboard-ignore': 'true' } : {})}
        />
        {handleDeleteOption != null && (
          <div className="iconContainer" style={{ cursor: 'pointer' }} onClick={() => removeOption()}>
            <Icon>close</Icon>
          </div>
        )}
      </InputTextContainer>
    </div>
  );
};

const CustomCard = styled(Card)`
  border: 1px solid #ffffff;
  box-sizing: border-box;
  /* Selected Box in Box */

  box-shadow: 0px 2px 8px 1px rgba(0, 0, 0, 0.2);
  border-radius: 10px;
`;

const ValueQuestion = ({ options, edit, savedOption, handleTextFields }) => {
  let savedOpt = savedOption == 'Percentage Value' ? 'Percentage value' : savedOption;
  const [selectedOption, setSelectedOption] = useState(savedOpt);
  let screenerQuestionIndex = options.findIndex(op => op.label == selectedOption);
  let screenerQuestionDefaultValue =
    screenerQuestionIndex >= 0 ? options[screenerQuestionIndex] : { value: null, label: 'Select one' };

  return (
    <div className="options-list ">
      <div className="d-flex align-items-center my-4">
        <div style={{ width: '120px' }}>
          <Text type="6">Ask for amount in:</Text>
        </div>
        {edit ? (
          <Dropdown
            options={options}
            defaultValue={screenerQuestionDefaultValue}
            singleValueStyle={{
              fontSize: '12px',
              fontWeight: '700',
              color: 'var(--greyBlue)'
            }}
            optionStyle={{
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--greyBlue)'
            }}
            controlStyle={{
              borderBottom: '1.5px solid var(--grey20)'
            }}
            onChange={e => {
              handleTextFields('valueQuestion_ID', e.value);
              setSelectedOption(e.label);
            }}
          />
        ) : (
          <Heading fontSize="12px" fontWeight="700" color="var(--greyBlue)" className="mb-0 pb-0">
            {selectedOption}
          </Heading>
        )}
      </div>
    </div>
  );
};
const AudioQuestion = ({ edit, numberOfFiles, maxFileSizeInMB, handleTextFields }) => {
  let maxFileSizeInMBIndex = tempOptionsNumberSize.findIndex(tons => tons.value == maxFileSizeInMB);
  let maxFileSizeInMBDefaultValue =
    maxFileSizeInMBIndex >= 0 ? tempOptionsNumberSize[maxFileSizeInMBIndex] : { value: 5, label: '5 Min' };

  return (
    <div className="options-list mt-3">
      {/* <div className="d-flex align-items-center">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum number of files
            </Text>
            {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumber}
                defaultValue={numberOfFilesDefaultValue}
                onChange={e => handleTextFields('numberOfFiles', e.value)}
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6">
                {numberOfFiles}
              </Text>
            )}
          </div>
        </div>
      </div> */}

      <div className="d-flex align-items-center mt-2">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum size of file :
            </Text>
            <Text color="var(--mediumGrey)" type="6" className="ml-2">
              {maxFileSizeInMB} MB
            </Text>
            {/* {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumberSize}
                defaultValue={maxFileSizeInMBDefaultValue}
                onChange={e => handleTextFields('maxFileSizeInMB', e.value)}
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6">
                {maxFileSizeInMB} Min
              </Text>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};
const VideoQuestion = ({ edit, numberOfFiles, maxFileSizeInMB, handleTextFields }) => {
  let maxFileSizeInMBIndex = tempOptionsNumberSize.findIndex(tons => tons.value == maxFileSizeInMB);
  let maxFileSizeInMBDefaultValue =
    maxFileSizeInMBIndex >= 0 ? tempOptionsNumberSize[maxFileSizeInMBIndex] : { value: 5, label: '5 Min' };

  return (
    <div className="options-list mt-3">
      {/* <div className="d-flex align-items-center">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum number of files
            </Text>
            {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumber}
                defaultValue={numberOfFilesDefaultValue}
                onChange={e => handleTextFields('numberOfFiles', e.value)}
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6">
                {numberOfFiles}
              </Text>
            )}
          </div>
        </div>
      </div> */}

      <div className="d-flex align-items-center mt-2">
        <div style={{ width: edit ? '300px' : '220px' }}>
          <div className="d-flex align-items-center">
            <Text color="var(--mediumGrey)" type="6">
              Maximum size of file :
            </Text>
            <Text color="var(--mediumGrey)" type="6" className="ml-2">
              {maxFileSizeInMB} MB
            </Text>
            {/* {edit ? (
              <Dropdown
                singleValueStyle={singleValueStyle}
                options={tempOptionsNumberSize}
                defaultValue={maxFileSizeInMBDefaultValue}
                onChange={e => handleTextFields('maxFileSizeInMB', e.value)}
              />
            ) : (
              <Text color="var(--mediumGrey)" type="6">
                {maxFileSizeInMB} Min
              </Text>
            )} */}
          </div>
        </div>
      </div>
    </div>
  );
};

//Ranking Question
const RankingQuestion = ({
  options,
  edit = false,
  handleOptions,
  onChoiceFocus,
  shuffleEnabled = 0,
  onToggleChoiceShuffle
}) => {
  const [choices, setChoices] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    setChoices(options);
  }, [options]);

  let className = 'col-md-12';
  // if (choices?.length > 4) {
  //   className = 'col-md-4';
  // }

  useEffect(() => {
    if (edit && choices.length === 0) {
      setChoices([{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }]);
    }
  }, [edit, choices.length]);

  // Sageer's code
  const handleRadioChange = index => {
    setSelectedOption(index);
  };

  const addChoice = () => {
    let tempChoices = choices?.slice();
    if (tempChoices == null) {
      tempChoices = [{ choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    } else {
      tempChoices = [...tempChoices, { choiceName_ID: generateUniqueID(), choiceName: '', toShuffle: 0 }];
    }
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleRankingChoiceOption = (index, value) => {
    const tempChoices = (choices || []).map((choice, i) =>
      i === index ? { ...choice, choiceName: value } : choice
    );
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  const handleDeleteOption = index => {
    const tempChoices = (choices || []).filter((_, i) => i !== index);
    setChoices(tempChoices);
    handleOptions(tempChoices);
  };

  return (
    <div className="options-list mt-2">
      <div className="row">
        <div className="col-md-12 px-0">
          {choices?.map((opt, oIndex) => (
            <ChoiceOptionRow key={opt.choiceName_ID}>
              <Radio
                checked={selectedOption === oIndex}
                onChange={() => handleRadioChange(oIndex)}
                className="mb-0 pb-0"
                fontSize="0.875rem"
              >
                {!edit ? (
                  <ChoiceLabelRow>
                    <span
                      style={{
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {opt.choiceName}
                    </span>
                    {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                      renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                  </ChoiceLabelRow>
                ) : null}
              </Radio>
              {edit && (
                <>
                  <EditOption
                    optionIndex={oIndex}
                    value={opt.choiceName}
                    handleOption={handleRankingChoiceOption}
                    handleDeleteOption={handleDeleteOption}
                    onFocus={onChoiceFocus}
                  />
                  {shouldShowChoiceShuffle(opt, edit, shuffleEnabled) &&
                    renderChoiceShuffleIndicator(opt, oIndex, edit, onToggleChoiceShuffle)}
                </>
              )}
            </ChoiceOptionRow>
          ))}
        </div>

        {edit && (
          <div className={className}>
            <ChoiceButton onClick={e => addChoice()}>
              <Icon className="material-icons-round">add</Icon> Add choice
            </ChoiceButton>
          </div>
        )}
      </div>
    </div>
  );
};

const SkipLogicControls = ({
  skipToOptions = [],
  skipToTarget,
  setSkipToTarget,
  options = [],
  skipIfChoice,
  setSkipIfChoice,
  conditionOptions = [],
  skipCondition,
  setSkipCondition,
  taskQuestion,
  edit,
  questionType_ID,
  OptionsSkip,
  setLikertScaleChoicesOptions,
  likertScaleChoicesOptions,
  skipLogicInputValue,
  setSkipLogicInputValue,
  likertScaleOptions,
  setLikertScaleOptions,
  isSkipEnabled
}) => {
  const endSurveyOptions =
    options?.filter(
      opt =>
        opt.EndSurvey != null ||
        (opt.sectionAttached && Object.keys(opt.sectionAttached || {}).length > 0)
    ) || [];
  const [showDropDown, setShowDropDown] = useState(taskQuestion?.isSkipLogicApplied === 1 ? true : false);
  const [rankValue, setRankValue] = useState(null);
  const userChangedSkipTargetRef = useRef(false);

  // Seed skipToTarget from saved section once. Stop after user picks a new target
  // so selecting End of Survey is not overwritten by stale skipSectionAttached.
  useEffect(() => {
    if (userChangedSkipTargetRef.current) return;
    const attachedId = taskQuestion?.skipSectionAttached?.questionPaperSection_ID;
    if (attachedId == null || attachedId === '') return;
    if (String(skipToTarget) === String(attachedId)) return;
    setSkipToTarget(attachedId);
  }, [taskQuestion?.skipSectionAttached?.questionPaperSection_ID, skipToTarget, setSkipToTarget]);

  const selectedEndSurveyOption = endSurveyOptions[0];
  const resolveLikertSkipScaleId = choice => {
    const raw = choice?.EndSurvey ?? Object.values(choice?.sectionAttached || {})[0];
    return Array.isArray(raw) ? raw[0] : raw;
  };
  useEffect(() => {
    if (questionType_ID === 5 || questionType_ID === 6 || questionType_ID == 3 || questionType_ID === 11) {
      setSkipLogicInputValue(taskQuestion?.EndSurvey);
    }
  }, [questionType_ID]);

  useEffect(() => {
    const savedSkipChoiceId =
      taskQuestion?.skipchoice?.choiceName_ID ??
      taskQuestion?.skipchoice?.QuestionChoice_ID ??
      taskQuestion?.skipchoice?.questionChoices_ID ??
      taskQuestion?.skipchoice?.id ??
      taskQuestion?.skipchoice;

    if (!skipIfChoice && savedSkipChoiceId != null) {
      setSkipIfChoice(savedSkipChoiceId);
    }
  }, [taskQuestion?.skipchoice, skipIfChoice]);

  useEffect(() => {
    if (
      questionType_ID === 1 ||
      questionType_ID === 2 ||
      questionType_ID === 7 ||
      questionType_ID === 16 ||
      questionType_ID === 4
    )
      if (questionType_ID === 16) {
        setSkipIfChoice(options.EndSurvey !== null ? options?.EndSurvey : options[0]?.choiceName_ID);
        const matchedOption = OptionsSkip?.find(opt => options[0]?.EndSurvey === opt?.questionLikertScale_ID);
        if (matchedOption) {
          setLikertScaleChoicesOptions(matchedOption.questionLikertScale_ID);
        } else {
          // setLikertScaleChoicesOptions(null); // Or the default ID if no match
        }
      }
    if (questionType_ID === 4 && selectedEndSurveyOption) {
      if (!skipIfChoice) setSkipIfChoice(selectedEndSurveyOption.choiceName_ID || selectedEndSurveyOption.id);
      const matched = OptionsSkip?.find(
        o => String(o.questionLikertScale_ID) === String(resolveLikertSkipScaleId(selectedEndSurveyOption))
      );
      if (matched) setLikertScaleOptions(matched);
    }

    if (
      (questionType_ID === 1 || questionType_ID === 2 || questionType_ID === 7) &&
      !skipIfChoice &&
      selectedEndSurveyOption
    ) {
      setSkipIfChoice(selectedEndSurveyOption?.choiceName_ID);
    }
  }, [skipIfChoice, selectedEndSurveyOption, questionType_ID, options, OptionsSkip]); // Removed setSkipIfChoice from deps, added others
  useEffect(() => {
    if (
      (questionType_ID === 14 || questionType_ID === 3 || questionType_ID === 11) &&
      taskQuestion?.CompareValueArray
    ) {
      const compareEntries = Object.entries(taskQuestion.CompareValueArray);
      for (const [indexStr, valueArray] of compareEntries) {
        const endSurvey = valueArray?.[0]?.EndSurvey;
        if (endSurvey !== null && endSurvey !== undefined) {
          const likertIndex = parseInt(indexStr); // e.g., "2" → 2

          if (typeof endSurvey === 'object' && !Array.isArray(endSurvey)) {
            const choiceId = Object.keys(endSurvey)[0];
            setRankValue(choiceId);
            const inputValue = endSurvey[choiceId];
            if (choiceId) {
              setSkipIfChoice(choiceId);
            }

            if (inputValue !== null && inputValue !== undefined) {
              setSkipLogicInputValue(inputValue);
            }
          } else if (typeof endSurvey === 'number') {
            setSkipLogicInputValue(endSurvey);
          }

          // Operator from CompareValueArray key (1-based index or id)
          const matchedOption =
            OptionsSkip?.find(opt => String(opt.id) === String(indexStr)) || OptionsSkip?.[likertIndex - 1];
          if (matchedOption) {
            setLikertScaleChoicesOptions(matchedOption);
          }
          break; // process only first valid EndSurvey
        }
      }
    }
    // Handle ranking question specific logic
    if (questionType_ID === 14) {
      if (taskQuestion?.choiceRankID) {
        setRankValue(taskQuestion.choiceRankID);
        setSkipIfChoice(taskQuestion.choiceRankID);
      } else if (taskQuestion?.choiceObj !== undefined) {
        const choiceArray = taskQuestion.choiceName_array || [];
        const selectedChoice = choiceArray[taskQuestion.choiceObj];
        const choiceId = selectedChoice?.choiceName_ID || selectedChoice?.id;
        if (choiceId != null) {
          setRankValue(choiceId);
          setSkipIfChoice(choiceId);
        }
      }
    }
  }, [questionType_ID, OptionsSkip, taskQuestion]);

  const skipSelectedVlaue = conditionOptions
    ? conditionOptions.find(option => String(option.value) === String(skipCondition)) || conditionOptions[0]
    : '';
  const skipChoiceOptionsVlaue = useMemo(() => {
    if ([3, 11, 14].includes(questionType_ID)) {
      const matched =
        (likertScaleChoicesOptions?.Operations && likertScaleChoicesOptions) ||
        OptionsSkip?.find(
          o => String(o.id) === String(likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions)
        );
      return matched?.Operations ? { label: matched.Operations, value: matched.id } : null;
    }
    if (!likertScaleChoicesOptions) return null;
    if (questionType_ID === 4) {
      return likertScaleChoicesOptions
        ? {
            label: likertScaleChoicesOptions.questionLikertScaleName,
            value: likertScaleChoicesOptions?.questionLikertScale_ID
          }
        : null;
    }
    setLikertScaleOptions(likertScaleChoicesOptions);
    if (questionType_ID === 16) {
      const selectedOption = OptionsSkip?.find(opt => opt.questionLikertScale_ID === likertScaleChoicesOptions);
      if (selectedOption !== undefined) {
        const displayIndex = OptionsSkip?.findIndex(opt => opt.questionLikertScale_ID === likertScaleChoicesOptions);
        return {
          label: `${displayIndex}`,
          value: selectedOption.questionLikertScale_ID
        };
      }
      const savedScaleId =
        likertScaleChoicesOptions ??
        taskQuestion?.npsScale_ID ??
        options?.find(c => c?.EndSurvey != null)?.EndSurvey;
      return resolveNpsScaleSelection(taskQuestion?.likertScaleName_array || OptionsSkip, savedScaleId);
    }
    setLikertScaleChoicesOptions(likertScaleChoicesOptions.value);
  }, [likertScaleChoicesOptions, questionType_ID, OptionsSkip, taskQuestion?.npsScale_ID, taskQuestion?.likertScaleName_array, options]);
  const rankLookupId = rankValue ?? skipIfChoice ?? taskQuestion?.choiceRankID;
  const rankValueOption =
    options?.find(opt => String(opt?.choiceName_ID || opt?.id) === String(rankLookupId)) ||
    (typeof taskQuestion?.choiceObj === 'number' ? options?.[taskQuestion.choiceObj] : null) ||
    null;
  const rankChoiceValue = rankValueOption
    ? {
        label: rankValueOption?.choiceName,
        value: rankValueOption?.choiceName_ID || rankValueOption?.id
      }
    : null;

  const skipChoice =
    options?.find(o => String(o?.choiceName_ID || o?.id) === String(skipIfChoice)) || selectedEndSurveyOption;
  const skipChoiceValue = skipChoice
    ? { label: skipChoice.choiceName, value: skipChoice.choiceName_ID || skipChoice.id }
    : null;

  const matchedLikert =
    (likertScaleOptions?.questionLikertScaleName && likertScaleOptions) ||
    OptionsSkip?.find(
      o => String(o.questionLikertScale_ID) === String(resolveLikertSkipScaleId(selectedEndSurveyOption))
    );
  const likertValue = matchedLikert
    ? { label: matchedLikert.questionLikertScaleName, value: matchedLikert.questionLikertScale_ID }
    : null;

  // Prefer skipToTarget alone when set. OR-matching skipSectionAttached in the same
  // find() snaps the dropdown back to the saved section and blocks End of Survey.
  const skiptargetValue = (() => {
    const optionsList = skipToOptions || [];
    const hasLiveTarget =
      skipToTarget != null && skipToTarget !== '' && Number(skipToTarget) !== 0;

    if (hasLiveTarget) {
      const matched = optionsList.find(i => String(i?.value) === String(skipToTarget));
      if (matched) return matched;
      if (String(skipToTarget) === '1') return { label: 'End Of Survey', value: '1' };
    }

    const attachedId = taskQuestion?.skipSectionAttached?.questionPaperSection_ID;
    if (attachedId != null && attachedId !== '') {
      const matched = optionsList.find(i => String(i?.value) === String(attachedId));
      if (matched) return matched;
      if (taskQuestion?.skipSectionAttached?.sectionName) {
        return {
          label: taskQuestion.skipSectionAttached.sectionName,
          value: attachedId
        };
      }
    }

    return String(skipToTarget) === '1' ? { label: 'End Of Survey', value: '1' } : null;
  })();

  return (
    <>
      {!edit &&
      [1, 2, 3, 4, 5, 6, 7, 11, 14, 16].includes(taskQuestion.questionType_ID) &&
      taskQuestion?.isSkipLogicApplied === 1 ? (
        <>
          <Heading color={'#728F01'} className="mt-4 text-uppercase" type={'6'} fontSize={'0.9rem'} fontWeight={'700'}>
            Skip To
          </Heading>

          <div className="d-flex mt-2 mb-3 align-items-center flex-wrap">
            <Text
              className="mb-0 pb-0 mt-0 pr-3"
              fontSize={'0.8rem'}
              color={'var(--paraColor)'}
              fontWeight={'600'}
              type="5"
            >
              {taskQuestion?.skipSectionAttached?.sectionName
                ? taskQuestion?.skipSectionAttached?.sectionName
                : 'End of Survey'}
            </Text>

            {/* <span style={{ fontWeight: '500' }}>End of Survey</span> */}
            {questionType_ID !== 5 && questionType_ID !== 6 && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--quadColor)'}
                fontWeight={'100'}
                type="5"
              >
                If
              </Text>
            )}
            {questionType_ID === 5 ||
              (questionType_ID === 6 && (
                <Text
                  className="mb-0 pb-0 mt-0"
                  fontSize={'0.8rem'}
                  color={'var(--quadColor)'}
                  fontWeight={'100'}
                  type="5"
                >
                  If
                </Text>
              ))}
            {questionType_ID !== 3 && questionType_ID !== 14 && questionType_ID !== 4 && questionType_ID !== 16 && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--paraColor)'}
                fontWeight={'600'}
                type="5"
              >
                {taskQuestion?.skipchoice?.choiceName
                  ? taskQuestion?.skipchoice?.choiceName
                  : selectedEndSurveyOption?.choiceName}
              </Text>
            )}

            {questionType_ID === 14 && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--paraColor)'}
                fontWeight={'600'}
                type="5"
              >
                {rankValueOption?.choiceName ? rankValueOption?.choiceName : taskQuestion?.choiceObj?.choiceName}
              </Text>
            )}

            {questionType_ID === 14 && (
              <Text
                Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--quadColor)'}
                fontWeight={'100'}
                type="5"
              >
                Ranked
              </Text>
            )}

            {(questionType_ID === 3 || questionType_ID === 11 || questionType_ID === 14) && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--paraColor)'}
                fontWeight={'600'}
                type="5"
              >
                {likertScaleChoicesOptions?.Operations}
              </Text>
            )}

            {/* {questionType_ID === 4 && (
      <span style={{ fontWeight: '600' }}>{likertScaleChoicesOptions?.questionLikertScaleName}</span>
    )} */}

            {questionType_ID === 4 && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--paraColor)'}
                fontWeight={'600'}
                type="5"
              >
                {likertScaleOptions?.questionLikertScaleName
                  ? likertScaleOptions?.questionLikertScaleName
                  : taskQuestion.skipchoice}
              </Text>
            )}

            {questionType_ID === 16 && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--paraColor)'}
                fontWeight={'600'}
                type="5"
              >
                {skipChoiceOptionsVlaue?.label ?? ''}
              </Text>
            )}

            {[5, 6, 3, 11, 14].includes(questionType_ID) && (
              <>
                {[5, 6].includes(questionType_ID) && (
                  <Text
                    className="mb-0 pb-0 mt-0 pr-3"
                    fontSize={'0.8rem'}
                    color={'var(--quadColor)'}
                    fontWeight={'100'}
                    type="5"
                  >
                    Contains "Words"
                  </Text>
                )}

                <Text
                  className="mb-0 pb-0 mt-0 pr-3"
                  fontSize={'0.8rem'}
                  color={'var(--paraColor)'}
                  fontWeight={'600'}
                  type="5"
                >
                  {skipLogicInputValue && skipLogicInputValue.length > 0
                    ? skipLogicInputValue
                    : taskQuestion.skipchoice|| skipLogicInputValue}
                </Text>
              </>
            )}

            {/* Fallback condition text */}
            {![3, 11, 5, 6, 14].includes(questionType_ID) && (
              <Text
                className="mb-0 pb-0 mt-0 pr-3"
                fontSize={'0.8rem'}
                color={'var(--quadColor)'}
                fontWeight={'100'}
                type="5"
              >
                Is Selected
              </Text>
            )}
          </div>
        </>
      ) : [1, 2, 3, 4, 5, 6, 7, 11, 14, 16].includes(taskQuestion.questionType_ID) && isSkipEnabled ? (
        <>
          <Heading color={'#000000'} className="mt-4 text-uppercase" type={'6'} fontSize={'0.9rem'} fontWeight={'700'}>
            SKIP TO
          </Heading>
          <div className="d-flex mt-2 mb-3" style={{ columnGap: '3rem' }}>
            <DesignDropdown
              key="skip-to-section-select"
              options={(skipToOptions || []).map(option => ({
                label: option.label,
                value: option.value
              }))}
              value={skiptargetValue}
              onChange={e => {
                userChangedSkipTargetRef.current = true;
                setSkipToTarget(e.value);
                setShowDropDown(true);
              }}
              singleValueStyle={{
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--greyBlue)'
              }}
              optionStyle={{
                fontSize: '12px',
                fontWeight: '500',
                color: 'var(--greyBlue)',
                width: '120px'
              }}
              controlStyle={{
                borderBottom: '1.5px solid var(--grey20)'
              }}
            />
            {/* {skipToTarget !=='1'|| skipToTarget !== 1 && ( */}
            {showDropDown && (
              <>
                <Heading className="mt-2" type="6" fontSize="0.9rem">
                  IF
                </Heading>
                {questionType_ID !== 3 &&
                  questionType_ID !== 11 &&
                  questionType_ID !== 5 &&
                  questionType_ID !== 6 &&
                  questionType_ID !== 16 && (
                    <DesignDropdown
                      key={`skip-if-${rankLookupId ?? skipIfChoice ?? 'none'}`}
                      options={options?.map(option => ({
                        label: option.choiceName,
                        value: option.choiceName_ID
                      }))}
                      value={questionType_ID === 14 ? rankChoiceValue : skipChoiceValue}
                      onChange={selectedOption => {
                        setSkipIfChoice(selectedOption.value); // Store primitive value
                        if (questionType_ID === 14) setRankValue(selectedOption.value);
                      }}
                      singleValueStyle={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: 'var(--greyBlue)',
                        skipIfChoice
                      }}
                      optionStyle={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: 'var(--greyBlue)',
                        width: '120px'
                      }}
                      controlStyle={{
                        borderBottom: '1.5px solid var(--grey20)'
                      }}
                      controlStyleWidth={questionType_ID === 14 ? "90px" : undefined}

                      // value={skipIfChoice}
                    />
                  )}
                {questionType_ID === 14 && (
                  <Text className="mt-2" type="6" fontSize="0.9rem">
                    <p>RANKED</p>
                  </Text>
                )}
                {(questionType_ID === 3 ||
                  questionType_ID === 11 ||
                  questionType_ID === 14 ||
                  questionType_ID === 16) && (
                  <DesignDropdown
                    key={`skip-op-${skipChoiceOptionsVlaue?.value ?? likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions ?? 'none'}`}
                    options={OptionsSkip?.map((option, index) => ({
                      label:
                        questionType_ID === 4
                          ? option.questionLikertScaleName
                          : questionType_ID === 16
                          ? `${index}`
                          : option.Operations,
                      value: questionType_ID === 4 || questionType_ID === 16 ? option.questionLikertScale_ID : option.id
                    }))}
                    value={skipChoiceOptionsVlaue}
                    onChange={e => {
                      setLikertScaleChoicesOptions(e.value);
                    }}
                    singleValueStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)',
                      likertScaleChoicesOptions
                    }}
                    optionStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)',
                      width: '120px'
                    }}
                    controlStyle={{
                      borderBottom: '1.5px solid var(--grey20)'
                    }}
                      controlStyleWidth={questionType_ID === 14 ? "90px" : undefined}

                  />
                )}
                {questionType_ID === 4 && (
                  <DesignDropdown
                    key={`skip-likert-${likertValue?.value ?? 'none'}`}
                    options={OptionsSkip?.map(option => ({
                      label: option.questionLikertScaleName,
                      value: option.questionLikertScale_ID
                    }))}
                    value={likertValue}
                    // onChange={e => {
                    //   setLikertScaleOptions(e.value);
                    // }}
                    onChange={e => {
                      const fullOption = OptionsSkip.find(
                        opt => String(opt.questionLikertScale_ID) === String(e.value)
                      );
                      setLikertScaleOptions(fullOption);
                    }}
                    singleValueStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)'
                    }}
                    optionStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)',
                      width: '120px'
                    }}
                    controlStyle={{
                      borderBottom: '1.5px solid var(--grey20)'
                    }}
                  />
                )}
                {(questionType_ID === 5 || questionType_ID === 6) && (
                  <Text className="mt-2" type="6" fontSize="0.9rem">
                    <p>Contains "word" </p>
                  </Text>
                )}
                {(questionType_ID === 3 ||
                  questionType_ID === 11 ||
                  questionType_ID === 5 ||
                  questionType_ID === 6 ||
                  questionType_ID === 14) && (
                  <input
                    // type="text"
                    type={[11, 3, 14].includes(questionType_ID) ? 'number' : 'text'}
                    style={{
                      height: '40px',
                      width: '80px',
                      backgroundColor: '#E7E7E7',
                      borderRadius: '4px',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    placeholder={questionType_ID === 5 || questionType_ID === 6 ? 'Enter Words' : 'Enter Value'}
                    value={skipLogicInputValue}
                    onChange={e => setSkipLogicInputValue(e.target.value)}
                  />
                )}
                {![3, 11, 5, 6, 14].includes(questionType_ID) && (
                  <DesignDropdown
                    options={conditionOptions.map(option => ({
                      label: option.label,
                      value: option.value
                    }))}
                    onChange={e => {
                      setSkipCondition(e.value);
                    }}
                    value={skipSelectedVlaue}
                    singleValueStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)'
                    }}
                    optionStyle={{
                      fontSize: '12px',
                      fontWeight: '500',
                      color: 'var(--greyBlue)',
                      width: '120px'
                    }}
                    controlStyle={{
                      borderBottom: '1.5px solid var(--grey20)'
                    }}
                  />
                )}
              </>
            )}
          </div>
        </>
      ) : (
        ''
      )}
    </>
  );
};

const SpanCustom = styled.span`
  margin-right: 3px;

  @media (max-width: 1344px) {
    display: none;
  }
`;

const ImgCustom = styled.img`
  @media (max-width: 1344px) {
    display: none;
  }
`;

const BtnAssignGroup = styled.a`
  background: transparent none repeat scroll 0% 0%;
  border: 0px none;
  display: flex;
  -moz-box-align: center;
  align-items: center;
  -moz-box-pack: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: var(--quadColor);
  padding: 0.25rem;
  text-align: center;
`;

function DesignQuestion({
  sectionIndex,
  questionIndex,
  questionArrayLength,
  sectionId,
  question,
  questionTypes,
  screenerTypeOption,
  masterTags,
  handleChangeQuestionType,
  handleDuplicateQuestion,
  handleEditQuestion,
  handleDeleteQuestion,
  ValueTypeChoices,
  handleProjectTaskTab,
  isInterview,
  setEditQuestionIndex,
  setNewQuestionIndex,
  setDeleteQuestionIndex,
  switchvalue,
  setSwitchValue,
  setQuestionIndex,
  enableEdit,
  setEditQuestionId,
  setAddQuestionDisable,
  questionmessage,
  popupColor,
  showCheckbox,
  handleSetEditing,
  setFormTaskSections,
  formTaskSections,
  setInterviewTaskSections,
  interviewTaskSections,
  task,
  isVirtualKeyboardEnabled = false,
  choiceNameParentQuestions = '',
  refreshTask,
  sectionShuffleEnabled = 0
}) {
  const { userId } = useSelector(state => state.LoginReducer);
  console.log("Deployment Checkpoint 1")
  const [taskQuestion, setTaskQuestion] = useState(question);
  const taskQuestionRef = useRef(question);
  useEffect(() => {
    taskQuestionRef.current = taskQuestion;
  }, [taskQuestion]);
  const [formChanged, setFormChanged] = useState(false);
  const [desc, setDesc] = useState(question.questionDescription || '');
  const projectId = useSelector(state => state.ProjectReducer.project_ID);
  const [visible, setVisible] = useState(false);
  const [optionVisible, setOptionVisible] = useState(false);

  const [scaletypeVisible, setScaleTypeVisible] = useState(false);
  let [saveQuestion, setSaveQuestion] = useState(true);
  const [isEdit, setIsEdit] = useState(false);
  const [popup, setPopup] = useState(false);
  const [checkBoxRequired, setCheckBoxRequired] = useState(
    taskQuestion.isRequired == 0 || taskQuestion.isRequired == null ? 0 : 1
  );
  const [checkboxDisplay, setCheckBoxdisplay] = useState(
    taskQuestion.isLogicApplied == 0 || taskQuestion.isLogicApplied == null ? 0 : 1
  );
  const [shuffleEnabled, setShuffleEnabled] = useState(
    taskQuestion.toShuffle == 0 || taskQuestion.toShuffle == null ? 0 : 1
  );
  const [showTooltipisdResponded, setShowTooltipisResponded] = useState(false);
  const [attachedQuestionAfter, setAttachedQuestion] = useState('');
  const [attachedChoiceAfter, setAttachedChoice] = useState('');
  const [likertSelectedChoice, setLikertSelectedChoice] = useState();
  const [answerChoice, setAnswerChoice] = useState();
  const [inputAnswer, setInputAnswer] = useState('');
  const [comparibilityValue, setComparibilityValue] = useState();
  const [isSkipEnabled, setIsSkipEnabled] = useState(taskQuestion?.isSkipLogicApplied === 1 ? 1 : 0);
  const [skipToTarget, setSkipToTarget] = useState(taskQuestion?.isSkipLogicApplied === 1 ? 1 : 0);
  const [skipIfChoice, setSkipIfChoice] = useState(null);
  const [skipCondition, setSkipCondition] = useState(null);
  const [likertScaleChoicesOptions, setLikertScaleChoicesOptions] = useState(null);
  const [skipLogicInputValue, setSkipLogicInputValue] = useState('');
  const [isQuestionSavedForSkipLogic, setIsQuestionSavedForSkipLogic] = useState(!!question?.question_ID);
  const [likertScaleOptions, setLikertScaleOptions] = useState();
  const [skipLogicError, setSkipLogicError] = useState('');
  const [skipToOptions, setSkipToOptions] = useState();
  const [displayList, setDisplayList] = useState([]);
  const [orLogic, setORLogic] = useState([]);
  const [andLogic, setAndLogic] = useState([]);
  const [isStateReady, setIsStateReady] = useState(false);
  const [hideNormalDisplay, setHideNormalDisplay] = useState(false);
  const [duplicateLogicError, setDuplicateLogicError] = useState('');
  const [customLikertError, setCustomLikertError] = useState(null);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [currentInputField, setCurrentInputField] = useState(null);
  const currentInputFieldRef = useRef(null);
  const keyboardContainerRef = useRef(null);

  // Initialize with a deep copy of the question prop to handle mount-in-edit cases
  const snapshotQuestion = useRef(JSON.parse(JSON.stringify(question)));
  const skipIfChoiceRef = useRef(null);        // Tracks skipIfChoice at edit-open time
  const inputAnswerRef = useRef(null);         // Tracks inputAnswer at edit-open time (types 3, 11, 14)
  const comparibilityValueRef = useRef(null);  // Tracks comparibilityValue at edit-open time (types 3, 11, 14)
  const displayListRef = useRef([]);           // Tracks displayList at edit-open time (multi-condition display logic)
  const likertScaleChoicesOptionsRef = useRef(null); // Tracks likertScaleChoicesOptions at edit-open time (Likert/NPS skip logic)
  const answerChoiceRef = useRef(null);              // Tracks answerChoice at edit-open time (ifContains for types 3, 11, 14)
  const likertSelectedChoiceRef = useRef(null);      // Tracks likertSelectedChoice at edit-open time
  const baseLogicStateRef = useRef(null);            // Tracks taskQuestion base condition fields
  const displayLogicInitRef = useRef('');
  const orLogicRef = useRef([]);
  const andLogicRef = useRef([]);

  const liveStateRef = useRef({});
  liveStateRef.current = {
    skipIfChoice,
    inputAnswer,
    comparibilityValue,
    displayList,
    orLogic,
    andLogic,
    likertScaleChoicesOptions,
    answerChoice,
    likertSelectedChoice,
    taskQuestion,
    skipToTarget,
    skipLogicInputValue,
    likertScaleOptions
  };

  useEffect(() => {
    if (!question.isEdit) {
      // Use JSON deep copy to avoid reference issues if parent mutates objects
      snapshotQuestion.current = JSON.parse(JSON.stringify(question));
      skipIfChoiceRef.current = null;
      inputAnswerRef.current = null;
      comparibilityValueRef.current = null;
      displayListRef.current = [];
      likertScaleChoicesOptionsRef.current = null;
      answerChoiceRef.current = null;
      likertSelectedChoiceRef.current = null;
      baseLogicStateRef.current = null;
      orLogicRef.current = [];
      andLogicRef.current = [];
    } else {
      // Edit just opened — snapshot state values after auto-population settles
      const t = setTimeout(() => {
        const latest = liveStateRef.current;
        skipIfChoiceRef.current = latest.skipIfChoice;
        inputAnswerRef.current = latest.inputAnswer;
        comparibilityValueRef.current = latest.comparibilityValue;
        displayListRef.current = JSON.parse(JSON.stringify(latest.displayList));
        orLogicRef.current = JSON.parse(JSON.stringify(latest.orLogic || []));
        andLogicRef.current = JSON.parse(JSON.stringify(latest.andLogic || []));
        likertScaleChoicesOptionsRef.current = latest.likertScaleChoicesOptions;
        answerChoiceRef.current = latest.answerChoice ? { ...latest.answerChoice } : null;
        likertSelectedChoiceRef.current = latest.likertSelectedChoice ? { ...latest.likertSelectedChoice } : null;
        
        baseLogicStateRef.current = {
          selectedChoiceID: latest.taskQuestion?.selectedChoiceID,
          selectedLikerChocieID: latest.taskQuestion?.selectedLikerChocieID,
          LikertScaleID: latest.taskQuestion?.LikertScaleID,
          npsScale_ID: latest.taskQuestion?.npsScale_ID,
          selectedComparibilityID: latest.taskQuestion?.selectedComparibilityID,
          inputValue: latest.taskQuestion?.inputValue,
          selectedQuestionSectionAttached: latest.taskQuestion?.selectedQuestionSectionAttached,
          selectedQuestion: latest.taskQuestion?.selectedQuestion,
          skipSectionAttached: latest.taskQuestion?.skipSectionAttached,
          skipToTarget: latest.skipToTarget,
          skipLogicInputValue: latest.skipLogicInputValue,
          likertScaleOptions: latest.likertScaleOptions,
          likertScaleName_array: latest.taskQuestion?.likertScaleName_array
        };
      }, 500);
      return () => clearTimeout(t);
    }
  }, [question.isEdit, question?.question_ID]);

  const conditionOptions = [{ label: 'Is Selected', value: 'is_selected' }];
  let customLogics = [
    { label: 'AND', value: 0 },
    { label: 'OR', value: 1 }
  ];

  useEffect(() => {
    if (taskQuestion?.ParentquestionAttached?.ORLogic?.length > 0 || taskQuestion?.ParentquestionAttached?.ANDLogic?.length > 0) {
      setHideNormalDisplay(true);
    }
  }, [taskQuestion?.ParentquestionAttached]);

  useEffect(() => {
    if (orLogic?.length > 0 || andLogic?.length > 0) {
        setIsStateReady(true);
    } else {
        setIsStateReady(false);
    }
}, [orLogic, andLogic]);

  useEffect(() => {
    if (displayList.length > 0) {
      const hasDiscontinuousSequences = displayList.some((item, index) => 
        item.parentSequence !== index + 2
      );
      
      if (hasDiscontinuousSequences) {
        setDisplayList(recalculateSequences(displayList));
      }
    }
  }, [displayList.length]);

  const dispatch = useDispatch();

  // Sync desc state when question prop changes (for GET/edit case)
  useEffect(() => {
    if (question?.questionDescription !== undefined) {
      setDesc(question.questionDescription || '');
    }
    if (question) {
      setTaskQuestion(question);
    }
  }, [question?.question_ID]);

  useEffect(() => {
    displayLogicInitRef.current = '';
  }, [question?.question_ID]);

  useEffect(() => {
    if (!taskQuestion?.ParentquestionAttached) {
      return;
    }

    const displayItems = [];
     
      // Helper to find the full question object
    const findQuestion = questionId => {
      return formTaskSections
        .flatMap(section => section.questionName_array || [])
        .find(q => String(q.question_ID) === String(questionId));
    };

    const findSectionIndex = questionId => {
      return formTaskSections.findIndex(
        section =>
          Array.isArray(section.questionName_array) &&
          section.questionName_array.some(q => String(q.question_ID) === String(questionId))
      );
    };

    if (Array.isArray(taskQuestion.ParentquestionAttached.ANDLogic)) {
      taskQuestion.ParentquestionAttached.ANDLogic.forEach((logicItem, index) => {
        const parentId = logicItem.parentQuestion_ID ?? logicItem.parentQuestionAttached;
        const parentType = logicItem.parentQuestionType ?? logicItem.parentQuestionType_ID;
        const sectionIdx = findSectionIndex(parentId);
        displayItems.push({
          id: `and-${index}`,
          condition: { label: 'AND', value: 0 },
          ...logicItem,
          parentQuestion_ID: parentId,
          parentQuestionAttached: parentId,
          parentQuestionType: parentType,
          parentQuestionType_ID: parentType,
          selectedQuestionAttached: findQuestion(parentId),
          selectedQuestionSectionAttached: sectionIdx !== -1 ? sectionIdx + 1 : undefined
        });
      });
    }

    if (Array.isArray(taskQuestion.ParentquestionAttached.ORLogic)) {
      taskQuestion.ParentquestionAttached.ORLogic.forEach((logicItem, index) => {
        const parentId = logicItem.parentQuestion_ID ?? logicItem.parentQuestionAttached;
        const parentType = logicItem.parentQuestionType ?? logicItem.parentQuestionType_ID;
        const sectionIdx = findSectionIndex(parentId);
        displayItems.push({
          id: `or-${index}`,
          condition: { label: 'OR', value: 1 },
          ...logicItem,
          parentQuestion_ID: parentId,
          parentQuestionAttached: parentId,
          parentQuestionType: parentType,
          parentQuestionType_ID: parentType,
          selectedQuestionAttached: findQuestion(parentId),
          selectedQuestionSectionAttached: sectionIdx !== -1 ? sectionIdx + 1 : undefined
        });
      });
    }

    const sortedLogic = [...displayItems].sort((a, b) => {
      if (a.parentSequence && b.parentSequence) {
        return a.parentSequence - b.parentSequence;
      }
      return 0;
    });

    const initKey = `${question?.question_ID}-${JSON.stringify(taskQuestion.ParentquestionAttached)}-${formTaskSections?.length ?? 0}`;
    if (displayLogicInitRef.current !== initKey) {
      // Keep displayList + orLogic on the SAME recalculated sequences so save
      // validation (match by parentSequence) does not false-fail on edit load.
      const normalizedLogic = recalculateSequences(sortedLogic).map((item, index) => ({
        ...item,
        questionIndex: index,
        parentSequence: index + 2,
        parentQuestionAttached: item.parentQuestionAttached ?? item.parentQuestion_ID,
        parentQuestion_ID: item.parentQuestion_ID ?? item.parentQuestionAttached,
        parentQuestionType_ID: item.parentQuestionType_ID ?? item.parentQuestionType,
        parentQuestionType: item.parentQuestionType ?? item.parentQuestionType_ID
      }));
      setDisplayList(normalizedLogic);
      setORLogic(normalizedLogic);
      setAndLogic([]);
      displayLogicInitRef.current = initKey;

      if (normalizedLogic.length > 0 && normalizedLogic[0].selectedQuestionSectionAttached) {
        const sectionIndexForDropdown = normalizedLogic[0].selectedQuestionSectionAttached - 1;
        setDropdownSectionIndex(sectionIndexForDropdown);
        setDropdownQuestions(sectionIndexForDropdown);
      }
    }

    if (sortedLogic.length === 0) {
      const pa = taskQuestion.ParentquestionAttached;
      const parentId = pa?.parentQuestionAttached ?? pa?.parentQuestion_ID;
      if (parentId) {
        const resolved = findQuestion(parentId);
        const sectionIdx = findSectionIndex(parentId);
        if (resolved) {
          setTaskQuestion(prev => {
            if (prev?.selectedQuestion) {
              return prev;
            }
            const updates = {
              ...prev,
              selectedQuestion: resolved,
              selectedQuestionSectionAttached:
                sectionIdx !== -1 ? sectionIdx + 1 : prev.selectedQuestionSectionAttached
            };
            if (pa.ifContains != null && pa.ifContains !== '' && !prev?.inputValue) {
              updates.inputValue = pa.ifContains;
            }
            const attachedChoices = pa.questionAttached?.[String(parentId)];
            if (attachedChoices?.[0] != null && prev?.selectedChoiceID == null) {
              updates.selectedChoiceID = attachedChoices[0];
            }
            if (pa.comparibility_value != null && !prev?.inputValue) {
              updates.inputValue = pa.comparibility_value;
            }
            if (pa.comparibility_ID != null && prev?.selectedComparibilityID == null) {
              updates.selectedComparibilityID = pa.comparibility_ID;
            }
            return updates;
          });

          setAttachedQuestion(prev => (prev && typeof prev === 'object' ? prev : resolved));

          const attachedChoices = pa.questionAttached?.[String(parentId)];
          if (attachedChoices?.[0] != null) {
            const choiceId = attachedChoices[0];
            if ([1, 2, 7].includes(resolved.questionType_ID)) {
              const choice = resolved.choiceName_array?.find(
                c => String(c.choiceName_ID) === String(choiceId)
              );
              if (choice) {
                setAnswerChoice(prev =>
                  prev?.value ? prev : { label: choice.choiceName, value: choice.choiceName_ID }
                );
                setAttachedChoice(prev => prev || choice.choiceName);
              }
            }
            if (resolved.questionType_ID === 16) {
              const npsSel = resolveNpsScaleSelection(resolved.likertScaleName_array, choiceId);
              if (npsSel) {
                setLikertSelectedChoice(prev => (prev?.value ? prev : npsSel));
                setTaskQuestion(prev =>
                  prev?.npsScale_ID != null && prev?.npsScale_ID !== ''
                    ? prev
                    : { ...prev, npsScale_ID: npsSel.value, selectedQuestion: resolved }
                );
              }
            }
          }
          // Type 4: questionAttached is { [choiceId]: [likertScaleId] }
          if (resolved.questionType_ID === 4 && pa.questionAttached && typeof pa.questionAttached === 'object') {
            const entry = Object.entries(pa.questionAttached)[0];
            const likertId = entry
              ? Array.isArray(entry[1])
                ? entry[1][0]
                : entry[1]
              : attachedChoices?.[0];
            if (likertId != null) {
              const match = resolved.likertScaleName_array?.find(
                s => String(s.questionLikertScale_ID) === String(likertId)
              );
              if (match) {
                setLikertSelectedChoice(prev =>
                  prev?.value
                    ? prev
                    : { label: match.questionLikertScaleName, value: match.questionLikertScale_ID }
                );
              }
            }
          }

          if (pa.comparibility_ID != null) {
            const op = resolved.MSTCompatibility_IDs?.find(
              v => String(v.id) === String(pa.comparibility_ID)
            );
            if (op) {
              setComparibilityValue(prev =>
                prev?.value ? prev : { label: op.Operations, value: op.id }
              );
            }
          }
          if (pa.comparibility_value != null) {
            setInputAnswer(prev => prev || pa.comparibility_value);
          }
          if (pa.ifContains != null && pa.ifContains !== '' && [5, 6].includes(resolved.questionType_ID)) {
            setAnswerChoice(prev =>
              prev?.label ? prev : { label: pa.ifContains, value: pa.ifContains }
            );
          }

          if (sectionIdx >= 0) {
            setDropdownSectionIndex(sectionIdx);
            setDropdownQuestions(sectionIdx);
          }
        }
      }
    }
  }, [taskQuestion?.ParentquestionAttached, formTaskSections, question?.question_ID]);

  useEffect(() => {
    if (isInterview === false) {
      const matchId = question.question_ID || question.PrevQuestion_ID;
      
      for (const data of choiceNameParentQuestions) {
        if (data.choice?.questionAttached && Array.isArray(data.choice.questionAttached)) {
          for (const id of data.choice.questionAttached || []) {
            if (id == matchId) {
              if (
                data.question.questionType_ID == 1 ||
                data.question.questionType_ID == 2 ||
                data.question.questionType_ID == 7
              ) {
                setAttachedQuestion(data.question);
                setAttachedChoice(data.choice.choiceName);
                setAnswerChoice({ label: data.choice.choiceName, value: data.choice.choiceName_ID });

                const tempTaskQuestion = {
                  ...taskQuestion,
                  selectedQuestion: data.question,
                  selectedQuestionSectionAttached: data.index + 1
                };
                setTaskQuestion(tempTaskQuestion);

                return; // Stop early once matched
              }
            }
          }
        } else {
          const attachedQuestionRefs = data?.choice?.questionAttached || data?.questionAttached;
          if (attachedQuestionRefs && typeof attachedQuestionRefs === 'object') {

            Object.entries(attachedQuestionRefs).forEach(([questionId, choiceId]) => {
              if (questionId == matchId) {
                if (data.question.questionType_ID == 4 || data.question.questionType_ID == 16) {
                  setAttachedQuestion(data.question);
                  if (data.question.questionType_ID == 4) {
                    const scaleRaw = Array.isArray(choiceId) ? choiceId[0] : choiceId;
                    const match = data.question.likertScaleName_array.find(
                      item => String(item.questionLikertScale_ID) === String(scaleRaw)
                    );
                    setLikertSelectedChoice({ label: match?.questionLikertScaleName, value: scaleRaw });
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1,
                      selectedChoiceID: data.choice.choiceName_ID,
                      selectedLikerChocieID: data.choice.choiceName_ID,
                      LikertScaleID: scaleRaw
                    };
                    setTaskQuestion(tempTaskQuestion);
                  }
                  if (data.question.questionType_ID == 16) {
                    const npsSel = resolveNpsScaleSelection(data.question.likertScaleName_array, choiceId);
                    if (npsSel) setLikertSelectedChoice(npsSel);
                    setAttachedChoice(''); // NPS stub choice is often "N/A" — don't show it
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1,
                      selectedChoiceID: data.choice.choiceName_ID,
                      selectedLikerChocieID: data.choice.choiceName_ID,
                      npsScale_ID: npsSel?.value ?? (Array.isArray(choiceId) ? choiceId[0] : choiceId)
                    };
                    setTaskQuestion(tempTaskQuestion);
                  }

                  return;
                }

                if (data.question.questionType_ID == 5 || data.question.questionType_ID == 6) {
                  setAttachedQuestion(data.question);
                  setAnswerChoice({ label: choiceId, value: choiceId });
                  let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == questionId);
                  attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);
                  const tempTaskQuestion = {
                    ...taskQuestion,
                    selectedQuestion: data.question,
                    skipSectionAttached: attachedSkipSection, 
                    selectedQuestionSectionAttached: data.index + 1,
                    ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue })
                  };
                  setTaskQuestion(tempTaskQuestion);
                  return;
                }
              }
            });
          }

          if (data.choice?.questionAttached && typeof data.choice.questionAttached === 'object') {
            Object.entries(data.choice.questionAttached).forEach(([questionId, choiceId]) => {
              if (questionId == matchId) {
                if (data.question.questionType_ID == 4 || data.question.questionType_ID == 16) {
                  setAttachedQuestion(data.question);
                  if (data.question.questionType_ID == 4) {
                    setAttachedChoice(data.choice.choiceName);
                    const scaleRaw = Array.isArray(choiceId) ? choiceId[0] : choiceId;
                    const match = data.question.likertScaleName_array.find(
                      item => String(item.questionLikertScale_ID) === String(scaleRaw)
                    );
                    setLikertSelectedChoice({ label: match?.questionLikertScaleName, value: scaleRaw });
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1,
                      selectedChoiceID: data.choice.choiceName_ID,
                      selectedLikerChocieID: data.choice.choiceName_ID,
                      LikertScaleID: scaleRaw
                    };
                    setTaskQuestion(tempTaskQuestion);
                  }
                  if (data.question.questionType_ID == 16) {
                    const npsSel = resolveNpsScaleSelection(data.question.likertScaleName_array, choiceId);
                    if (npsSel) setLikertSelectedChoice(npsSel);
                    setAttachedChoice(''); // NPS stub choice is often "N/A" — don't show it
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1,
                      selectedChoiceID: data.choice.choiceName_ID,
                      selectedLikerChocieID: data.choice.choiceName_ID,
                      npsScale_ID: npsSel?.value ?? (Array.isArray(choiceId) ? choiceId[0] : choiceId)
                    };
                    setTaskQuestion(tempTaskQuestion);
                  }
                  return;
                }
              }
            });
          }

          if (data.valueChoice?.questionAttached && typeof data.valueChoice.questionAttached === 'object') {
            Object.entries(data.valueChoice.questionAttached).forEach(([questionId, innerObj]) => {
              if (typeof innerObj == 'number') {
                if (questionId == matchId) {
                  if (
                    data.question.questionType_ID == 3 ||
                    data.question.questionType_ID == 11 ||
                    data.question.questionType_ID == 14
                  ) {
                    setAttachedQuestion(data.question);

                    setInputAnswer(innerObj);

                    setComparibilityValue({
                      label: data.comparibility_ID.Operations,
                      value: data.comparibility_ID.id
                    });
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1
                    };
                    setTaskQuestion(tempTaskQuestion);

                    return;
                  }
                }
              }
              Object.entries(innerObj).forEach(([choiceId, value]) => {
                if (questionId == matchId) {
                  if (
                    data.question.questionType_ID == 3 ||
                    data.question.questionType_ID == 11 ||
                    data.question.questionType_ID == 14
                  ) {
                    setAttachedQuestion(data.question);
                    const labelName = data?.question?.choiceName_array?.find(
                      choice => choice.choiceName_ID == choiceId
                    );
                    setAnswerChoice({
                      label: labelName?.choiceName,
                      value: choiceId
                    });

                    setInputAnswer(value);

                    setComparibilityValue({
                      label: data.comparibility_ID.Operations,
                      value: data.comparibility_ID.id
                    });
                    const tempTaskQuestion = {
                      ...taskQuestion,
                      selectedQuestion: data.question,
                      selectedQuestionSectionAttached: data.index + 1,
                      inputValue: value
                    };
                    setTaskQuestion(tempTaskQuestion);

                    return;
                  }
                }
              });
            });
          }
        }
      }
    }
  }, [choiceNameParentQuestions, question.question_ID, question.PrevQuestion_ID]);

  useEffect(() => {
    setTaskQuestion(prevTaskQuestion => ({
      ...prevTaskQuestion,
      questionDescription: desc
    }));
  }, [desc]);

  useEffect(() => {
   
    setTaskQuestion(prevTaskQuestion => {
      // Only update if this is a different question
      if (prevTaskQuestion?.question_ID === question?.question_ID) {
        return prevTaskQuestion; // Return existing state unchanged
      }
     
      const newState = {
        ...question,
        // Preserve custom properties that were added during question attachment logic
        selectedQuestion: prevTaskQuestion?.selectedQuestion,
        selectedQuestionSectionAttached: prevTaskQuestion?.selectedQuestionSectionAttached,
        selectedChoiceID: prevTaskQuestion?.selectedChoiceID,
        ParentquestionAttached: prevTaskQuestion?.ParentquestionAttached,
        EndSurvey: prevTaskQuestion?.EndSurvey,
        CompareValueArray: prevTaskQuestion?.CompareValueArray,
        sectionAttached: prevTaskQuestion?.sectionAttached,
      };
      return newState;
    });
  }, [question?.question_ID]);


  // Ensure dropdown options are populated when selectedQuestionSectionAttached is set
  useEffect(() => {
    if (taskQuestion?.selectedQuestionSectionAttached && formTaskSections?.length > 0) {
      const sectionIndex = taskQuestion.selectedQuestionSectionAttached - 1;
      if (sectionIndex >= 0 && sectionIndex < formTaskSections.length) {
        setDropdownSectionIndex(sectionIndex);
        setDropdownQuestions(sectionIndex);
      }
    }
  }, [taskQuestion?.selectedQuestionSectionAttached, formTaskSections]);

  useEffect(() => {
    setTimeout(() => {
      setIsEdit(false);
    }, 10000);
  }, [isEdit]);

  useEffect(() => {
    if (taskQuestion?.questionName != null && taskQuestion?.questionName != '') {
      setVisible(false);
    }
    if (taskQuestion?.sectionAttached && Object.keys(taskQuestion.sectionAttached).length > 0) {
      const [[section_ID, value]] = Object.entries(taskQuestion?.sectionAttached);

      // const attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
      // const tempTaskQuestion = { ...taskQuestion, skipSectionAttached: attachedSkipSection, skipchoice: value };
      // setSkipToOptions([
      //   { label: attachedSkipSection?.sectionName, value: attachedSkipSection?.questionPaperSection_ID }
      // ]);
      let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
      attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);
      const tempTaskQuestion = { 
        ...taskQuestion, 
        skipSectionAttached: attachedSkipSection, 
        skipchoice: value,
        ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue })
      };
      setSkipDropdownQuestions(sectionIndex);
      setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);
      setSkipLogicInputValue(value);
      setTaskQuestion(tempTaskQuestion);
    }
    if(taskQuestion?.EndSurvey != null && !Array.isArray(taskQuestion?.EndSurvey) && taskQuestion?.sectionAttached?.length === 0) {
      let sectionAttached = {1 : taskQuestion?.EndSurvey}
      const [[section_ID, value]] = Object.entries(sectionAttached);
      let attachedSkipSection = formTaskSections?.find(section => section.questionPaperSection_ID == section_ID);
      attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);
      const tempTaskQuestion = { 
        ...taskQuestion, 
        skipSectionAttached: attachedSkipSection, 
        skipchoice: value,
        ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
        // Preserve custom attachment properties
        ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
        ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
        ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
      };
      setSkipDropdownQuestions(sectionIndex);
      setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);
      setSkipLogicInputValue(value);
      setTaskQuestion(tempTaskQuestion);
    }

    if (taskQuestion.choiceName_array) {
      taskQuestion.choiceName_array.map(choice => {
        if (taskQuestion.questionType_ID === 16) {
          if (choice?.sectionAttached && Object.keys(choice.sectionAttached).length > 0) {
            const [[section_ID, value]] = Object.entries(choice?.sectionAttached);

            if (taskQuestion.questionType_ID == 16) {
              const likertChoice = taskQuestion.likertScaleName_array.find(op => op.questionLikertScale_ID == value);

              if (likertChoice !== undefined) {
                const likertScale = taskQuestion.likertScaleName_array?.find(
                  opt => opt.questionLikertScale_ID === value
                );

                setLikertScaleChoicesOptions(likertScale.questionLikertScale_ID);
              }
            }

            // const attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
            // setSkipToOptions([
            //   { label: attachedSkipSection?.sectionName, value: attachedSkipSection?.questionPaperSection_ID }
            // ]);
            let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
            attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);


            setSkipDropdownQuestions(sectionIndex);
            setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);
            // const tempTaskQuestion = { ...taskQuestion, skipSectionAttached: attachedSkipSection, skipchoice: value };
            const tempTaskQuestion = { 
              ...taskQuestion, 
              skipSectionAttached: attachedSkipSection, 
              skipchoice: value,
            ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
            // Preserve custom attachment properties
            ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
            ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
            ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
          };
          setTaskQuestion(tempTaskQuestion);
          }
        }
        if(choice?.EndSurvey && (!choice?.sectionAttached || Object.keys(choice.sectionAttached).length === 0)) {
          // This means "End of Survey" is applied to this choice
          const attachedSkipSection = { 
            questionPaperSection_ID: 1, 
            sectionName: "End of Survey",
            skipSectionAttachedValue: 1
          };
          setSkipDropdownQuestions(sectionIndex);
          setSkipToTarget(attachedSkipSection.questionPaperSection_ID);
          setSkipLogicInputValue(choice.EndSurvey);
          const likertChoice = taskQuestion?.likertScaleName_array?.find(op => op.questionLikertScale_ID == choice?.EndSurvey)

              if (likertChoice !== undefined) {
                const likertScale = taskQuestion?.likertScaleName_array?.find(
                  opt => opt.questionLikertScale_ID === choice?.EndSurvey
                );
                setLikertScaleChoicesOptions(likertScale?.questionLikertScale_ID);
              }
          const tempTaskQuestion = { 
            ...taskQuestion, 
            skipSectionAttached: attachedSkipSection,
            skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue,
            // Preserve custom attachment properties
            ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
            ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
            ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
          };
          setTaskQuestion(tempTaskQuestion);
        }
        
      });
    }

    if (
      taskQuestion.choiceName_array &&
      taskQuestion.choiceName_array.every(obj => {
        return obj.choiceName != '';
      })
    ) {
      setOptionVisible(false);
      setScaleTypeVisible(false);
      taskQuestion?.choiceName_array?.map(choice => {
      if (choice?.EndSurvey && (!choice?.sectionAttached || Object.keys(choice.sectionAttached).length === 0)) {
          // This means "End of Survey" is applied to this choice
          const attachedSkipSection = { 
            questionPaperSection_ID: 1, 
            sectionName: "End of Survey",
            skipSectionAttachedValue: 1
          };
          setSkipDropdownQuestions(sectionIndex);
          setSkipToTarget(attachedSkipSection.questionPaperSection_ID);
          setSkipLogicInputValue(choice.EndSurvey);
          if (taskQuestion.questionType_ID == 4) {
            const likertChoice = taskQuestion?.likertScaleName_array?.find(
              op => String(op.questionLikertScale_ID) === String(choice.EndSurvey)
            );
            setLikertScaleOptions(likertChoice);
            setSkipIfChoice(choice?.choiceName_ID || choice?.id);
          }
          const tempTaskQuestion = { 
            ...taskQuestion, 
            skipSectionAttached: attachedSkipSection,
            skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue
          };
          setTaskQuestion(tempTaskQuestion);
        }
        if (choice?.sectionAttached && Object.keys(choice.sectionAttached)?.length > 0) {
          const [[section_ID, value]] = Object.entries(choice?.sectionAttached);

          if (taskQuestion.questionType_ID == 4) {
            const scalePointId = Array.isArray(value) ? value[0] : value;
            const likertChoice = taskQuestion?.likertScaleName_array?.find(
              op => String(op.questionLikertScale_ID) === String(scalePointId)
            );
            if (likertChoice) setLikertScaleOptions(likertChoice);
            setSkipIfChoice(choice?.choiceName_ID || choice?.id);
          }

          // const attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
          // setSkipToOptions([
          //   { label: attachedSkipSection?.sectionName, value: attachedSkipSection?.questionPaperSection_ID }
          // ]);
           let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
          attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);

          setSkipLogicInputValue(value);

          setSkipDropdownQuestions(sectionIndex);
          setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);
          // const tempTaskQuestion = { ...taskQuestion, skipSectionAttached: attachedSkipSection };
          const tempTaskQuestion = { 
            ...taskQuestion, 
            skipSectionAttached: attachedSkipSection,
            ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
            // Preserve custom attachment properties
            ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
            ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
            ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
          };
          setTaskQuestion(tempTaskQuestion);
        }

        if (choice?.sectionAttached && choice?.sectionAttached?.length > 0) {
          let attachedSkipSection = formTaskSections.find(
            section => section.questionPaperSection_ID == choice?.sectionAttached[0]
          );
          // setSkipToOptions([
          //   { label: attachedSkipSection?.sectionName, value: attachedSkipSection?.questionPaperSection_ID }
          // ]);
          attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);

          setSkipDropdownQuestions(sectionIndex);
          setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);

          // const tempTaskQuestion = { ...taskQuestion, skipSectionAttached: attachedSkipSection, skipchoice: choice };
          const tempTaskQuestion = { 
            ...taskQuestion, 
            skipSectionAttached: attachedSkipSection, 
            skipchoice: choice,
            ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
            // Preserve custom attachment properties
            ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
            ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
            ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
          };
          setTaskQuestion(tempTaskQuestion);
        }
      });
    }
    
    if (taskQuestion.CompareValueArray && Object.keys(taskQuestion.CompareValueArray).length > 0) {
      Object.entries(taskQuestion.CompareValueArray).forEach(([key, choice]) => {
        let sectionAttached;
        // Case 1: choice is an array with sectionAttached
        if (Array.isArray(choice) && choice[0]?.sectionAttached) {
          // sectionAttached = choice[0].sectionAttached;
           if(choice[0]?.EndSurvey ==null){
            sectionAttached =choice[0]?.sectionAttached;
          } else {
            sectionAttached = {1:choice[0]?.EndSurvey};
          }
        }

        // Case 2: choice itself is sectionAttached object
        else if (typeof choice === 'object' && !Array.isArray(choice)) {
          sectionAttached = choice;
        }

        if (taskQuestion.questionType_ID === 14) {
          Object.entries(sectionAttached || {}).forEach(([section_ID, value]) => {
            const [[choice_ID, inputValue]] = Object.entries(value || {});
            // const attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
           let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
            attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);
            const choiceValue = taskQuestion.choiceName_array?.findIndex(
              op => String(op.choiceName_ID || op.id) === String(choice_ID)
            );
            const compareValue =
              taskQuestion.MSTCompatibility_IDs?.find(op => String(op.id) === String(key)) ||
              taskQuestion.MSTCompatibility_IDs?.[Number(key) - 1];

            // setSkipToOptions([
            //   { label: attachedSkipSection?.sectionName, value: attachedSkipSection?.questionPaperSection_ID }
            // ]);
            setSkipLogicInputValue(inputValue);
            setLikertScaleChoicesOptions(compareValue);
            setSkipIfChoice(choice_ID);

            setSkipDropdownQuestions(sectionIndex);
            setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);

            const tempTaskQuestion = {
              ...taskQuestion,
              skipSectionAttached: attachedSkipSection,
              choiceObj: choiceValue,
              skipchoice: inputValue,
              choiceRankID: choice_ID,
              chocieRankIndex: compareValue
                ? taskQuestion.MSTCompatibility_IDs?.findIndex(op => String(op.id) === String(compareValue.id))
                : -1,
              ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
              // Preserve custom attachment properties
              ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
              ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
              ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
            };

            setTaskQuestion(tempTaskQuestion);
          });
        } else {
          const isValidSectionAttached =
            sectionAttached &&
            typeof sectionAttached === 'object' &&
            !Array.isArray(sectionAttached) &&
            Object.keys(sectionAttached).length > 0;

          if (isValidSectionAttached) {
            const [[section_ID, value]] = Object.entries(sectionAttached);

            // const attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);
            let attachedSkipSection = formTaskSections.find(section => section.questionPaperSection_ID == section_ID);

            attachedSkipSection = resolveSkipSection(attachedSkipSection, taskQuestion);
            const compareValue = taskQuestion.MSTCompatibility_IDs?.find(op => op.id == key);

            setLikertScaleChoicesOptions(compareValue);
            // setSkipToOptions([
            //   { label: attachedSkipSection.sectionName, value: attachedSkipSection.questionPaperSection_ID }
            // ]);
            setSkipLogicInputValue(value);

            setSkipDropdownQuestions(sectionIndex);
            setSkipToTarget(attachedSkipSection?.questionPaperSection_ID);

            const tempTaskQuestion = {
              ...taskQuestion,
              skipSectionAttached: attachedSkipSection,
              skipchoice: value,
              ...(attachedSkipSection?.skipSectionAttachedValue && { skipSectionAttachedValue: attachedSkipSection.skipSectionAttachedValue }),
              // Preserve custom attachment properties
              ...(taskQuestion.selectedQuestionSectionAttached && { selectedQuestionSectionAttached: taskQuestion.selectedQuestionSectionAttached }),
              ...(taskQuestion.selectedQuestion && { selectedQuestion: taskQuestion.selectedQuestion }),
              ...(taskQuestion.selectedChoiceID && { selectedChoiceID: taskQuestion.selectedChoiceID })
            };

            setTaskQuestion(tempTaskQuestion);
          }
        }
      });
    }
  }, [question.question_ID, formTaskSections]);

  let options = taskQuestion?.choiceName_array;
  let tagList = [];
  if (
    question.questionType_ID == 1 ||
    question.questionType_ID == 2 ||
    question.questionType_ID == 3 ||
    question.questionType_ID == 14
  ) {
    {
      [...Array(2)].map((opt, oIndex) => {
        options?.length <= oIndex && options.push({ choiceName: '', choiceName_ID: '' });
      });
    }
  }
  if (question.questionType_ID == 7) {
    {
      [...Array(7)].map((opt, oIndex) => {
        options?.length <= oIndex && options.push({ choiceName: '', choiceName_ID: '' });
      });
    }
  }

  if (question.questionType_ID == 9) {
    options =
      screenerTypeOption?.map(option => {
        return { ...option, label: option.groupFilterLable, value: option.question_ID };
      }) || [];

    options.unshift({ value: null, label: 'Select one', question_ID: null, groupFilterLable: null });

    options = options?.filter(function(option) {
      return option != null;
    });
  }
  if (question.questionType_ID == 10) {
    options = masterTags?.map(option => {
      return { ...option, label: option.tag, value: option.tag_ID };
    });

    options = options?.filter(function(option) {
      return option != null;
    });

    tagList =
      taskQuestion?.tags?.length > 0
        ? taskQuestion?.tags?.map(tag => {
            return { ...tag };
          })
        : [];

    tagList = tagList.filter(function(option) {
      return option != null;
    });
  }
  if (question.questionType_ID == 11) {
    options =
      ValueTypeChoices?.length > 0
        ? ValueTypeChoices?.map(val => {
            return { ...val, label: val.valueChoice, value: val.id };
          })
        : [];
  }

  let likerTOptions =
    question.questionType_ID == 4 ? resolveLikertScaleRows(taskQuestion) : [];
  let LikertScaleType =
    question.questionType_ID == 4 ? taskQuestion?.LikertScaleType || taskQuestion?.scaleType || null : null;

  let questionTypesDefaultValue =
    questionTypes != undefined
      ? questionTypes[questionTypes?.findIndex(qt => qt.questionType_ID == question.questionType_ID)]
      : {};

  const handleQuestionFields = (_field, _value, _scaleType) => {
    let tempTaskQuestion = taskQuestionRef.current || taskQuestion;

    if (_field === 'likertScaleOptions') {
      const nextScaleOptions = (isMatrixScaleType(_scaleType)
        ? _value
        : (_value || []).map(opt => setScaleShuffleState(opt, false))
      ).map(opt => ({ ...opt, shuffleScale: getScaleShuffleFlag(opt) }));
      tempTaskQuestion = {
        ...tempTaskQuestion,
        likertScaleOptions: nextScaleOptions,
        scaleType: _scaleType,
        LikertScaleType: _scaleType,
        isEdit: true
      };
    } else {
      tempTaskQuestion = { ...tempTaskQuestion, [_field]: _value };
      if (_field === 'questionImage') {
        const fileType = _value.type;

        let currentMediaType = '';
        if (fileType.startsWith('image/')) {
          currentMediaType = 0;
        } else if (fileType.startsWith('video/')) {
          currentMediaType = 1;
        } else if (fileType === 'application/pdf'|| fileName.endsWith('.pdf') || fileName.startsWith('pdf/')) {
      currentMediaType = 2;
    } else {
          console.warn('Unsupported file type:', fileType);
          return;
        }
        tempTaskQuestion = { ...tempTaskQuestion, ['fileType']: currentMediaType };
      }
    }

    if (_field == 'screenerQuestion_ID') {
      let index = screenerTypeOption.findIndex(option => option.question_ID == _value);
      tempTaskQuestion = { ...tempTaskQuestion, savedOption: screenerTypeOption[index].groupFilterLable };
    }
    if (_field == 'valueQuestion_ID') {
      let index = ValueTypeChoices.findIndex(option => option.id == _value);
      tempTaskQuestion = { ...tempTaskQuestion, savedOption: ValueTypeChoices[index]?.valueChoice };
    }
    setVisible(false);

    taskQuestionRef.current = tempTaskQuestion;
    setTaskQuestion(tempTaskQuestion);
  };

  const handleOptions = choices => {
    const safeChoices = (choices || []).map(choice => ({ ...choice }));
    setTaskQuestion(prev => {
      const next = { ...prev, isEdit: true, choiceName_array: safeChoices };
      taskQuestionRef.current = next;
      return next;
    });
  };

  const handleTags = tags => {
    let tempTaskQuestion = { ...taskQuestion, isEdit: true };
    tempTaskQuestion.tags = tags;

    setTaskQuestion(tempTaskQuestion);
  };
  const handleDeleteTag = tags => {
    handleTags(tags);
    storeOrUpdateQuestion(taskQuestion?.question_ID);
    handleTags(tags);
  };
  // Keyboard: ref = active field before React commits state. Likert-only: pad choice rows / vk-ignore (see questionType_ID === 4).
  const handleKeyboardChange = input => {
    const field = currentInputFieldRef.current;
    if (field === 'questionName') {
      setTaskQuestion(prev => {
        const next = { ...prev, questionName: input, isEdit: true };
        taskQuestionRef.current = next;
        return next;
      });
    } else if (field === 'questionDescription') {
      setDesc(input);
      setTaskQuestion(prev => {
        const next = { ...prev, questionDescription: input, isEdit: true };
        taskQuestionRef.current = next;
        return next;
      });
    } else if (typeof field === 'string' && field.startsWith('choiceName:')) {
      const choiceIndex = Number(field.split(':')[1]);
      if (!Number.isNaN(choiceIndex)) {
        setTaskQuestion(prev => {
          const updatedChoices = [...(prev?.choiceName_array || [])];
          const isLikert = prev?.questionType_ID === 4;
          if (isLikert) {
            while (updatedChoices.length <= choiceIndex) {
              updatedChoices.push({ choiceName_ID: generateUniqueID(), choiceName: '' });
            }
            const prevRow = updatedChoices[choiceIndex] || {};
            updatedChoices[choiceIndex] = {
              ...prevRow,
              choiceName_ID: prevRow.choiceName_ID || generateUniqueID(),
              choiceName: input
            };
          } else {
            if (!updatedChoices[choiceIndex]) return prev;
            updatedChoices[choiceIndex] = { ...updatedChoices[choiceIndex], choiceName: input };
          }
          const next = { ...prev, choiceName_array: updatedChoices, isEdit: true };
          taskQuestionRef.current = next;
          return next;
        });
      }
    } else if (typeof field === 'string' && field.startsWith('likertScaleName:')) {
      const scaleIndex = Number(field.split(':')[1]);
      if (!Number.isNaN(scaleIndex)) {
        const sanitized = input.replace(/,/g, ';');
        setTaskQuestion(prev => {
          if (prev?.questionType_ID !== 4) return prev;
          const arr = [...(prev?.likertScaleOptions || [])];
          if (!arr[scaleIndex]) return prev;
          arr[scaleIndex] = { ...arr[scaleIndex], questionLikertScaleName: sanitized };
          const next = { ...prev, likertScaleOptions: arr, isEdit: true };
          taskQuestionRef.current = next;
          return next;
        });
      }
    }
  };

  const handleInputFocus = fieldName => {
    if (!isVirtualKeyboardEnabled) return;
    currentInputFieldRef.current = fieldName;
    setCurrentInputField(fieldName);
    setShowKeyboard(true);
  };
  const handleChoiceInputFocus = choiceIndex => {
    if (!isVirtualKeyboardEnabled) return;
    const key = `choiceName:${choiceIndex}`;
    currentInputFieldRef.current = key;
    setCurrentInputField(key);
    setShowKeyboard(true);
  };

  const handleLikertCustomFocus = scaleIndex => {
    if (!isVirtualKeyboardEnabled) return;
    const key = `likertScaleName:${scaleIndex}`;
    currentInputFieldRef.current = key;
    setCurrentInputField(key);
    setShowKeyboard(true);
  };

  const getKeyboardValue = () => {
    if (currentInputField === 'questionName') return taskQuestion.questionName || '';
    if (currentInputField === 'questionDescription') return desc || '';
    if (typeof currentInputField === 'string' && currentInputField.startsWith('choiceName:')) {
      const choiceIndex = Number(currentInputField.split(':')[1]);
      if (!Number.isNaN(choiceIndex)) {
        return taskQuestion?.choiceName_array?.[choiceIndex]?.choiceName || '';
      }
    }
    if (typeof currentInputField === 'string' && currentInputField.startsWith('likertScaleName:')) {
      const scaleIndex = Number(currentInputField.split(':')[1]);
      if (!Number.isNaN(scaleIndex)) {
        return taskQuestion?.likertScaleOptions?.[scaleIndex]?.questionLikertScaleName || '';
      }
    }
    return '';
  };

  const handleKeyboardClose = () => {
    currentInputFieldRef.current = null;
    setShowKeyboard(false);
    setCurrentInputField(null);
  };

  const isQuestionKeyboardVisible =
    isVirtualKeyboardEnabled &&
    showKeyboard &&
    (currentInputField === 'questionName' || currentInputField === 'questionDescription');

  const isChoiceKeyboardVisible =
    isVirtualKeyboardEnabled &&
    showKeyboard &&
    typeof currentInputField === 'string' &&
    (currentInputField.startsWith('choiceName:') || currentInputField.startsWith('likertScaleName:'));

  // Close keyboard when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const ignore = event?.target?.closest?.('[data-virtualkeyboard-ignore="true"]');
      if (ignore) return;
      if (showKeyboard && keyboardContainerRef.current && !keyboardContainerRef.current.contains(event.target)) {
        handleKeyboardClose();
      }
    };

    if (showKeyboard) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showKeyboard]);

  const closeHandler = e => {
    setPopup(e);
  };

  const changeQuestionType = e => {
    let tempTaskQuestion = taskQuestion;
    const newTypeId = e.value;
    const isChoiceType = [1, 2, 7].includes(newTypeId);

    if (!isChoiceType) {
      tempTaskQuestion = { ...tempTaskQuestion, choiceName_array: [] };
    } else if (!Array.isArray(tempTaskQuestion.choiceName_array)) {
      tempTaskQuestion = { ...tempTaskQuestion, choiceName_array: [] };
    }
    tempTaskQuestion = {
      ...tempTaskQuestion,
      questionType_ID: e.value,
      oldQuestionType_ID: tempTaskQuestion.questionType_ID
    };
    setIsQuestionSavedForSkipLogic(false);
    setTaskQuestion(tempTaskQuestion);

    handleChangeQuestionType(+sectionIndex, +questionIndex, e.value, tempTaskQuestion);
  };
  const handleCheckboxrequired = e => {
    // setCheckBoxRequired(!checkBoxRequired);
    const newChecked = checkBoxRequired == 0 ? 1 : 0;
    setTaskQuestion(prevState => ({
      ...prevState,
      isRequired: newChecked
    }));
    setCheckBoxRequired(newChecked);
    setSkipToTarget(newChecked);
  };

  const liveSectionShuffleValue =
    formTaskSections?.[sectionIndex]?.shuffleQuestion ??
    interviewTaskSections?.[sectionIndex]?.shuffleQuestion ??
    question?.shuffleQuestion ??
    taskQuestion?.shuffleQuestion;
  const isSectionShuffleOn =
    Number(sectionShuffleEnabled) === 1 || Number(liveSectionShuffleValue) === 1;
  const conditionDisableMessage = 'Cannot apply conditions when questions are shuffled';
  const blockConditionToggle = e => {
    if (!isSectionShuffleOn) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDisplayCheckbox = e => {
    if (isSectionShuffleOn) return;
    const newChecked = checkboxDisplay == 0 ? 1 : 0;

    setCheckBoxdisplay(newChecked);

    if (newChecked === 1) {
      setHideNormalDisplay(false);
    } else {
      setHideNormalDisplay(true);
      setDisplayList([]);
    }
  };
  const handleSkipChange = e => {
    if (isSectionShuffleOn) return;
    setIsSkipEnabled(e.target.checked);

    setSkipDropdownQuestions(sectionIndex);
    if (!e.target.checked) {
      // setSkipToTarget('1');
      setSkipIfChoice(null);
      setSkipCondition('is_selected');
    }
  };

  const isScaleQuestionType = [4, 16].includes(Number(taskQuestion?.questionType_ID));
  // Form only: logic applied disables Shuffle Scale. Interview ignores this check.
  const isShuffleScaleDisabled =
    isInterview === false && isScaleQuestionType && Number(checkboxDisplay) === 1;

  const handleShuffleToggle = e => {
    if (isShuffleScaleDisabled) return;
    const newShuffleValue = shuffleEnabled == 0 ? 1 : 0;
    const baseQuestion = taskQuestionRef.current || taskQuestion;
    const updatedChoices = Array.isArray(baseQuestion.choiceName_array)
      ? baseQuestion.choiceName_array.map(choice => setChoiceShuffleState(choice, false))
      : baseQuestion.choiceName_array;
    const scaleRows = resolveLikertScaleRows(baseQuestion);
    const updatedScaleOptions = scaleRows.length
      ? scaleRows.map(opt => setScaleShuffleState(opt, false))
      : scaleRows;
    const nextTaskQuestion = {
      ...baseQuestion,
      toShuffle: newShuffleValue,
      isEdit: true,
      ...(updatedChoices ? { choiceName_array: updatedChoices } : {}),
      ...(updatedScaleOptions?.length ? { likertScaleOptions: updatedScaleOptions } : {})
    };
    taskQuestionRef.current = nextTaskQuestion;
    setTaskQuestion(nextTaskQuestion);
    setShuffleEnabled(newShuffleValue);
  };

  const handleChoiceShuffleToggle = (index, checked) => {
    const tempChoices = (taskQuestion.choiceName_array || []).map((choice, i) =>
      i === index ? setChoiceShuffleState(choice, checked) : choice
    );
    handleOptions(tempChoices);
  };

  const handleToggleChoiceShuffleTag = index => {
    const currentChoice = (taskQuestion.choiceName_array || [])[index];
    handleChoiceShuffleToggle(index, getChoiceShuffleFlag(currentChoice) !== 1);
  };

  const handleToggleScaleShuffleTag = index => {
    const baseQuestion = taskQuestionRef.current || taskQuestion;
    const scaleType = baseQuestion.scaleType || baseQuestion.LikertScaleType;
    const rows = resolveLikertScaleRows(baseQuestion);
    const nextChecked = getScaleShuffleFlag(rows[index]) !== 1;
    const tempScaleOptions = rows.map((opt, i) =>
      i === index ? setScaleShuffleState(opt, nextChecked) : { ...opt, shuffleScale: getScaleShuffleFlag(opt) }
    );
    handleQuestionFields('likertScaleOptions', tempScaleOptions, scaleType);
  };

  const storeOrUpdateQuestion = (questionId, questionIndex, taskQuestionSnapshot) => {
    // Use the latest snapshot to avoid stale closure issues.
    // Not const: the display-logic block below may promote the first display row into the base condition.
    let taskQuestion = taskQuestionSnapshot ?? taskQuestionRef.current;
    let isAutomatedAnswer_array = [];

    setSkipLogicError('');

    if (
      getShuffleChoiceValidationMessage(
        taskQuestion?.questionType_ID,
        shuffleEnabled,
        taskQuestion?.choiceName_array
      )
    ) {
      return;
    }

    if (
      getShuffleScaleValidationMessage(
        taskQuestion?.questionType_ID,
        taskQuestion?.scaleType || taskQuestion?.LikertScaleType,
        shuffleEnabled,
        resolveLikertScaleRows(taskQuestion)
      )
    ) {
      return;
    }

    if (isSkipEnabled) {
      // if (skipToTarget != 1) {
      //   setSkipLogicError('Please select a "SKIP TO" target.');
      //   return;
      // }
      switch (taskQuestion.questionType_ID) {
        case 1: // Single Choice
        case 2: // Multiple Choice
        case 7: // Single Seven
          if (!skipIfChoice) {
            setSkipLogicError('Please select an option for "IF".');
            return;
          }
          // if (!skipCondition) {
          //   setSkipLogicError('Please select a condition (e.g., "Is Selected").');
          //   return;
          // }
          break;
        case 4: // Likert
          if (taskQuestion.LikertScaleType === 'matrix' || taskQuestion.LikertScaleType === 'Custom Likert') {
            const invalidOptions = taskQuestion?.likertScaleOptions?.some(
              opt =>
                !opt.questionLikertScaleName ||
                opt.questionLikertScaleName.trim() === '' ||
                opt.questionLikertScaleName.startsWith('Enter Parameter')
            );
            if (invalidOptions) {
              setCustomLikertError('Fill all Parameters');
              setOptionVisible(true);
              return; // Stop saving
            } else {
              // Check for duplicates
              const values = (Array.isArray(taskQuestion?.likertScaleOptions) ? taskQuestion.likertScaleOptions : []).map(opt =>
                opt.questionLikertScaleName.trim()
              );
              const uniqueValues = new Set(values);
              if (uniqueValues.size !== values.length) {
                setCustomLikertError('Some Parameters are Same');
                setOptionVisible(true);
                return; // Stop saving
              }
              setCustomLikertError(null);
            }
          }
          if (!skipIfChoice) {
            setSkipLogicError('Please select a statement for "IF".');
            return;
          }
          if (!likertScaleOptions) {
            setSkipLogicError('Please select a scale point for the Likert condition.');
            return;
          }
          break;
        case 16: // NPS
          if (likertScaleChoicesOptions === null || likertScaleChoicesOptions === undefined) {
            setSkipLogicError('Please select an NPS scale point for the condition.');
            return; // Stop saving
          }
          break;
        case 3: // Slider
        case 11: // Value Question
          if (!likertScaleChoicesOptions) {
            // This holds the operation ID (equals, greater than, etc.)
            setSkipLogicError('Please select an operation (e.g., "Equals", "Greater than").');
            return;
          }
          if ((skipLogicInputValue ?? '').toString().trim() === '') {
            setSkipLogicError('Please enter a value for the condition.');
            return;
          }
          break;
        case 5: // Text Answer (Short)
        case 6: // Text Answer (Long)
          if ((skipLogicInputValue ?? '').toString().trim() === '') {
            setSkipLogicError('Please enter a value for the condition.');
            return;
          }

          break;
        case 14: // Ranking
          if (!skipIfChoice) {
            setSkipLogicError('Please select an item to rank for "IF".');
            return;
          }
          if (!likertScaleChoicesOptions) {
            setSkipLogicError('Please select an operation for the ranking condition.');
            return;
          }
          if ((skipLogicInputValue ?? '').toString().trim() === '') {
            setSkipLogicError('Please enter a value for the condition.');
            return;
          }
          break;
        case 16:
          if (!likertScaleChoicesOptions) {
            setSkipLogicError('Please select scale value.');
            return;
          }

        default:
          break;
      }
    }
    if (
      taskQuestion.questionType_ID == 1 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 2 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 3 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 7 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 4 &&
      (taskQuestion.LikertScaleType === 'matrix' || taskQuestion.LikertScaleType === 'Custom Likert') &&
      taskQuestion.likertScaleOptions?.some(
        opt =>
          !opt.questionLikertScaleName ||
          opt.questionLikertScaleName.trim() === '' ||
          opt.questionLikertScaleName.startsWith('Enter Parameter')
      )
    ) {
      setCustomLikertError('Fill all Parameters');
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 4 &&
      (taskQuestion.LikertScaleType === 'matrix' || taskQuestion.LikertScaleType === 'Custom Likert') &&
      taskQuestion.likertScaleOptions &&
      new Set(taskQuestion.likertScaleOptions.map(opt => opt.questionLikertScaleName.trim())).size !==
      taskQuestion.likertScaleOptions.length
    ) {
      setCustomLikertError('Some Parameters are Same');
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 4 && !taskQuestion?.scaleType && !taskQuestion?.LikertScaleType) {
      setScaleTypeVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 4 && taskQuestion.choiceName_array.length == 0) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 4 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (
      taskQuestion.questionType_ID == 14 &&
      taskQuestion.choiceName_array.some(obj => {
        return obj.choiceName == '';
      })
    ) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 5 && taskQuestion.characterLimit == '') {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 6 && taskQuestion.characterLimit == '') {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 9 && taskQuestion.savedOption == null) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 10 && taskQuestion.buttonName == null) {
      setOptionVisible(true);
      saveQuestion = false;
    } else if (taskQuestion.questionType_ID == 11 && taskQuestion.savedOption == '') {
      setOptionVisible(true);
      saveQuestion = false;
    } else {
      saveQuestion = true;
    }
    if (taskQuestion.questionName == null || taskQuestion.questionName == '') {
      setVisible(true);
    }

    if (taskQuestion.questionName != null && taskQuestion.questionName != '' && saveQuestion) {
      setOptionVisible(false);

      // Check if any changes were made (only for existing questions)
      if (questionId !== null) {
        // Use snapshot or fallback to current question prop if snapshot is missing
        const original = snapshotQuestion.current || question;
        const originalIsRequired = original.isRequired == 0 || original.isRequired == null ? 0 : 1;
        const originalIsLogicApplied = original.isLogicApplied == 0 || original.isLogicApplied == null ? 0 : 1;
        const originalToShuffle = original.toShuffle == 0 || original.toShuffle == null ? 0 : 1;
        const originalIsSkipLogicApplied = original.isSkipLogicApplied === 1 ? 1 : 0;

        const hasTypeChange =
          taskQuestion.questionType_ID != original.questionType_ID ||
          (taskQuestion.oldQuestionType_ID != null && taskQuestion.oldQuestionType_ID != taskQuestion.questionType_ID);

        const hasBasicChanges =
          taskQuestion.questionName !== original.questionName ||
          (taskQuestion.questionDescription || '') !== (original.questionDescription || '') ||
          hasTypeChange ||
          checkBoxRequired !== originalIsRequired ||
          checkboxDisplay !== originalIsLogicApplied ||
          shuffleEnabled !== originalToShuffle ||
          isSkipEnabled !== originalIsSkipLogicApplied ||
          taskQuestion.characterLimit !== original.characterLimit ||
          taskQuestion.maxFileSizeInMB !== original.maxFileSizeInMB ||
          taskQuestion.numberOfFiles !== original.numberOfFiles ||
          taskQuestion.buttonName !== original.buttonName ||
          taskQuestion.savedOption !== original.savedOption ||
          // NPS scale style change (type 16)
          (taskQuestion.npsScale_ID ?? null) !== (original.npsScale_ID ?? null) ||
          // Value question type change (type 11)
          (taskQuestion.valueQuestion_ID ?? null) !== (original.valueQuestion_ID ?? null) ||
          // Question image removed (selectedFile covers new upload; this covers removal)
          (taskQuestion.questionImage ?? null) !== (original.questionImage ?? null) ||
          (taskQuestion.presignedUrlQuestionImage ?? null) !== (original.presignedUrlQuestionImage ?? null) ||
          // Display logic parent question changed
          (taskQuestion.selectedQuestion?.question_ID ?? null) !== (baseLogicStateRef.current?.selectedQuestion?.question_ID ?? null) ||
          // Likert changes
          JSON.stringify(taskQuestion.likertScaleOptions || []) !== JSON.stringify(original.likertScaleOptions || []) ||
          JSON.stringify(taskQuestion.likertScaleChoicesOptions || []) !== JSON.stringify(original.likertScaleChoicesOptions || []) ||
          taskQuestion.LikertScaleType !== original.LikertScaleType ||
          // Likert scaleType field (separate from LikertScaleType, both used in formData)
          (taskQuestion.scaleType ?? null) !== (original.scaleType ?? null) ||
          // Likert labels
          JSON.stringify(taskQuestion.likertScaleName_array || []) !== JSON.stringify(baseLogicStateRef.current?.likertScaleName_array || []);

        const originalChoices = (original.choiceName_array || []).map(c => c.choiceName);
        const currentChoices = (taskQuestion.choiceName_array || []).map(c => c.choiceName);
        const editQuestionTypeId = Number(taskQuestion.questionType_ID);
        const hasChoiceChanges = CHOICE_JSON_TYPES.includes(editQuestionTypeId)
          ? hasJsonChoiceChanges({
              questionTypeId: editQuestionTypeId,
              originalRows: original.choiceName_array,
              currentRows: taskQuestion.choiceName_array,
              shuffleEnabled
            })
          : SCALE_EDIT_JSON_TYPES.includes(editQuestionTypeId)
            ? hasJsonLikertEditChanges({
                questionTypeId: editQuestionTypeId,
                originalChoiceRows: original.choiceName_array,
                currentChoiceRows: taskQuestion.choiceName_array,
                originalLikertRows: resolveScaleRowsForEdit(original, editQuestionTypeId, tempLikerTOptions),
                currentLikertRows: resolveScaleRowsForEdit(taskQuestion, editQuestionTypeId, tempLikerTOptions),
                shuffleEnabled,
                scaleType: taskQuestion?.scaleType || taskQuestion?.LikertScaleType || ''
              })
            : JSON.stringify(originalChoices) !== JSON.stringify(currentChoices);

        const originalTags = original.tags || [];
        const currentTags = taskQuestion.tags || [];
        const hasTagChanges = JSON.stringify(originalTags) !== JSON.stringify(currentTags);

        // Additional check for skip logic related states if skip is enabled
        let hasSkipChanges = false;
        if (isSkipEnabled) {
          hasSkipChanges =
            skipToTarget !== baseLogicStateRef.current?.skipToTarget ||
            skipLogicInputValue !== baseLogicStateRef.current?.skipLogicInputValue ||
            skipIfChoice !== skipIfChoiceRef.current ||
            // Likert/NPS skip: scale point comparator changed
            (likertScaleChoicesOptions ?? null) !== (likertScaleChoicesOptionsRef.current ?? null) ||
            // Likert skip (type 4 specifically)
            (likertScaleOptions?.questionLikertScale_ID ?? null) !== (baseLogicStateRef.current?.likertScaleOptions?.questionLikertScale_ID ?? null);
        }

        // Types 3, 11, 14: compare condition value, operator, and choice selection
        const hasConditionChanges =
          ([3, 11, 14].includes(taskQuestion.questionType_ID) && (
            inputAnswer !== inputAnswerRef.current ||
            (comparibilityValue?.value ?? null) !== (comparibilityValueRef.current?.value ?? null) ||
            (answerChoice?.value ?? null) !== (answerChoiceRef.current?.value ?? null)
          ));

        // Display logic AND/OR logic conditions change
        // Also compare displayList state directly — user may add/edit/remove rows
        // without taskQuestion.ParentquestionAttached updating until save
        const hasDisplayLogicChanges =
          JSON.stringify(taskQuestion.ParentquestionAttached?.ANDLogic || []) !==
            JSON.stringify(original.ParentquestionAttached?.ANDLogic || []) ||
          JSON.stringify(taskQuestion.ParentquestionAttached?.ORLogic || []) !==
            JSON.stringify(original.ParentquestionAttached?.ORLogic || []) ||
          JSON.stringify(displayList.map(({ id, ...rest }) => rest)) !==
            JSON.stringify(displayListRef.current.map(({ id, ...rest }) => rest)) ||
          JSON.stringify(orLogic) !== JSON.stringify(orLogicRef.current) ||
          JSON.stringify(andLogic) !== JSON.stringify(andLogicRef.current);

        let hasBaseDisplayLogicChanges = false;
        if (checkboxDisplay === 1 && baseLogicStateRef.current) {
          hasBaseDisplayLogicChanges = 
            (taskQuestion.selectedChoiceID ?? null) !== (baseLogicStateRef.current.selectedChoiceID ?? null) ||
            (taskQuestion.selectedLikerChocieID ?? null) !== (baseLogicStateRef.current.selectedLikerChocieID ?? null) ||
            (taskQuestion.LikertScaleID ?? null) !== (baseLogicStateRef.current.LikertScaleID ?? null) ||
            (taskQuestion.npsScale_ID ?? null) !== (baseLogicStateRef.current.npsScale_ID ?? null) ||
            (taskQuestion.selectedComparibilityID ?? null) !== (baseLogicStateRef.current.selectedComparibilityID ?? null) ||
            (taskQuestion.inputValue ?? null) !== (baseLogicStateRef.current.inputValue ?? null) ||
            (taskQuestion.selectedQuestionSectionAttached ?? null) !== (baseLogicStateRef.current.selectedQuestionSectionAttached ?? null) ||
            (likertSelectedChoice?.value ?? null) !== (likertSelectedChoiceRef.current?.value ?? null) ||
            inputAnswer !== inputAnswerRef.current ||
            (comparibilityValue?.value ?? null) !== (comparibilityValueRef.current?.value ?? null) ||
            (answerChoice?.value ?? null) !== (answerChoiceRef.current?.value ?? null);
        }

        if (!hasBasicChanges && !hasChoiceChanges && !hasTagChanges && !hasSkipChanges && !hasConditionChanges && !hasDisplayLogicChanges && !hasBaseDisplayLogicChanges && !selectedFile) {
          // No changes detected, exit edit mode without API call
          let tempTaskQuestion = { ...taskQuestion, isEdit: false };
          setAddQuestionDisable(false);
          setTaskQuestion(tempTaskQuestion);
          setEditQuestionId(''); // Clear parent edit ID to ensure edit mode exits

          if (task.taskType === 'Form') {
            const updatedSections = [...formTaskSections];
            const updatedSection = { ...updatedSections[sectionIndex] };
            updatedSection.questionName_array = [...updatedSection.questionName_array];
            updatedSection.questionName_array[questionIndex] = tempTaskQuestion;
            updatedSections[sectionIndex] = updatedSection;
            setFormTaskSections(updatedSections);
          } else {
            const updatedSections = [...interviewTaskSections];
            const updatedSection = { ...updatedSections[sectionIndex] };
            updatedSection.questionName_array = [...updatedSection.questionName_array];
            updatedSection.questionName_array[questionIndex] = tempTaskQuestion;
            updatedSections[sectionIndex] = updatedSection;
            setInterviewTaskSections(updatedSections);
          }

          handleSetEditing();
          return;
        }
      }

      const formData = new FormData();
      formData.append('isLogicApplied', '');
      formData.append('isSkipLogicApplied', '');
      formData.append('user_ID', userId);
      formData.append('questionPaperSection_ID', sectionId);
      formData.append('questionType_ID', taskQuestion.questionType_ID);
      formData.append('questionDescription', desc);
      formData.append('questionOrder', questionIndex + 1);
      formData.append('NewQuestionType_ID', taskQuestion.questionType_ID);
      formData.append(
        'OldQuestionType_ID',
        taskQuestion.oldQuestionType_ID != null ? taskQuestion.oldQuestionType_ID : taskQuestion.questionType_ID
      );
      formData.append('questionName', taskQuestion.questionName);
      if (taskQuestion?.tags) {
        formData.append('tags', JSON.stringify(taskQuestion.tags));
      }

      if (taskQuestion?.questionImage || preview) {
        formData.append('questionImage', taskQuestion?.questionImage || preview);
        formData.append('fileType', taskQuestion?.fileType);
      } else {
        formData.append('questionImage', '');
        formData.append('fileType', '');
      }
      //   formData.append('questionDescription', taskQuestion.questionDescription);
      formData.append('isRequired', checkBoxRequired);
      formData.append('isLogicApplied', checkboxDisplay ? 1 : 0);
      formData.append('isSkipLogicApplied', isSkipEnabled ? 1 : 0);

      formData.append('toShuffle', shuffleEnabled);
      formData.append('isAutomatedQuestion', 0);

      const isEditMode = questionId != null;
      const isQuestionTypeChanged =
        taskQuestion.oldQuestionType_ID != null &&
        Number(taskQuestion.oldQuestionType_ID) !== Number(taskQuestion.questionType_ID);
      // Type changed from dropdown on an existing question → use create payload for the new type
      const useCreateTypePayload = !isEditMode || isQuestionTypeChanged;
      const useEditTypePayload = isEditMode && !isQuestionTypeChanged;
      const isNewChoice = choice => {
        const id = choice.choiceName_ID;
        if (!id && id !== 0) return true;
        if (id === 0) return true;
        if (typeof id === 'string') {
          return isNaN(Number(id));
        }
        return typeof id === 'number' ? id <= 0 : false;
      };

      const buildCreateChoicesPayload = () =>
        buildAddQuestionChoiceObjects({
          rows: taskQuestion?.choiceName_array || [],
          isResponded: Number(taskQuestion.isResponded),
          isNewChoice,
          shuffleEnabled
        });

      // Helper function to get question details with fallback
      const getQuestionDetails = () => {
        const selectedQuestion =
          taskQuestion?.selectedQuestion ||
          (attachedQuestionAfter && typeof attachedQuestionAfter === 'object'
            ? attachedQuestionAfter
            : null);
        return {
          questionId: selectedQuestion?.question_ID,
          questionTypeId: selectedQuestion?.questionType_ID,
          questionName: selectedQuestion?.questionName
        };
      };

      // comparibility_ID is an MSTCompatibility id and must go out numeric. Dropdowns hand back
      // numbers, but server-hydrated logic and the display rows carry strings, and JSON.stringify
      // on a string shipped literal quotes to the API.
      const toComparibilityId = value => {
        if (value === null || value === undefined || value === '') return value;
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value;
      };

      const cleanLogicForApi = (logicArray) => {
  return (logicArray || [])
    .map(item => {
      const cleaned = {};
      
      if (item.questionAttached !== undefined && typeof item.questionAttached === 'object') {
        const hasValidKeys = Object.keys(item.questionAttached).some(
          key => key !== 'undefined' && key.trim() !== ''
        );
        if (hasValidKeys) {
          const filtered = {};
          Object.entries(item.questionAttached).forEach(([key, value]) => {
            if (key !== 'undefined' && key.trim() !== '') {
              filtered[key] = value;
            }
          });
          if (Object.keys(filtered).length > 0) {
            cleaned.questionAttached = filtered;
          }
        }
      }
      
      if (item.parentSequence !== undefined) cleaned.parentSequence = item.parentSequence;
      if (item.parentQuestionType_ID !== undefined) cleaned.parentQuestionType_ID = item.parentQuestionType_ID;
      if (item.parentQuestionAttached !== undefined) cleaned.parentQuestionAttached = item.parentQuestionAttached;
      
      if (item.comparibility_ID !== undefined) cleaned.comparibility_ID = toComparibilityId(item.comparibility_ID);
      if (item.comparibility_value !== undefined) cleaned.comparibility_value = item.comparibility_value;
      if (item.ifContains !== undefined) cleaned.ifContains = item.ifContains;
      
      return cleaned;
    })
    .filter(item => {
      if (!item.parentSequence) return false;
      
      const hasParentInfo = item.parentQuestionType_ID !== undefined && item.parentQuestionAttached !== undefined;
      const hasValidQuestionAttached = item.questionAttached && Object.keys(item.questionAttached).length > 0;
      
      return hasParentInfo || hasValidQuestionAttached;
    });
};

      const currentDisplayList = liveStateRef.current?.displayList ?? displayList ?? [];
      const currentAndLogic = liveStateRef.current?.andLogic ?? andLogic ?? [];
      const currentOrLogic = liveStateRef.current?.orLogic ?? orLogic ?? [];
      // Set when the first display row stands in for the hidden base row (see promotion below).
      let promotedSequence = null;

      if (isInterview === false && checkboxDisplay == 1 ) {
        // displayList is the source of truth for the conditions on screen. andLogic/orLogic are
        // patched incrementally as rows change, so they can still carry entries for rows that were
        // removed or for parent questions that no longer exist.
        const activeRowSequences = new Set(
          currentDisplayList.map(row => Number(row?.parentSequence)).filter(sequence => !Number.isNaN(sequence))
        );
        const findQuestionById = id =>
          (formTaskSections || [])
            .flatMap(section => section?.questionName_array || [])
            .find(q => String(q?.question_ID) === String(id));
        const existingQuestionIds = new Set(
          (formTaskSections || [])
            .flatMap(section => section?.questionName_array || [])
            .map(q => String(q?.question_ID))
        );
        const isLiveLogicRow = logic => {
          // parentSequence 1 is the base condition built below, it has no displayList row.
          const sequence = Number(logic?.parentSequence);
          if (sequence !== 1 && !activeRowSequences.has(sequence)) return false;
          const parentId = logic?.parentQuestionAttached ?? logic?.parentQuestion_ID;
          if (parentId != null && existingQuestionIds.size > 0 && !existingQuestionIds.has(String(parentId))) {
            return false;
          }
          return true;
        };
        const liveAndLogic = currentAndLogic.filter(isLiveLogicRow);
        const liveOrLogic = currentOrLogic.filter(isLiveLogicRow);

        // Once a question has been saved with AND/OR logic, hideNormalDisplay hides the base row and
        // every condition — the first one included — lives in displayList from parentSequence 2 up.
        // Promote that first row back into the base condition, otherwise a single remaining condition
        // goes out as a lone parentSequence 2 entry with no base fields, and multi-condition payloads
        // lose their parentSequence 1 anchor.
        if (!taskQuestion?.selectedQuestion && currentDisplayList.length > 0) {
          const firstRow = currentDisplayList[0];
          const firstEntry = [...liveAndLogic, ...liveOrLogic].find(
            logic => Number(logic?.parentSequence) === Number(firstRow?.parentSequence)
          );
          const parentId =
            firstEntry?.parentQuestionAttached ?? firstRow?.parentQuestionAttached ?? firstRow?.parentQuestion_ID;
          const parentQuestion = findQuestionById(parentId) || firstRow?.selectedQuestionAttached;
          if (parentQuestion) {
            const attached = firstEntry?.questionAttached || {};
            const attachedKey = Object.keys(attached)[0];
            const attachedValue = Array.isArray(attached[attachedKey]) ? attached[attachedKey][0] : attached[attachedKey];
            const parentType = Number(parentQuestion.questionType_ID);
            const promoted = { ...taskQuestion, selectedQuestion: parentQuestion };

            if (firstRow?.selectedQuestionSectionAttached != null) {
              promoted.selectedQuestionSectionAttached = firstRow.selectedQuestionSectionAttached;
            }
            if (firstEntry?.comparibility_ID != null) {
              promoted.selectedComparibilityID = toComparibilityId(firstEntry.comparibility_ID);
            }
            if (firstEntry?.comparibility_value != null) promoted.inputValue = firstEntry.comparibility_value;
            if (firstEntry?.ifContains != null) promoted.inputValue = firstEntry.ifContains;
            if ([1, 2, 7, 14].includes(parentType) && attachedValue != null) {
              promoted.selectedChoiceID = attachedValue;
            }
            if (parentType === 4 && attachedKey != null) {
              promoted.selectedChoiceID = attachedKey;
              promoted.selectedLikerChocieID = attachedKey;
              if (attachedValue != null) promoted.LikertScaleID = attachedValue;
            }
            if (parentType === 16 && attachedValue != null) promoted.npsScale_ID = attachedValue;

            taskQuestion = promoted;
            promotedSequence = Number(firstRow?.parentSequence);
          }
        }

        // Drop the promoted row and close the gap it leaves, so the remaining rows stay at 2..n.
        const shiftSequence = value => {
          const sequence = Number(value);
          return Number.isNaN(sequence) || sequence <= promotedSequence ? value : sequence - 1;
        };
        const dropPromoted = list =>
          promotedSequence == null
            ? list
            : list
                .filter(entry => Number(entry?.parentSequence) !== promotedSequence)
                .map(entry => ({ ...entry, parentSequence: shiftSequence(entry?.parentSequence) }));

        const andLogic = dropPromoted(liveAndLogic);
        const orLogic = dropPromoted(liveOrLogic);
        const displayList =
          promotedSequence == null
            ? currentDisplayList
            : currentDisplayList
                .slice(1)
                .map(row => ({ ...row, parentSequence: shiftSequence(row?.parentSequence) }));
        const isStateReady = andLogic.length > 0 || orLogic.length > 0;

        // One-shot hydrate for edit load: UI already shows ParentquestionAttached,
        // but selectedQuestion may still be null — fills it so existing save paths work.
        if (!taskQuestion.selectedQuestion && displayList.length === 0) {
          const pa = taskQuestion?.ParentquestionAttached;
          const parentId = pa?.parentQuestionAttached ?? pa?.parentQuestion_ID;
          if (parentId) {
            const resolved = formTaskSections
              .flatMap(section => section.questionName_array || [])
              .find(q => String(q.question_ID) === String(parentId));
            if (resolved) {
              taskQuestion.selectedQuestion = resolved;
              if (taskQuestion.selectedQuestionSectionAttached == null) {
                const sectionIdx = formTaskSections.findIndex(
                  section =>
                    Array.isArray(section.questionName_array) &&
                    section.questionName_array.some(q => String(q.question_ID) === String(parentId))
                );
                if (sectionIdx !== -1) {
                  taskQuestion.selectedQuestionSectionAttached = sectionIdx + 1;
                }
              }
            }
          }
        }

        if (
          !taskQuestion.selectedQuestion &&
          !attachedQuestionAfter &&
          checkboxDisplay == 1 &&
          !hideNormalDisplay &&
          displayList.length === 0
        ) {
          setSkipLogicError('Please select the question and section number!');
          return;
        }

        if (displayList.length > 0) {
          const allLogicEntries = [...orLogic, ...andLogic];
          const hasEmptyRow = displayList.some(item => {
            const matched = allLogicEntries.find(
              logic => Number(logic?.parentSequence) === Number(item?.parentSequence)
            );
            const rowParent =
              item?.parentQuestionAttached ??
              item?.parentQuestion_ID ??
              item?.selectedQuestionAttached?.question_ID;
            return !matched && !rowParent;
          });
          if (hasEmptyRow) {
            setSkipLogicError('Please select the question and section number!');
            return;
          }
        }
        if (
          (taskQuestion?.selectedQuestion &&
            (getQuestionDetails().questionTypeId === 1 ||
              getQuestionDetails().questionTypeId === 2 ||
              getQuestionDetails().questionTypeId === 7)) ||
          (!taskQuestion?.selectedQuestion &&
            (orLogic[0]?.parentQuestionType_ID === 1 || andLogic[0]?.parentQuestionType_ID === 1 ||
              orLogic[0]?.parentQuestionType_ID === 7 || andLogic[0]?.parentQuestionType_ID === 7 ||
              orLogic[0]?.parentQuestionType_ID === 2 || andLogic[0]?.parentQuestionType_ID === 2))
        ) {
          const attachedQuestionList = [];
          const attachedQuestion = {};
          const selectedChoices = [];

          if (displayList?.length == 0) {
            if (taskQuestion.selectedChoiceID) {
              selectedChoices.push(String(taskQuestion.selectedChoiceID));
            } else {
              if (answerChoice?.value) {
                selectedChoices.push(String(answerChoice.value));
              } else {
                setSkipLogicError('Please select a choice!');
                return;
              }
            }
          }
          if(displayList?.length > 0){
            if (taskQuestion.selectedChoiceID) {
              selectedChoices.push(String(taskQuestion.selectedChoiceID));
            } else {
              if (answerChoice?.value) {
                selectedChoices.push(String(answerChoice.value));
              }
          }}
          const { questionId } = getQuestionDetails();
          attachedQuestion[String(questionId)] = selectedChoices;
          attachedQuestionList.push(attachedQuestion);
          if (attachedQuestionList?.length < 1 && displayList.length == 0) {
            setSkipLogicError('Please fill in the dropdowns!');
            return;
          }
          if (displayList.length == 0 || !isStateReady) {
            formData.append('questionAttached', JSON.stringify(attachedQuestionList));
            const { questionId: parentQuestionId1, questionTypeId: parentQuestionTypeId1 } = getQuestionDetails();
            formData.append('parentQuestionType_ID', parentQuestionTypeId1);
            formData.append('parentQuestionAttached', parentQuestionId1);
            formData.append('ANDLogic', JSON.stringify([]));
            formData.append('ORLogic', JSON.stringify([]));
          }
          if (isStateReady || displayList.length > 0) {
            const andOr = displayList[0]?.condition?.value;
            const newLogic = {
              questionAttached: attachedQuestion,
              parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
              parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
              parentSequence:1
            };
                        if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }
            setHideNormalDisplay(true);
          }
        }
        if (
          (taskQuestion?.selectedQuestion &&
            getQuestionDetails().questionTypeId === 4)||
          (!taskQuestion?.selectedQuestion &&
            orLogic[0]?.parentQuestionType_ID === 4 ||andLogic[0]?.parentQuestionType_ID === 4

        ))
          {
          const attachedQuestionList = [];
          const attachedQuestion = {};
          const selectedChoices = [];
          const valueToPush = likertSelectedChoice?.value ?? taskQuestion?.LikertScaleID;

          if (valueToPush) {
            selectedChoices.push(String(valueToPush));
          } else if (displayList?.length === 0) {
            setSkipLogicError('Please select any Likert Scale!');
            return;
          }


          if (taskQuestion?.selectedChoiceID || displayList?.length>0) {
            // attachedQuestion[String(taskQuestion.selectedChoiceID)] = selectedChoices;
            if (Number(taskQuestion.selectedChoiceID) === Number(selectedChoices[0])) {
              attachedQuestion[String(taskQuestion.selectedLikerChocieID)] = selectedChoices;
            } else{
            attachedQuestion[String(taskQuestion.selectedChoiceID)] = selectedChoices;
            }
            attachedQuestionList.push(attachedQuestion);

          if (displayList.length == 0 || !isStateReady) {
            formData.append('questionAttached', JSON.stringify(attachedQuestionList));
            const { questionId: parentQuestionId2, questionTypeId: parentQuestionTypeId2 } = getQuestionDetails();
            formData.append('parentQuestionType_ID', parentQuestionTypeId2);
            formData.append('parentQuestionAttached', parentQuestionId2);
            formData.append('ANDLogic', JSON.stringify([]));
            formData.append('ORLogic', JSON.stringify([]));
          } 
          if(isStateReady || displayList.length > 0) {
            const andOr = displayList[0]?.condition?.value;
            const newLogic = {
              questionAttached: attachedQuestion,
              parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
              parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
              parentSequence:1
            };
                        if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }
            setHideNormalDisplay(true);

          }
          } else {
            setSkipLogicError('Please select a choice!');
            return;
          }
        }

       if (
          (taskQuestion?.selectedQuestion &&
            getQuestionDetails().questionTypeId === 16)||
          (!taskQuestion?.selectedQuestion &&
            orLogic[0]?.parentQuestionType_ID === 16 ||andLogic[0]?.parentQuestionType_ID === 16
        ))          {
          const attachedQuestionList = [];
          const attachedQuestion = {};
          const selectedChoices = [];

                    if (displayList?.length === 0) {
                  if (taskQuestion?.npsScale_ID != null && taskQuestion?.npsScale_ID !== '') {
                    selectedChoices.push(String(taskQuestion.npsScale_ID));
                  } else {
                    setSkipLogicError('Please select any NPS scale!');
                    return;
                  }
                } else if (taskQuestion?.npsScale_ID != null && taskQuestion?.npsScale_ID !== '') {
                  selectedChoices.push(String(taskQuestion.npsScale_ID));
                }

          const selectedChoice_ID = taskQuestion?.selectedQuestion?.choiceName_array[0]?.choiceName_ID;

          if (selectedChoice_ID || displayList?.length) {
            attachedQuestion[String(selectedChoice_ID)] = selectedChoices;
            attachedQuestionList.push(attachedQuestion);
            
          if (displayList.length == 0 || !isStateReady) {
            formData.append('questionAttached', JSON.stringify(attachedQuestionList));
            formData.append('parentQuestionType_ID', getQuestionDetails().questionTypeId);
            formData.append('parentQuestionAttached', getQuestionDetails().questionId);
            formData.append('ANDLogic', JSON.stringify([]));
            formData.append('ORLogic', JSON.stringify([]));
          } 
          if(isStateReady || displayList.length > 0) {
            const andOr = displayList[0]?.condition?.value;
            const newLogic = {
              questionAttached: attachedQuestion,
              parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
              parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
              parentSequence:1
            };
                        if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }
            setHideNormalDisplay(true);
          }
            
          } else {
            setSkipLogicError('Please select a choice!');
            return;
          }
        }

                  // Handle type 14 separately to avoid conflicts with other types
      if ((
          (taskQuestion?.selectedQuestion &&
            getQuestionDetails().questionTypeId === 14)||
          (!taskQuestion?.selectedQuestion &&
                orLogic[0]?.parentQuestionType_ID === 14 ||andLogic[0]?.parentQuestionType_ID === 14
        ))             &&
              ![1, 2, 3, 4, 5, 6, 7, 11, 16].includes(taskQuestion?.selectedQuestion?.questionType_ID)) {
          const attachedQuestionList = [];
          const attachedQuestion = {};
          const selectedChoices = [];

         if (taskQuestion?.selectedChoiceID) {
            selectedChoices.push(String(taskQuestion.selectedChoiceID));
          } else if (answerChoice?.value) {
            selectedChoices.push(String(answerChoice.value));
          } else if (displayList?.length === 0) {
            setSkipLogicError('Please select a choice!');
            return;
          }
          const { questionId: questionId2 } = getQuestionDetails();
          attachedQuestion[String(questionId2)] = selectedChoices;
          attachedQuestionList.push(attachedQuestion);

          const inputValue = taskQuestion?.inputValue ? taskQuestion.inputValue : inputAnswer;

          if (taskQuestion?.selectedComparibilityID || comparibilityValue?.value || displayList.length > 0) {
            if (!inputValue && displayList.length === 0) {
              setSkipLogicError('Please fill a value!');
              return;
            }
            if (displayList.length == 0 || !isStateReady) {
            formData.append('questionAttached', JSON.stringify(attachedQuestionList));
            formData.append(
              'parentQuestionType_ID',
              taskQuestion?.selectedQuestion?.questionType_ID
                ? getQuestionDetails().questionTypeId
                : attachedQuestionAfter.questionType_ID
            );
            formData.append(
              'parentQuestionAttached',
              taskQuestion?.selectedQuestion?.question_ID
                ? getQuestionDetails().questionId
                : attachedQuestionAfter.question_ID
            );
            formData.append(
              'comparibility_ID',
              toComparibilityId(
                taskQuestion?.selectedComparibilityID ? taskQuestion.selectedComparibilityID : comparibilityValue.value
              )
            );
            formData.append('comparibility_value', taskQuestion?.inputValue ? taskQuestion.inputValue : inputAnswer);
            formData.append('ANDLogic', JSON.stringify([]));
            formData.append('ORLogic', JSON.stringify([]));
          }
          if(isStateReady || displayList?.length > 0) {
            const andOr = displayList[0]?.condition?.value;
            const newLogic = {
              questionAttached: attachedQuestion,
              parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
              parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
              comparibility_value:taskQuestion?.inputValue ? taskQuestion?.inputValue : inputAnswer,
              comparibility_ID: toComparibilityId(
                taskQuestion?.selectedComparibilityID ? taskQuestion?.selectedComparibilityID : comparibilityValue?.value
              ),
              parentSequence:1

            };

                        if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }
            setHideNormalDisplay(true);
          }
          } else {
            setSkipLogicError('Please select the comparing dropdown!');
            return;
          }
        }

        const selectedTypeIDs = [3, 9, 11];
        const fallbackTypeIDs = [3, 9, 11];

        const selected = taskQuestion?.selectedQuestion;

        if (
          (selected && selectedTypeIDs.includes(selected.questionType_ID)) ||
          (!selected && (andLogic[0]?.parentQuestionType_ID === 9 ||
                andLogic[0]?.parentQuestionType_ID === 3 ||andLogic[0]?.parentQuestionType_ID === 11||
                orLogic[0]?.parentQuestionType_ID === 11 ||
                orLogic[0]?.parentQuestionType_ID === 3 ||  
                orLogic[0]?.parentQuestionType_ID === 9))
        )  {
          const inputValue = taskQuestion?.inputValue ? taskQuestion.inputValue : inputAnswer;

          if (taskQuestion?.selectedComparibilityID || comparibilityValue?.value || displayList?.length>0) {
            if (!isStateReady || displayList?.length === 0) {
            formData.append('questionAttached', '');
            formData.append('parentQuestionType_ID', getQuestionDetails().questionTypeId);
            formData.append('parentQuestionAttached', getQuestionDetails().questionId);
            formData.append(
              'comparibility_ID',
              toComparibilityId(
                taskQuestion?.selectedComparibilityID ? taskQuestion.selectedComparibilityID : comparibilityValue.value
              )
            );
            formData.append('comparibility_value', inputValue);
            formData.append('ANDLogic', JSON.stringify([]));
            formData.append('ORLogic', JSON.stringify([]));
          } else {
            const andOr = displayList[0]?.condition?.value;
            const newLogic = {
              // questionAttached: attachedQuestion,
              parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
              parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
              comparibility_value:taskQuestion?.inputValue ? taskQuestion?.inputValue : inputAnswer,
              comparibility_ID: toComparibilityId(
                taskQuestion?.selectedComparibilityID ? taskQuestion?.selectedComparibilityID : comparibilityValue?.value
              ),
              parentSequence:1
            };

                        if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }
            setHideNormalDisplay(true);

          }
            
            if (!inputValue && !hideNormalDisplay) {
              setSkipLogicError('Please fill a value!');
              return;
            }
            formData.append('comparibility_value', taskQuestion.inputValue ? taskQuestion.inputValue : inputAnswer);
          } else {
            setSkipLogicError('Please fill in the values!');
            return;
          }
        }

        // Handle text answer questions (types 5 and 6) separately
        if ((
              (taskQuestion?.selectedQuestion &&
                (getQuestionDetails().questionTypeId === 5 ||
                getQuestionDetails().questionTypeId === 6)) ||
              (!taskQuestion?.selectedQuestion &&
                (andLogic[0]?.parentQuestionType_ID === 6 ||
                andLogic[0]?.parentQuestionType_ID === 5 ||
                orLogic[0]?.parentQuestionType_ID === 5 ||  
                orLogic[0]?.parentQuestionType_ID === 6))
            ) &&
            ![1, 2, 3, 4, 7, 11, 14, 16].includes(taskQuestion?.selectedQuestion?.questionType_ID)) {
          if (taskQuestion.selectedQuestion || displayList?.length>0) {
            const inputValue = taskQuestion.inputValue ? taskQuestion.inputValue : answerChoice?.label;
            if (!inputValue && displayList.length === 0) {
              setSkipLogicError('Please fill a value!');
              return;
            }

            // Initialize logic arrays
            let updatedANDLogic = [];
            let updatedORLogic = [];

            if (displayList.length == 0 && taskQuestion.selectedQuestion) {
              formData.append('questionAttached', '');
              formData.append('parentQuestionType_ID', getQuestionDetails().questionTypeId);
              formData.append('parentQuestionAttached', getQuestionDetails().questionId);
              formData.append('ifContains', taskQuestion.inputValue ? taskQuestion.inputValue : answerChoice.label);
              formData.append('ANDLogic', JSON.stringify([]));
              formData.append('ORLogic', JSON.stringify([]));
            } else {
              const andOr = displayList[0]?.condition?.value;
              const newLogic = {
                parentQuestionType_ID: taskQuestion?.selectedQuestion?.questionType_ID,
                parentQuestionAttached: taskQuestion?.selectedQuestion?.question_ID,
                ifContains: taskQuestion.inputValue ? taskQuestion?.inputValue : answerChoice?.label,
                parentSequence:1
              };
            if (andOr === 1) {
              const orLogicResult = filterDuplicateLogic([newLogic, ...orLogic]);
              const andLogicResult = filterDuplicateLogic([...andLogic]);
              
              // Check for duplicates and show errors
              if (orLogicResult.hasDuplicates || andLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
            } else {
              const andLogicResult = filterDuplicateLogic([newLogic, ...andLogic]);
              const orLogicResult = filterDuplicateLogic([...orLogic]);
              
              // Check for duplicates and show errors
              if (andLogicResult.hasDuplicates || orLogicResult.hasDuplicates) {
                setDuplicateLogicError('Duplicate logic conditions detected. Please remove duplicate entries or change values before saving.');
                return;
              }
              
              formData.append('ANDLogic', JSON.stringify(cleanLogicForApi(andLogicResult.filteredArray)));
              formData.append('ORLogic', JSON.stringify(cleanLogicForApi(orLogicResult.filteredArray)));
            }

              setHideNormalDisplay(true);
            }

            // Append final logic arrays - only done once for both cases
          
          } else {
            setSkipLogicError('Please fill in the values!');
            return;
          }
        }
      }
      if (isInterview === false) {
        // formData.append('isLogicApplied', isSkipEnabled ? 1 : 0);
        // formData.append('parentQuestionType_ID', '');
        // formData.append('parentQuestionAttached', '');

        if (
          isSkipEnabled &&
          (taskQuestion?.questionType_ID === 1 ||
            taskQuestion?.questionType_ID === 2 ||
            taskQuestion?.questionType_ID === 7)
        ) {
          if (skipToTarget !=1) {
            const skipSectionData = [{ [skipToTarget]: [skipIfChoice] }];
            formData.append('ToSection', JSON.stringify(skipSectionData));
          } else {
            const endSurveyData = [{ [taskQuestion?.question_ID]: [skipIfChoice] }];
            formData.append('EndSurvey', JSON.stringify(endSurveyData));
          }
        }
        if (isSkipEnabled && taskQuestion?.questionType_ID === 4) {
          const likertScaleId = likertScaleOptions?.questionLikertScaleName
            ? likertScaleOptions?.questionLikertScale_ID
            : likertScaleOptions;

          if (skipToTarget !='1' || skipToTarget != 1) {
            const skipSectionData = [
              { [skipIfChoice?.value ?? skipIfChoice]: [likertScaleId], section_ID: skipToTarget }
            ];
            formData.append('ToSection', JSON.stringify(skipSectionData));
          } else {
            const endSurveyData = [{ [skipIfChoice?.value ?? skipIfChoice]: [likertScaleId] }];

            formData.append('EndSurvey', JSON.stringify(endSurveyData));
          }
        }
        if (isSkipEnabled && taskQuestion?.questionType_ID === 16) {
          const value = taskQuestion?.choiceName_array?.[0]?.choiceName_ID;
          if (likertScaleChoicesOptions >= 0 && likertScaleChoicesOptions <= 10) {
            const matchedLikert =
              taskQuestion?.likertScaleName_array?.[likertScaleChoicesOptions].questionLikertScale_ID;

            if (skipToTarget !='1' || skipToTarget != 1) {
              const skipSectionData = [{ [value]: [matchedLikert], section_ID: skipToTarget }];
              formData.append('ToSection', JSON.stringify(skipSectionData));
            } else {
              const endSurveyData = [{ [value]: [matchedLikert] }];

              formData.append('EndSurvey', JSON.stringify(endSurveyData));
            }
          } else {
            if (skipToTarget !='1' || skipToTarget != 1) {
              const skipSectionData = [{ [value]: [likertScaleChoicesOptions], section_ID: skipToTarget }];
              formData.append('ToSection', JSON.stringify(skipSectionData));
            } else {
              const endSurveyData = [{ [value]: [likertScaleChoicesOptions] }];
              formData.append('EndSurvey', JSON.stringify(endSurveyData));
            }
          }
        }

        if ((isSkipEnabled && taskQuestion?.questionType_ID === 3) || taskQuestion?.questionType_ID === 11) {
          if (skipToTarget !='1' || skipToTarget != 1) {
            formData.append('ToSection', skipToTarget);
            formData.append('TScomparibility_value', skipLogicInputValue);
            formData.append('TScomparibility_ID', likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions);
          } else {
            formData.append('EScomparibility_ID', likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions);
            formData.append('EScomparibility_value', skipLogicInputValue);
            formData.append('EndSurvey', '1'); // Fixed value
          }
        }
        if (isSkipEnabled && taskQuestion?.questionType_ID === 14) {
          if (skipToTarget !='1' || skipToTarget != 1) {
            const skipSectionData = [{ [taskQuestion?.question_ID]: [skipIfChoice], section_ID: skipToTarget }];
            formData.append('ToSection', JSON.stringify(skipSectionData));
            formData.append('TScomparibility_value', skipLogicInputValue);

            formData.append('TScomparibility_ID', likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions);
          } else {
            const endSurveyData = [{ [taskQuestion?.question_ID]: [skipIfChoice] }];

            formData.append('EndSurvey', JSON.stringify(endSurveyData));
            formData.append('EScomparibility_value', skipLogicInputValue);

            formData.append('EScomparibility_ID', likertScaleChoicesOptions?.id ?? likertScaleChoicesOptions);
          }
        }
        if (isSkipEnabled && (taskQuestion?.questionType_ID === 5 || taskQuestion?.questionType_ID === 6)) {
          if (skipToTarget !='1' || skipToTarget != 1) {
            formData.append('ToSection', skipToTarget);
            formData.append('TSifContains', skipLogicInputValue); // Value from state
          } else {
            formData.append('EndSurvey', '1'); // Fixed value
            formData.append('ESifContains', skipLogicInputValue); // Value from state
          }
        }
      }

      const questionTypeId = Number(taskQuestion.questionType_ID);
      const choiceRows = taskQuestion?.choiceName_array || [];
      const choicePayloadBase = {
        questionTypeId,
        rows: choiceRows,
        isResponded: useEditTypePayload ? 0 : Number(taskQuestion.isResponded),
        isNewChoice,
        shuffleEnabled
      };
      const initialChoiceRows = (snapshotQuestion.current || question)?.choiceName_array;
      const appendEditJsonChoices = () =>
        appendJsonChoiceFields(formData, {
          choices: buildChoiceApiObjects(choicePayloadBase),
          deletedIds: getDeletedChoiceIds(initialChoiceRows, choiceRows)
        });
      const appendCreateChoices = () => {
        isAutomatedAnswer_array = (taskQuestion?.choiceName_array || []).map(() => 0);
        formData.append('choices', JSON.stringify(buildCreateChoicesPayload()));
      };
      const appendCreateLikert = () => {
        const likertOptions = resolveLikertScaleRows(taskQuestion);
        const scaleType = taskQuestion?.scaleType || taskQuestion?.LikertScaleType;
        formData.append('choices', JSON.stringify(buildCreateChoicesPayload()));
        formData.append(
          'questionLikertScaleName',
          JSON.stringify(
            buildLikertScaleApiObjects(likertOptions, {
              includeIds: false,
              shuffleEnabled,
              scaleType
            })
          )
        );
        formData.append('LikertScaleType', scaleType || '');
      };
      const appendCreateRanking = () => {
        isAutomatedAnswer_array = (taskQuestion?.choiceName_array || []).map(() => 0);
        formData.append('choices', JSON.stringify(buildCreateChoicesPayload()));
        formData.append(
          'questionLikertScaleName',
          (resolveRankingScaleRows(taskQuestion, tempLikerTOptions) || []).map(lt => lt.questionLikertScaleName)
        );
      };

      let screenerTypePayload = {};
      let tagsPayload = {};

      switch (questionTypeId) {
        case 1:
        case 2:
          if (useCreateTypePayload) {
            appendCreateChoices();
          }
          break;

        case 3: {
          const sliderLikertOptions =
            taskQuestion?.likertScaleOptions || taskQuestion?.likertScaleName_array || [];
          const sliderScaleType = taskQuestion?.scaleType || taskQuestion?.LikertScaleType;
          formData.append('choices', JSON.stringify(buildCreateChoicesPayload()));
          formData.append(
            'questionLikertScaleName',
            sliderLikertOptions
              ?.map(lt => lt.questionLikertScaleName)
              .toString()
              .replace(/[\[\]"]/g, '') || ''
          );
          formData.append('LikertScaleType', sliderScaleType || '');
          break;
        }
        case 4:
          // Create only — edit uses appendLikertEditFields below (JSON choices / scales / deleted_choice_ids)
          if (useCreateTypePayload) {
            appendCreateLikert();
          }
          break;
        case 5:
        case 6:
          // payload = { ...payload, "characterLimit": taskQuestion?.characterLimit };

          formData.append('characterLimit', taskQuestion?.characterLimit);
          break;
        case 7:
          if (useCreateTypePayload) {
            appendCreateChoices();
          }
          break;
        case 8:
          // payload = { ...payload, "maxFileSizeInMB": taskQuestion?.maxFileSizeInMB, "numberOfFiles": taskQuestion?.numberOfFiles };

          formData.append('maxFileSizeInMB', taskQuestion?.maxFileSizeInMB);
          formData.append('numberOfFiles', taskQuestion?.numberOfFiles);
          break;
        case 9:
          // payload = { ...payload, "screenerQuestion_ID": taskQuestion?.screenerQuestion_ID };
          const matchingScreenerQuestion = taskQuestion?.screenerQuestion?.find(
            question => question.groupFilterLable === taskQuestion?.savedOption
          );

          const screenerQuestion_ID = matchingScreenerQuestion ? matchingScreenerQuestion.question_ID : null;

          formData.append('screenerQuestion_ID', screenerQuestion_ID);

          screenerTypePayload = {
            user_ID: userId,
            screenerQuestion_ID: screenerQuestion_ID,
            question_ID: questionId
          };

          break;
        case 10:
          // payload = { ...payload, "buttonName": taskQuestion?.buttonName }

          formData.append('buttonName', taskQuestion?.buttonName);
          formData.append('project_ID', projectId);

          tagsPayload = {
            user_ID: userId,
            question_ID: questionId,
            project_ID: projectId,
            tags: taskQuestion?.tags ? taskQuestion?.tags : [],
            buttonName: taskQuestion?.buttonName
          };
          break;
        case 11:
          // payload = { ...payload, "valueQuestion_ID": taskQuestion?.valueQuestion_ID };

          formData.append(
            'valueQuestion_ID',
            taskQuestion?.valueQuestion_ID == undefined ? 1 : taskQuestion?.valueQuestion_ID
          );
          formData.append('SavedOption', taskQuestion?.savedOption);
          break;
        case 12:
          // payload = { ...payload, "maxFileSizeInMB": taskQuestion?.maxFileSizeInMB, "numberOfFiles": taskQuestion?.numberOfFiles };

          formData.append('maxFileSizeInMB', 5);
          formData.append('numberOfFiles', taskQuestion?.numberOfFiles);
          break;
        case 13:
          // payload = { ...payload, "maxFileSizeInMB": taskQuestion?.maxFileSizeInMB, "numberOfFiles": taskQuestion?.numberOfFiles };

          formData.append('maxFileSizeInMB', 10);
          formData.append('numberOfFiles', taskQuestion?.numberOfFiles);
          break;

        case 14:
          // Create only — edit uses SCALE_EDIT_JSON_TYPES block below (JSON choices / scales / deleted_choice_ids)
          if (useCreateTypePayload) {
            appendCreateRanking();
          }
          break;
        case 15:
          // payload = { ...payload, "maxFileSizeInMB": taskQuestion?.maxFileSizeInMB, "numberOfFiles": taskQuestion?.numberOfFiles };

          formData.append('maxFileSizeInMB', 10);
          formData.append('numberOfFiles', taskQuestion?.numberOfFiles);
          break;
        case 16:
          const NpsOptions = getNpsScaleOptions() || [];
          formData.append('choices', JSON.stringify([]));
          formData.append(
            'questionLikertScaleName',
            JSON.stringify(
              buildLikertScaleApiObjects(NpsOptions, {
                includeIds: false,
                shuffleEnabled: 0,
                scaleType: ''
              })
            )
          );
          break;

        default:
          break;
      }

      if (CHOICE_JSON_TYPES.includes(questionTypeId) && useEditTypePayload) {
        appendEditJsonChoices();
      }

      // Likert (4) / Ranking (14): JSON payload only when editing an existing question without type change
      if (useEditTypePayload && SCALE_EDIT_JSON_TYPES.includes(questionTypeId)) {
        const originalQuestion = snapshotQuestion.current || question;
        const currentScaleRows = resolveScaleRowsForEdit(taskQuestion, questionTypeId, tempLikerTOptions);
        const originalScaleRows = resolveScaleRowsForEdit(originalQuestion, questionTypeId, tempLikerTOptions);
        const omitScaleIds = shouldOmitCustomLikertScaleIds({
          original: originalQuestion,
          current: taskQuestion,
          originalRows: originalScaleRows,
          currentRows: currentScaleRows
        });
        const editScaleType = taskQuestion?.scaleType || taskQuestion?.LikertScaleType || '';
        appendLikertEditFields(formData, {
          choices: buildChoiceApiObjects(choicePayloadBase),
          deletedChoiceIds: getDeletedChoiceIds(initialChoiceRows, choiceRows),
          likertScale: buildLikertScaleApiObjects(currentScaleRows, {
            includeIds: !omitScaleIds,
            shuffleEnabled,
            scaleType: editScaleType
          }),
          likertScaleType: LIKERT_EDIT_JSON_TYPES.includes(questionTypeId) ? editScaleType : undefined
        });
      }

      if (!usesEditChoiceJson(questionTypeId, useEditTypePayload)) {
        formData.append('isAutomatedAnswer', isAutomatedAnswer_array);
      }
      formData.append('mediaDisclaimer', null);
      if (formData.get('NewQuestionType_ID') == formData.get('OldQuestionType_ID')) {
        formData.delete('NewQuestionType_ID');
        formData.delete('OldQuestionType_ID');
      }

      

      if (questionId === null) {
        if (taskQuestion?.PrevQuestion_ID) {
          formData.append('PrevQuestion_ID', taskQuestion?.PrevQuestion_ID);
        }
          
        dispatch({
          type: 'REQUEST_ADD_QUESITON',
          formData: formData
        });

        // Refresh is handled by DesignFormTab's addQues effect — avoid a second
        // getProjectTaskQuestions call via handleProjectTaskTab().

        // dispatch({
        // 	type: 'REQUEST_ADD_QUESITON',
        // 	formData: payload
        // });
      } else {
        // payload = { ...payload, "questions_ID": questionId };

        formData.append('questions_ID', questionId);
        setEditQuestionId(questionId);

        
        dispatch({
          type: 'REQUEST_QUESTION_EDIT',
          formData: formData
        });

        if (taskQuestion.questionType_ID == 9) {
          dispatch({
            type: 'REQUEST_ADD_SCREENER_VALIDATION_QUESTION',
            formData: screenerTypePayload
          });
        }
        if (taskQuestion.questionType_ID == 10) {
          dispatch({
            type: 'REQUEST_ADD_QUESTION_TAGS',
            formData: tagsPayload
          });
        }
      }

      let tempTaskQuestion = taskQuestion;

      tempTaskQuestion = { ...tempTaskQuestion, isEdit: false };

      setAddQuestionDisable(false);
      setTaskQuestion(tempTaskQuestion);
      setFormChanged(true);
      setIsQuestionSavedForSkipLogic(true); // Question saved, skip logic can be configured

      if (task.taskType === 'Form') {
        setFormTaskSections(prevSections => {
          const nextSections = [...prevSections];
          const currentSection = nextSections?.[sectionIndex];
          if (!currentSection) return prevSections;
          const nextQuestionNameArray = [...(currentSection.questionName_array || [])];
          nextQuestionNameArray[questionIndex] = tempTaskQuestion;
          nextSections[sectionIndex] = { ...currentSection, questionName_array: nextQuestionNameArray };
          return nextSections;
        });
      } else {
        setInterviewTaskSections(prevSections => {
          const nextSections = [...prevSections];
          const currentSection = nextSections?.[sectionIndex];
          if (!currentSection) return prevSections;
          const nextQuestionNameArray = [...(currentSection.questionName_array || [])];
          nextQuestionNameArray[questionIndex] = tempTaskQuestion;
          nextSections[sectionIndex] = { ...currentSection, questionName_array: nextQuestionNameArray };
          return nextSections;
        });
      }
      handleSetEditing();
    }
  };

  const editquest = () => {
    setEditQuestionId(taskQuestion?.question_ID);
    setQuestionIndex(questionIndex);
    setNewQuestionIndex(null);
    setDeleteQuestionIndex(null);
    setEditQuestionIndex(sectionIndex);
    setIsEdit(true);
  };
  const editQuestion = () => {
    let tempTaskQuestion = taskQuestion;
    tempTaskQuestion = { ...tempTaskQuestion, isEdit: true };

    setTaskQuestion(tempTaskQuestion);
    setEditQuestionId(taskQuestion?.question_ID);
    setQuestionIndex(questionIndex);
    setNewQuestionIndex(null);
    setDeleteQuestionIndex(null);
    setEditQuestionIndex(sectionIndex);
    setIsEdit(true);
  };

  const duplicateQuestion = (sIndex, qIndex, setCheckBoxRequired) => {
    handleDuplicateQuestion(sIndex, qIndex, setCheckBoxRequired);
  };

  const deleteQuestion = (sIndex, qIndex) => {
    handleDeleteQuestion(sIndex, qIndex);
  };

  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState(
    taskQuestion.presignedUrlQuestionImage ? taskQuestion.presignedUrlQuestionImage : ''
  );
  const [mediaType, setMediaType] = useState(taskQuestion.fileType ? taskQuestion.fileType : '');

  const [dropDownQuestionOptions, setDropDownQuestionOptions] = useState([]);
  const [dropdownSectionIndex, setDropdownSectionIndex] = useState({});
  const [fileError, setFileError] = useState(null);

  // create a preview as a side effect, whenever selected file is changed
  const onSelectFile = e => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }
    const file = e.target.files[0];
    //code for file upload size
    const maxSizeMB = 15.1 * 1024 * 1024;

    if (file.size > maxSizeMB) {
      setFileError('File size should not exceed 15 MB.');
      setSelectedFile(undefined);
      setTimeout(() => setFileError(null), 5000);
      handleRemoveFile(); // Clear file, reset state
      return;
    } else {
      setFileError(null); // Clear any previous error
    }

    const fileType = file.type;

    let currentMediaType = '';
    if (fileType.startsWith('image/')) {
      currentMediaType = 0;
    } else if (fileType.startsWith('video/')) {
      currentMediaType = 1;
    } else if (fileType === 'application/pdf'|| fileName.endsWith('.pdf') || fileName.startsWith('pdf/')) {
      currentMediaType = 2;
    } else {
      setFileError('Unsupported File type');
      setTimeout(() => setFileError(null), 5000);
      setSelectedFile(undefined);
      handleRemoveFile(); // Clear file, reset state
      return;
    }

    setMediaType(currentMediaType); // Updates state

    const objectUrl = URL.createObjectURL(file);
    console.log(objectUrl);
    setPreview(objectUrl);
    // I've kept this example simple by using the first image instead of multiple
    // handleQuestionFields('fileType', currentMediaType);

    setSelectedFile(e.target.files[0]);
    handleQuestionFields('questionImage', e.target.files[0]);
    // handleQuestionFields('fileType', mediaType);
  };

  const setDropdownQuestions = sectionIndex => {
    if (!formTaskSections[sectionIndex]?.questionName_array) {
      setDropDownQuestionOptions([]);
      return;
    }
   
    const questionList = formTaskSections[sectionIndex].questionName_array;
    const excludedTypes = [8, 10, 12, 13, 15];
    const options = [];
    let indexx = 0;
    for (let i = 0; i < questionList.length; i++) {
      const ques = questionList[i];
      if (ques.question_ID == taskQuestion.question_ID) {
        break;
      }
      if (!excludedTypes.includes(ques.questionType_ID) && ques.question_ID !== taskQuestion.question_ID) {
        options.push({
          label: ques.questionName,
          value: i,
          questionId: ques.question_ID
        });
      }
      indexx = indexx + 1;
    }

    setDropDownQuestionOptions(options);
  };

    const resolveSkipSection = (attachedSkipSection, taskQuestion) => {
    if (!attachedSkipSection && taskQuestion?.isSkipLogicApplied == 1) {
      return { 
        questionPaperSection_ID: 1, 
        sectionName: "End of Survey",
        skipSectionAttachedValue: 1
      };
    }
    return attachedSkipSection;
  };
  const setSkipDropdownQuestions = sectionIndex => {
    // const questionList = formTaskSections[sectionIndex].questionName_array;

    // const excludedTypes = [8, 10, 12, 13, 15];
    const options = [];

    // for (let i = questionIndex + 1; i < questionList.length; i++) {
    //   const ques = questionList[i];

    //   if (!excludedTypes.includes(ques.questionType_ID)) {
    //     options.push({
    //       label: ques.questionName,
    //       value: i // use current options length as index
    //     });
    //   }
    // }

    formTaskSections.map((section, index) => {
      if (index > sectionIndex) {
        options.push({
          label: section?.sectionName,
          value: section?.questionPaperSection_ID // use current options length as index
        });
      }
    });

    options.push({ label: 'End Of Survey', value: '1' });

    // if (formTaskSections[sectionIndex + 1]) {
    //   options.push({
    //     label: 'Next Section',
    //     value: formTaskSections[sectionIndex + 1].questionPaperSection_ID // use current options length as index
    //   });
    // }

    setSkipToOptions(options);
  };

  const handleRemoveFile = () => {
    setPreview(null);
    setMediaType(null);
    setSelectedFile(undefined);
    setTaskQuestion(prev => ({
      ...prev,
      questionImage: null,
      presignedUrlQuestionImage: null,
      fileType: null,
      isEdit: true
    }));
    // Also clear the file input (optional but recommended)
    document.getElementById('file-input').value = null;
  };

  const recalculateSequences = (list) => {
    return list.map((item, index) => ({
      ...item,
      parentSequence: index + 2 // Start from 2 since first item is fixed
    }));
  };

  // Helper function to add new logic item with proper sequence
  const addNewLogicItem = () => {
    // Must stay unique for the lifetime of the list: length + 1 repeated an id after a delete,
    // so React reused one row's component state for a different row.
    const newId = `dl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const newItem = { 
      id: newId, 
      condition: { label: 'AND', value: 0 },
      parentSequence: displayList.length + 2 // Next sequence number
    };
    setDisplayList(prev => [...prev, newItem]);
    setDuplicateLogicError('');
  };
  // Add this function inside DesignQuestion
  const handleDeleteLogicCondition = (item, index) => {
    setDuplicateLogicError('');

    // parentSequence is the row identity ConditionalDisplayLogic writes with; questionIndex is
    // only a positional snapshot, so it goes stale as rows shift and leaves orphan entries behind.
    const deletedSequence = Number(
      (Array.isArray(displayList) ? displayList[index]?.parentSequence : undefined) ??
        item?.parentSequence ??
        index + 2
    );

    setDisplayList(prevArray => {
      if (!Array.isArray(prevArray) || index < 0 || index >= prevArray.length) return prevArray;
      const newDisplayList = prevArray.filter((_, i) => i !== index);
      return recalculateSequences(newDisplayList);
    });

    const pruneAndResequence = prev => {
      if (!Array.isArray(prev)) return [];
      const pruned = prev.filter(l =>
        l?.parentSequence != null
          ? Number(l.parentSequence) !== deletedSequence
          : Number(l?.questionIndex) !== Number(index)
      );
      return pruned.map(l => ({
        ...l,
        questionIndex: Number(l?.questionIndex) > Number(index) ? Number(l?.questionIndex) - 1 : l?.questionIndex,
        parentSequence:
          Number(l?.parentSequence) > deletedSequence ? Number(l.parentSequence) - 1 : l?.parentSequence
      }));
    };

    setORLogic(pruneAndResequence);
    setAndLogic(pruneAndResequence);
  };
  // const textAreaRef = useRef(null);
  // updatedCount = checkedCount;

  // Function to filter duplicate logic items based on parentQuestion_ID
     const filterDuplicateLogic = (logicArray) => {
    if (!Array.isArray(logicArray) || logicArray.length === 0) return { hasDuplicates: false, duplicates: [], filteredArray: [] };    
    const seen = new Map();
    const duplicates = [];
    const filtered = [];
    
    logicArray.forEach(logic => {
      let key;
      
      switch (Number(logic.parentQuestionType_ID)) {
        case 1: // Single Choice
        case 2: // Multiple Choice
        case 7: // Single Seven
          // For choice-based questions, check parentQuestionType_ID, parentQuestionAttached, and choice ID
          let choiceId;
          if (logic.questionAttached && logic.questionAttached[logic.parentQuestionAttached]) {
            choiceId = logic.questionAttached[logic.parentQuestionAttached][0];
          } else if (logic.choice_ID) {
            choiceId = logic.choice_ID;
          } else {
            choiceId = 'no-choice';
          }
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${choiceId}`;
          break;
          
        case 4: // Likert
          const likertKey = Object.keys(logic.questionAttached || {})[0];
          const likertValue = logic.questionAttached?.[likertKey]?.[0];
          const likertComparison = logic.comparibility_ID && logic.comparibility_value ? 
            `${logic.comparibility_ID}-${logic.comparibility_value}` : 'no-comparison';
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${likertKey || 'no-key'}-${likertValue || 'no-value'}-${likertComparison}`;
          break;
          
        case 5: // Text Answer (Short)
        case 6: // Text Answer (Long)
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${logic.ifContains || 'no-text'}`;
          break;
          
        case 3: // Slider
        case 11: // Value Question
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${logic.comparibility_ID || 'no-op'}-${logic.comparibility_value || 'no-value'}`;
          break;
          
        case 14: // Ranking
          // For ranking questions, check parentQuestionType_ID, parentQuestionAttached, comparibility_ID, comparibility_value, and choice
          const rankKey = Object.keys(logic.questionAttached || {})[0];
          const rankValue = logic.questionAttached?.[rankKey]?.[0];
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${logic.comparibility_ID || 'no-op'}-${logic.comparibility_value || 'no-value'}-${rankValue || 'no-rank'}`;
          break;
          
        case 16: // NPS
          // For NPS questions, check parentQuestionType_ID, parentQuestionAttached, and scale value
          const npsKey = Object.keys(logic.questionAttached || {})[0];
          const npsValue = logic.questionAttached?.[npsKey];
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}-${npsValue || 'no-nps'}`;
          break;
          
        default:
          // Default case: check parentQuestionType_ID and parentQuestionAttached
          key = `${logic.parentQuestionType_ID}-${logic.parentQuestionAttached}`;
      }
      
      // Ensure key is valid
      if (!key || key.includes('undefined') || key.includes('null')|| key.includes('-no-nps')|| key.includes('-no-op')|| key.includes('-no-value')|| key.includes('-no-rank')|| key.includes('-no-text')|| key.includes('-no-choice')) {
        console.warn('Generated invalid key for logic item:', logic, 'Key:', key);
        return; // Skip this item
      }
      
      if (seen.has(key)) {
        duplicates.push({
          original: seen.get(key),
          duplicate: logic,
          key: key
        });
        // Don't add duplicate to filtered array
      } else {
        seen.set(key, logic);
        filtered.push(logic);
      }
    });
    
    const hasDuplicates = duplicates.length > 0;    
    if (hasDuplicates) {
      setSkipLogicError('Duplicate Display logic found');
    } else {
      // setSkipLogicError(''); // Clear any previous error
    }
    
    return { 
      hasDuplicates, 
      duplicates, 
      filteredArray: hasDuplicates ? filtered : logicArray 
    };
  }; 

  const singleDisplayQuestion = useMemo(() => {
    if (taskQuestion?.selectedQuestion) return taskQuestion.selectedQuestion;
    if (attachedQuestionAfter && typeof attachedQuestionAfter === 'object') return attachedQuestionAfter;
    const pa = taskQuestion?.ParentquestionAttached;
    const parentId = pa?.parentQuestionAttached ?? pa?.parentQuestion_ID;
    if (!parentId) return null;
    return (
      formTaskSections
        .flatMap(section => section.questionName_array || [])
        .find(q => String(q.question_ID) === String(parentId)) || null
    );
  }, [
    taskQuestion?.selectedQuestion,
    attachedQuestionAfter,
    taskQuestion?.ParentquestionAttached,
    formTaskSections
  ]);

  const singleDisplayChoiceValue = useMemo(() => {
    if (!singleDisplayQuestion) return [];
    const choiceId = taskQuestion?.selectedChoiceID ?? answerChoice?.value;
    if (choiceId != null) {
      const choice = singleDisplayQuestion.choiceName_array?.find(
        c =>
          String(c.choiceName_ID || c.id || c.QuestionChoice_ID) === String(choiceId)
      );
      if (choice) {
        return [
          {
            label: choice.choiceName,
            value: choice.choiceName_ID || choice.id || choice.QuestionChoice_ID
          }
        ];
      }
    }
    if (attachedChoiceAfter) {
      const choice = singleDisplayQuestion.choiceName_array?.find(
        c => c.choiceName === attachedChoiceAfter
      );
      if (choice) {
        return [
          {
            label: choice.choiceName,
            value: choice.choiceName_ID || choice.id || choice.QuestionChoice_ID
          }
        ];
      }
    }
    return [];
  }, [singleDisplayQuestion, taskQuestion?.selectedChoiceID, answerChoice, attachedChoiceAfter]);

  return (
    question != null && (
      <CustomCard className="mb-4">
        <QuestionWrapper className="question-wrapper">
          <Heading type="6" fontWeight="500" color="var(--quadColor)" className="q-no">
            {+sectionIndex + +1 + '.' + (+questionIndex + +1)}
          </Heading>
          {/* {duplicateLogicError && (
            <div className="col-12 mb-3">
              <div className="alert alert-danger" role="alert" style={{ fontSize: '12px', padding: '8px 12px' }}>
                {duplicateLogicError}
              </div>
            </div>
          )} */}
          <div className="row">
            <div className="col-md-8 col-xs-6 d-flex flex-column">
              {!taskQuestion?.isEdit ? (
                <>
                  <Heading
                    type="6"
                    color="var(--greyBlue)"
                    fontSize="16px"
                    fontWeight="700"
                    className="mb-0 pb-0 mt-2"
                    style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {taskQuestion.questionName}
                  </Heading>

                  {taskQuestion.questionDescription !== 'null' && taskQuestion.questionDescription?.length > 0 ? (
                    <Heading
                      type="6"
                      color="var(--greyBlue)"
                      fontSize="16px"
                      fontWeight="700"
                      className="mb-0 pb-0 mt-2"
                      style={{ display: 'flex' }}
                    >
                      <Heading
                        type="4"
                        color="var(--greyBlue)"
                        fontSize="13px"
                        fontWeight="500"
                        className="mb-0 pb-0 mt-0"
                        style={{ display: 'inline-block' }}
                      >
                        Description:
                      </Heading>
                      <Heading
                        type="5"
                        color="var(--greyBlue)"
                        fontSize="13px"
                        fontWeight="700"
                        className="mb-0 pb-0 mt-0 pl-2"
                        style={{ display: 'inline-block', whiteSpace: 'pre-line', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {'  '} {taskQuestion.questionDescription}
                      </Heading>
                    </Heading>
                  ) : (
                    ''
                  )}
                  {attachedQuestionAfter && displayList?.length == 0 ? (
                    <div>
                      {typeof edit !== 'undefined' && edit && (
                        <div className={typeof className !== 'undefined' ? className : ''}>
                          <ChoiceButton onClick={e => typeof addChoice === 'function' && addChoice()}>
                              <Icon className="material-icons-round">add</Icon> Add choice
                            </ChoiceButton>
                          </div>
                        )}
                      <Heading className="mt-3" type="6" fontWeight="700" color="#728F01">
                        Display This Question
                      </Heading>
                      {attachedQuestionAfter.questionType_ID == 1 ||
                      attachedQuestionAfter.questionType_ID == 2 ||
                      attachedQuestionAfter.questionType_ID == 7 ? (
                        <div className="d-flex ">
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                            type="5"
                          >
                            If
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--paraColor)'}
                          >
                            {attachedQuestionAfter.questionName}
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            type="5"
                            fontWeight={'100'}
                          >
                            Is
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {attachedChoiceAfter}{' '}
                          </Text>
                          <Text
                            type="5"
                            fontSize={'0.8rem'}
                            className="mb-0 pb-0 mt-0 pr-3"
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                          >
                            Is Selected
                          </Text>
                        </div>
                      ) : (
                        ''
                      )}

                      {attachedQuestionAfter.questionType_ID == 4 || attachedQuestionAfter.questionType_ID == 16 ? (
                        <div className="d-flex ">
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                            type="5"
                          >
                            If
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--paraColor)'}
                          >
                            {attachedQuestionAfter.questionName}
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            type="5"
                            fontWeight={'100'}
                          >
                            Is
                          </Text>
                          {/* NPS stub choice is often named "N/A" — only show row label for Likert */}
                          {attachedQuestionAfter.questionType_ID == 4 && (
                            <Text
                              className="mb-0 pb-0 mt-0 pr-3"
                              fontSize={'0.8rem'}
                              type="5"
                              color={'var(--paraColor)'}
                              fontWeight={'600'}
                            >
                              {attachedChoiceAfter}{' '}
                            </Text>
                          )}
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {attachedQuestionAfter.questionType_ID == 16
                              ? (() => {
                                  const id = taskQuestion?.npsScale_ID ?? likertSelectedChoice?.value;
                                  const npsSel = resolveNpsScaleSelection(
                                    attachedQuestionAfter?.likertScaleName_array,
                                    id
                                  );
                                  return npsSel?.label ?? likertSelectedChoice?.label ?? '';
                                })()
                              : likertSelectedChoice?.label}
                          </Text>

                          <Text
                            type="5"
                            fontSize={'0.8rem'}
                            className="mb-0 pb-0 mt-0 pr-3"
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                          >
                            Is Selected
                          </Text>
                        </div>
                      ) : (
                        ''
                      )}

                      {attachedQuestionAfter.questionType_ID == 5 || attachedQuestionAfter.questionType_ID == 6 ? (
                        <div className="d-flex ">
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                            type="5"
                          >
                            If
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--paraColor)'}
                          >
                            {attachedQuestionAfter.questionName}
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            type="5"
                            fontWeight={'100'}
                          >
                            Contains "word"
                          </Text>

                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {answerChoice.label}
                          </Text>
                        </div>
                      ) : (
                        ''
                      )}

                      {attachedQuestionAfter.questionType_ID == 3 || attachedQuestionAfter.questionType_ID == 11 ? (
                        <div className="d-flex ">
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                            type="5"
                          >
                            If
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--paraColor)'}
                          >
                            {attachedQuestionAfter.questionName}
                          </Text>

                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {comparibilityValue.label}
                          </Text>

                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {inputAnswer}
                          </Text>
                        </div>
                      ) : (
                        ''
                      )}

                      {attachedQuestionAfter.questionType_ID == 14 ? (
                        <div className="d-flex ">
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            color={'var(--quadColor)'}
                            fontWeight={'100'}
                            type="5"
                          >
                            If
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--paraColor)'}
                          >
                            {attachedQuestionAfter.questionName}
                          </Text>
                          
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--quadColor)'}
                          >
                            Is
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {answerChoice.label}
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--quadColor)'}
                          >
                            Ranked
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {comparibilityValue.label}
                          </Text>

                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            fontSize={'0.8rem'}
                            type="5"
                            color={'var(--paraColor)'}
                            fontWeight={'600'}
                          >
                            {inputAnswer}
                          </Text>
                          <Text
                            className="mb-0 pb-0 mt-0 pr-3"
                            type="4"
                            fontSize={'0.8rem'}
                            fontWeight={'600'}
                            color={'var(--quadColor)'}
                          >
                            Is Selected
                          </Text>
                        </div>
                      ) : (
                        ''
                      )}
                    </div>
                  ) : (
                    ''
                  )}
                  {/* Display OR Logic Conditions */}
                  {/* Display AND Logic Conditions */}
                   {(taskQuestion?.ParentquestionAttached?.ORLogic?.length > 0  ||
                    taskQuestion?.ParentquestionAttached?.ANDLogic?.length > 0) && (
                      <Heading className="mt-3" type="6" fontWeight="700" color="#728F01">
                        Display This Question
                      </Heading>
                  )}
                  {(taskQuestion?.ParentquestionAttached?.ORLogic?.length > 0  ||
                    taskQuestion?.ParentquestionAttached?.ANDLogic?.length > 0) && (
                      <LogicDisplaySection
                        logicData={taskQuestion.ParentquestionAttached}
                        // logicType="AND"
                        formTaskSections={formTaskSections}
                        hide={taskQuestion?.ParentquestionAttached}
                      />
                    )}

                  {!formChanged &&
                  [1, 2, 3, 4, 5, 6, 7, 11, 14, 16].includes(taskQuestion.questionType_ID) &&
                  isInterview === false ? (
                    <SkipLogicControls
                      skipToOptions={skipToOptions}
                      skipToTarget={skipToTarget}
                      setSkipToTarget={setSkipToTarget}
                      options={options}
                      skipIfChoice={skipIfChoice}
                      setSkipIfChoice={setSkipIfChoice}
                      conditionOptions={conditionOptions}
                      skipCondition={skipCondition}
                      setSkipCondition={setSkipCondition}
                      taskQuestion={taskQuestion}
                      edit={taskQuestion.isEdit}
                      questionType_ID={taskQuestion.questionType_ID} //likertScaleName_array
                      OptionsSkip={
                        taskQuestion.questionType_ID === 4 || taskQuestion.questionType_ID === 16
                          ? taskQuestion?.likertScaleName_array
                          : taskQuestion?.MSTCompatibility_IDs
                      }
                      setLikertScaleChoicesOptions={setLikertScaleChoicesOptions}
                      likertScaleChoicesOptions={likertScaleChoicesOptions}
                      skipLogicInputValue={skipLogicInputValue}
                      setSkipLogicInputValue={setSkipLogicInputValue}
                      likertScaleOptions={likertScaleOptions}
                      setLikertScaleOptions={setLikertScaleOptions}
                      isSkipEnabled={isSkipEnabled}
                    />
                  ) : (
                    ''
                  )}
                  {(taskQuestion.presignedUrlQuestionImage || selectedFile) && (
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        marginTop: '15px',
                        marginBottom: '10px',
                        marginLeft: '-15px',
                        marginRight: '20px',
                        padding: '0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}
                    >
                      {/*
                      <img
                        src={preview ? preview : taskQuestion.presignedUrlQuestionImage}
                        width="250"
                        height="150"
                        style={{ objectFit: 'contain', marginTop: '5%', marginBottom: '5%' }}
                      />
                      */}

                      {/* Conditional rendering based on mediaType */}
                      {/* {taskQuestion?.fileType == 0 ? (
                        <img
                          src={preview ? preview : taskQuestion.presignedUrlQuestionImage}
                          width="250"
                          height="150"
                          style={{ objectFit: 'contain', float: 'left' }}
                          alt="Preview"
                        />
                      ) : (
                        <video width="350" height="240" controls style={{ float: 'left' }}>
                          <source src={preview ? preview : taskQuestion.presignedUrlQuestionImage} />
                          Your browser does not support the video tag.
                        </video>
                        )
                      } */}
                      {taskQuestion?.fileType === 0 ? (
                        // Image Preview
                        <img
                          src={preview ? preview : taskQuestion.presignedUrlQuestionImage}
                          width="250"
                          height="150"
                          style={{ objectFit: 'cover', display: 'block', margin: '0', padding: '0' }}
                          alt="Preview"
                        />
                      ) : taskQuestion?.fileType === 1 ? (
                        // Video Preview
                        <video
                          width="350"
                          height="240"
                          controls
                          style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
                        >
                          <source src={preview ? preview : taskQuestion.presignedUrlQuestionImage} />
                          Your browser does not support the video tag.
                        </video>
                      ) : taskQuestion?.fileType === 2 ? (
                        // PDF Preview
                        <iframe
                          src={preview ? preview : taskQuestion.presignedUrlQuestionImage}
                          title="PDF Preview"
                          style={{ width: '100%', height: '500px', display: 'block', border: 'none' }}
                        />
                      ) : null}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <TextArea
                      key={`question-name-${taskQuestion.question_ID || 'new'}`}
                      showCharacters={false}
                      fontSize="14px"
                      value={taskQuestion.questionName || ''}
                      placeholder={'Add question'}
                      width="390px"
                      minRows={1}
                      maxRows={10}
                      useDefaultValue={true}
                      style={{
                        color: 'var(--greyBlue)',
                        fontWeight: '700',
                        fontSize: '16px',
                        fontFamily: 'Arial, "Noto Sans", "Noto Sans Devanagari", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", sans-serif'
                      }}
                      onChange={e => {
                        const newValue = e.target.value;
                        setTaskQuestion(prev => ({ ...prev, questionName: newValue, isEdit: true }));
                      }}
                      onFocus={() => handleInputFocus('questionName')}
                      onClick={() => handleInputFocus('questionName')}
                    />

                    <TextArea
                      key={`question-desc-${taskQuestion.question_ID || 'new'}`}
                      showCharacters={false}
                      fontSize="14px"
                      value={desc || ''}
                      placeholder={'Add a description'}
                      width="390px"
                      minRows={1}
                      maxRows={10}
                      useDefaultValue={true}
                      style={{
                        color: 'var(--greyBlue)',
                        fontWeight: '400',
                        fontSize: '16px',
                        fontFamily: 'Arial, "Noto Sans", "Noto Sans Devanagari", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", sans-serif'
                      }}
                      onChange={e => {
                        setDesc(e.target.value);
                        setTaskQuestion(prev => ({ ...prev, questionDescription: e.target.value, isEdit: true }));
                      }}
                      onFocus={() => handleInputFocus('questionDescription')}
                      onClick={() => handleInputFocus('questionDescription')}
                    />

                    {/* On-screen Keyboard (questionName/questionDescription) */}
                    {isQuestionKeyboardVisible && (
                      <div ref={keyboardContainerRef}>
                        <VirtualKeyboard
                          isVisible={showKeyboard}
                          fieldKey={currentInputField}
                          value={getKeyboardValue()}
                          onChange={handleKeyboardChange}
                          onClose={handleKeyboardClose}
                        />
                      </div>
                    )}
                  </div>
                  {checkboxDisplay == 1 ? (
                    <>
                      <Heading
                        color={'#000000'}
                        className="mt-4 text-uppercase"
                        type={'6'}
                        fontSize={'0.9rem'}
                        fontWeight={'700'}
                      >
                        Display this question IF These conditions are met
                      </Heading>
                      <div className="d-flex">
                        {!hideNormalDisplay && (
                        <div>
                          <div className="d-flex mt-2 mb-3" style={{ columnGap: '1.2rem' }}>
                            {taskQuestion?.selectedQuestionSectionAttached !== undefined && (
                              <DesignDropdown
                                key={`sd-section-${taskQuestion.selectedQuestionSectionAttached}`}
                                options={formTaskSections
                                  .filter((_, index) => index <= sectionIndex)
                                  .map((_, index) => ({
                                    value: index + 1,
                                    label: index + 1
                                  }))}
                                value={[
                                  {
                                    label: taskQuestion.selectedQuestionSectionAttached,
                                    value: taskQuestion.selectedQuestionSectionAttached
                                  }
                                ]}
                                controlStyleWidth="60px"
                                onChange={e => {
                                  setDropdownQuestions(Number(e.value) - 1);
                                  setDropdownSectionIndex(Number(e.value) - 1);
                                }}
                              />
                            )}
                            {taskQuestion?.selectedQuestionSectionAttached == undefined && (
                              <DesignDropdown
                                options={formTaskSections
                                  .filter((_, index) => index <= sectionIndex)
                                  .map((_, index) => ({
                                    value: index + 1,
                                    label: index + 1
                                  }))}
                                controlStyleWidth="60px"
                                onChange={e => {
                                  setDropdownQuestions(Number(e.value) - 1);
                                  setDropdownSectionIndex(Number(e.value) - 1);
                                }}
                              />
                            )}
                            <DesignDropdown
                              key={`sd-q-${singleDisplayQuestion?.question_ID || 'none'}`}
                              options={dropDownQuestionOptions}
                              value={
                                (() => {
                                  if (!singleDisplayQuestion?.questionName) {
                                    return [];
                                  }

                                  const matchingOption = Array.isArray(dropDownQuestionOptions)
                                    ? dropDownQuestionOptions.find(
                                        option =>
                                          String(option.questionId) === String(singleDisplayQuestion.question_ID)
                                      )
                                    : null;

                                  if (matchingOption) {
                                    return [matchingOption];
                                  }

                                  return [{
                                    label: singleDisplayQuestion.questionName,
                                    value: singleDisplayQuestion.question_ID,
                                    questionId: singleDisplayQuestion.question_ID
                                  }];
                                })()
                              }
                              controlStyleWidth="150px"
                              onChange={e => {
                                const index = Number(e.value);
                                const selectedQuestion =
                                  formTaskSections[dropdownSectionIndex]?.questionName_array?.[index];

                                setTaskQuestion(prev => {
                                  const updated = { ...prev, selectedQuestion };
                                  return updated;
                                });
                              }}
                              isDisabled={!Array.isArray(dropDownQuestionOptions) || dropDownQuestionOptions.length === 0}
                            />

                            {singleDisplayQuestion ? (
                              <>
                                <Heading className="mt-2" type={'6'} fontSize={'0.9rem'}>
                                  IF
                                </Heading>

                                {[1, 2, 4, 7].includes(singleDisplayQuestion?.questionType_ID) ? (
                                  <DesignDropdown
                                    key={`sd-choice-${taskQuestion?.selectedChoiceID || answerChoice?.value || 'none'}`}
                                    options={singleDisplayQuestion?.choiceName_array?.map(val => ({
                                      value: val.choiceName_ID || val.id || val.QuestionChoice_ID,
                                      label: val.choiceName
                                    }))}
                                    value={singleDisplayChoiceValue}
                                    controlStyleWidth="80px"
                                    onChange={e => {
                                      const choice = singleDisplayQuestion?.choiceName_array?.find(
                                        c =>
                                          String(c.choiceName_ID || c.id || c.QuestionChoice_ID) ===
                                          String(e.value)
                                      );
                                      const label = choice?.choiceName || e.label;
                                      setAttachedChoice(label);
                                      setAnswerChoice({ label, value: e.value });
                                      setTaskQuestion(prev => ({
                                        ...prev,
                                        selectedChoiceID: e.value
                                      }));
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 16 ? (
                                  <DesignDropdown
                                    key={`sd-nps-${taskQuestion?.npsScale_ID ?? 'none'}`}
                                    options={singleDisplayQuestion?.likertScaleName_array?.map(
                                      (val, index) => ({
                                        value: val.questionLikertScale_ID ?? index,
                                        label: `${index}`
                                      })
                                    )}
                                    value={(() => {
                                      const id = taskQuestion?.npsScale_ID;
                                      if (id == null || id === '') return [];
                                      const scales = singleDisplayQuestion?.likertScaleName_array || [];
                                      const asIndex = Number(id);
                                      if (
                                        Number.isInteger(asIndex) &&
                                        asIndex >= 0 &&
                                        asIndex <= 10 &&
                                        scales[asIndex] &&
                                        (scales[asIndex].questionLikertScale_ID == null ||
                                          String(scales[asIndex].questionLikertScale_ID) === String(id) ||
                                          scales.findIndex(
                                            s => String(s.questionLikertScale_ID) === String(id)
                                          ) < 0)
                                      ) {
                                        return [
                                          {
                                            label: `${asIndex}`,
                                            value: scales[asIndex].questionLikertScale_ID ?? asIndex
                                          }
                                        ];
                                      }
                                      const byIdIndex = scales.findIndex(
                                        item => String(item.questionLikertScale_ID) === String(id)
                                      );
                                      if (byIdIndex < 0) return []; // never show "-1"
                                      return [
                                        {
                                          label: `${byIdIndex}`,
                                          value: scales[byIdIndex].questionLikertScale_ID ?? byIdIndex
                                        }
                                      ];
                                    })()}
                                    controlStyleWidth="80px"
                                    onChange={e => {
                                      setTaskQuestion(prev => ({ ...prev, npsScale_ID: e.value }));
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 4 ? (
                                  <DesignDropdown
                                    key="sd-likert-scale"
                                    controlStyleWidth="60px"
                                    options={singleDisplayQuestion?.likertScaleName_array?.map(val => ({
                                      value: val.questionLikertScale_ID,
                                      label: val.questionLikertScaleName
                                    }))}
                                    onChange={e => {
                                      const scale = singleDisplayQuestion?.likertScaleName_array?.find(
                                        s => String(s.questionLikertScale_ID) === String(e.value)
                                      );
                                      setLikertSelectedChoice({
                                        label: scale?.questionLikertScaleName || e.label,
                                        value: e.value
                                      });
                                      setTaskQuestion(prev => ({ ...prev, LikertScaleID: e.value }));
                                    }}
                                    value={(() => {
                                      const id = likertSelectedChoice?.value ?? taskQuestion?.LikertScaleID;
                                      const scale = singleDisplayQuestion?.likertScaleName_array?.find(
                                        s => String(s.questionLikertScale_ID) === String(id)
                                      );
                                      return scale
                                        ? [{ label: scale.questionLikertScaleName, value: scale.questionLikertScale_ID }]
                                        : [];
                                    })()}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 14 ? (
                                  <DesignDropdown
                                    key="sd-rank-choice"
                                    options={singleDisplayQuestion?.choiceName_array?.map(val => ({
                                      value: val.choiceName_ID || val.id || val.QuestionChoice_ID,
                                      label: val.choiceName
                                    }))}
                                    onChange={e => {
                                      const choice = singleDisplayQuestion?.choiceName_array?.find(
                                        c =>
                                          String(c.choiceName_ID || c.id || c.QuestionChoice_ID) ===
                                          String(e.value)
                                      );
                                      const label = choice?.choiceName || e.label;
                                      setAnswerChoice({ label, value: e.value });
                                      setAttachedChoice(label);
                                      setTaskQuestion(prev => ({
                                        ...prev,
                                        selectedChoiceID: e.value
                                      }));
                                    }}
                                    controlStyleWidth="60px"
                                    value={singleDisplayChoiceValue}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 14 ||
                                singleDisplayQuestion?.questionType_ID == 3 ||
                                singleDisplayQuestion?.questionType_ID == 9 ||
                                singleDisplayQuestion?.questionType_ID == 11 ? (
                                  <DesignDropdown
                                    key="sd-rank-op"
                                    options={singleDisplayQuestion?.MSTCompatibility_IDs?.map(val => ({
                                      value: val.id,
                                      label: val.Operations
                                    }))}
                                    controlStyleWidth="60px"
                                    onChange={e => {
                                      const op = singleDisplayQuestion?.MSTCompatibility_IDs?.find(
                                        v => String(v.id) === String(e.value)
                                      );
                                      setComparibilityValue({
                                        label: op?.Operations || e.label,
                                        value: e.value
                                      });
                                      setTaskQuestion(prev => ({
                                        ...prev,
                                        selectedComparibilityID: e.value
                                      }));
                                    }}
                                    value={(() => {
                                      const id =
                                        comparibilityValue?.value ?? taskQuestion?.selectedComparibilityID;
                                      const op = singleDisplayQuestion?.MSTCompatibility_IDs?.find(
                                        v => String(v.id) === String(id)
                                      );
                                      return op ? [{ label: op.Operations, value: op.id }] : [];
                                    })()}
                                  />
                                ) : (
                                  ''
                                )}

                                {[14, 3, 11, 9].includes(singleDisplayQuestion?.questionType_ID) ? (
                                  <InputText
                                    showCharacters={false}
                                    fontSize="14px"
                                    value={taskQuestion.inputValue ? taskQuestion.inputValue : inputAnswer}
                                    placeholder={'Value'}
                                    width="70px"
                                    style={{
                                      color: 'var(--greyBlue)',
                                      fontWeight: '700',
                                      fontSize: '16px'
                                    }}
                                    onChange={e => {
                                      let tempTaskQuestion = taskQuestion;
                                      tempTaskQuestion = { ...tempTaskQuestion, inputValue: e.target.value };

                                      setTaskQuestion(tempTaskQuestion);
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 5 ||
                                singleDisplayQuestion?.questionType_ID == 6 ? (
                                  <InputText
                                    showCharacters={false}
                                    fontSize="14px"
                                    value={
                                      answerChoice?.label
                                        ? answerChoice.label
                                        : taskQuestion.inputValue ||
                                          taskQuestion?.ParentquestionAttached?.ifContains ||
                                          ''
                                    }
                                    placeholder={'Enter words'}
                                    width="100px"
                                    style={{
                                      color: 'var(--greyBlue)',
                                      fontWeight: '700',
                                      fontSize: '16px'
                                    }}
                                    onChange={e => {
                                      let tempTaskQuestion = taskQuestion;
                                      tempTaskQuestion = { ...tempTaskQuestion, inputValue: e.target.value };
                                      setTaskQuestion(tempTaskQuestion);
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 5 ||
                                singleDisplayQuestion?.questionType_ID == 6 ? (
                                  <DesignDropdown
                                    options={[{ value: 'Contains', label: 'Contains' }]}
                                    value={[{ value: 'Contains', label: 'Contains' }]}
                                    controlStyleWidth="100px"
                                    onChange={e => {
                                      // console.log('HALO');
                                    }}
                                  />
                                ) : (
                                  ''
                                )}

                                {singleDisplayQuestion?.questionType_ID == 1 ||
                                singleDisplayQuestion?.questionType_ID == 2 ||
                                singleDisplayQuestion?.questionType_ID == 4 ||
                                singleDisplayQuestion?.questionType_ID == 7 ||
                                singleDisplayQuestion?.questionType_ID == 16 ? (
                                  <DesignDropdown
                                    options={[{ value: 'Is selected', label: 'Is Selected' }]}
                                    value={[
                                      {
                                        label: 'Is selected',
                                        value: 'Is selected'
                                      }
                                    ]}
                                    controlStyleWidth="100px"
                                    onChange={e => {
                                      // console.log('HALO');
                                    }}
                                  />
                                ) : (
                                  ''
                                )}
                              </>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        )}
                        {displayList.length == 0 && (
                          <button
                            onClick={addNewLogicItem}
                            style={{
                              backgroundImage: `url(${addDisplayConditionIcon})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              width: '1rem',
                              height: '1rem',
                              border: 'none',
                              padding: 0,
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              alignSelf: 'center'
                            }}
                          />
                        )}
                      </div>
                      
                      {displayList.map((item, index) => {
                        // Skip rendering if ID matches
                      const shouldSkipLogic =
                      (taskQuestion?.ParentquestionAttached?.ORLogic?.length > 0 ||
                        taskQuestion?.ParentquestionAttached?.ANDLogic?.length > 0) &&
                      item?.parentQuestion_ID === attachedQuestionAfter?.question_ID;

                        return (
                          <React.Fragment key={item.id}>
                             {/* {!shouldSkipLogic && ( */}
                              <>
                              {(!hideNormalDisplay || index !== 0) && (
                            <ActionButton size="small" normalBtn={true} left="1%">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <SpanCustom>{item?.condition.label}</SpanCustom>
                                
                                  <ImgCustom src={whiteDropdown} alt="White Dropdown" />
                              
                              </div>
                                <TaskMenu className="task-menu" width="80px">
                                  {customLogics.map((logic, i) => (
                                    <BtnAssignGroup
                                      key={i}
                                      className="border-bottom"
                                      onClick={() => {
                                        const updatedList = displayList.map((it, idx) =>
                                          idx === index ? { ...it, condition: logic } : it
                                        );
                                        // Ensure sequence numbers are maintained when condition changes
                                        setDisplayList(recalculateSequences(updatedList));
                                        setDuplicateLogicError('');
                                      }}
                                    >
                                      <Text type="5" className="text-left">
                                        {logic.label}
                                      </Text>
                                    </BtnAssignGroup>
                                  ))}
                                </TaskMenu>
                            </ActionButton>
                              )}

                        <div className="d-flex" style={{ gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                             
                              <ConditionalDisplayLogic
                                checkboxDisplay={checkboxDisplay}
                                taskQuestion={{
                                  ...taskQuestion,
                                  // Scope section to this display row so "add new" doesn't show previous section.
                                  selectedQuestionSectionAttached: item?.selectedQuestionSectionAttached
                                }}
                                formTaskSections={formTaskSections}
                                sectionIndex={sectionIndex}
                                dropDownQuestionOptions={dropDownQuestionOptions}
                                dropdownSectionIndex={dropdownSectionIndex}
                                setDropdownQuestions={setDropdownQuestions}
                                setDropdownSectionIndex={setDropdownSectionIndex}
                                setTaskQuestion={setTaskQuestion}
                                attachedChoiceAfter={attachedChoiceAfter}
                                likertSelectedChoice={likertSelectedChoice}
                                answerChoice={answerChoice}
                                inputAnswer={inputAnswer}
                                comparibilityValue={comparibilityValue}
                                setLikertSelectedChoice={setLikertSelectedChoice}
                                setORLogic={setORLogic}
                                setAndLogic={setAndLogic}
                                logicValue={item.condition.value}
                                item={item}
                                hideId={attachedQuestionAfter?.question_ID}
                                selectedSectionValue={item.selectedQuestionSectionAttached}
                                onSectionChange={sectionValue => {
                                  setDisplayList(prev => prev.map((it, idx) => idx === index ? { ...it, selectedQuestionSectionAttached: sectionValue, selectedQuestionAttached: undefined, parentQuestionAttached: undefined, parentQuestion_ID: undefined, parentQuestionType: undefined, parentQuestionType_ID: undefined } : it));
                                  setDuplicateLogicError('');
                                }}
                                onQuestionChange={selectedQuestionAttached => {
                                  setDisplayList(prev =>
                                    prev.map((it, idx) =>
                                      idx === index
                                        ? {
                                            id: it.id,
                                            condition: it.condition,
                                            parentSequence: it.parentSequence,
                                            questionIndex: it.questionIndex ?? index,
                                            selectedQuestionSectionAttached: it.selectedQuestionSectionAttached,
                                            selectedQuestionAttached,
                                            parentQuestionAttached: selectedQuestionAttached?.question_ID,
                                            parentQuestion_ID: selectedQuestionAttached?.question_ID,
                                            parentQuestionType: selectedQuestionAttached?.questionType_ID,
                                            parentQuestionType_ID: selectedQuestionAttached?.questionType_ID
                                          }
                                        : it
                                    )
                                  );
                                  setDuplicateLogicError('');
                                }}
                                questionIndex={index}
                                parentSequence={item.parentSequence || index + 2}

                              />
                              {index === displayList.length - 1 && (
                                    <button
                                      onClick={addNewLogicItem}
                                      style={{
                                        backgroundImage: `url(${addDisplayConditionIcon})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat',
                                        width: '1rem',
                                        height: '1rem',
                                        border: 'none',
                                        padding: 0,
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                        marginLeft: '0.5rem'
                                      }}
                                    />
                                  )}
                              {(!hideNormalDisplay || index !== 0) && (
                                <>
                                  <button
                                    onClick={() => handleDeleteLogicCondition(item, index)}
                                    style={{
                                      backgroundImage: `url(${CrossIcon})`,
                                      backgroundSize: 'cover',
                                      backgroundPosition: 'center',
                                      backgroundRepeat: 'no-repeat',
                                      width: '1rem',
                                      height: '1rem',
                                      border: 'none',
                                      padding: 0,
                                      backgroundColor: 'transparent',
                                      cursor: 'pointer',
                                      marginLeft: '0.5rem'
                                    }}
                                  />
                                </>
                              )}
                            </div>
                            </>
                           {/* )} */}
                          </React.Fragment>
                        );
                      })}
                    </>
                  ) : (
                    ''
                  )}

                  {isSkipEnabled && isInterview === false ? (
                    <SkipLogicControls
                      skipToOptions={skipToOptions}
                      skipToTarget={skipToTarget}
                      setSkipToTarget={setSkipToTarget}
                      options={options}
                      skipIfChoice={skipIfChoice}
                      setSkipIfChoice={setSkipIfChoice}
                      conditionOptions={conditionOptions}
                      skipCondition={skipCondition}
                      setSkipCondition={setSkipCondition}
                      taskQuestion={taskQuestion}
                      edit={taskQuestion.isEdit}
                      questionType_ID={taskQuestion.questionType_ID}
                      // OptionsSkip={taskQuestion.likertScaleName_array}
                      OptionsSkip={
                        taskQuestion.questionType_ID === 4 || taskQuestion.questionType_ID === 16
                          ? taskQuestion?.likertScaleName_array
                          : taskQuestion?.MSTCompatibility_IDs
                      }
                      setLikertScaleChoicesOptions={setLikertScaleChoicesOptions}
                      likertScaleChoicesOptions={likertScaleChoicesOptions}
                      skipLogicInputValue={skipLogicInputValue}
                      setSkipLogicInputValue={setSkipLogicInputValue}
                      likertScaleOptions={likertScaleOptions}
                      setLikertScaleOptions={setLikertScaleOptions}
                      isSkipEnabled={isSkipEnabled}
                    />
                  ) : (
                    ''
                  )}

                  <Text type="2">
                    {visible ? (
                      <p className="mt-2" style={{ color: 'red', fontSize: '11px' }}>
                        Please enter the question
                      </p>
                    ) : null}
                  </Text>
                  {isInterview === false ? (
                    <div>
                      <FileInputWrapper>
                        <HiddenFileInput
                          id="file-input"
                          type="file"
                          accept="image/*,video/*,.pdf,.doc,.docx"
                          onChange={e => onSelectFile(e)}
                        />
                        <FileInputLabel htmlFor="file-input">
                          {selectedFile || preview ? 'Change File' : 'Choose File'}
                        </FileInputLabel>
                        {(selectedFile || (preview && !selectedFile)) && (
                          <FileNameDisplay>
                            {selectedFile ? selectedFile.name : 'File uploaded'}
                          </FileNameDisplay>
                        )}
                      </FileInputWrapper>
                      {fileError && (
                        <Text type="4" fontWeight="400" color="red">
                          {fileError}
                        </Text>
                      )}
                      <div>
                        {preview && (
                          <div
                            style={{
                              position: 'relative',
                              display: 'inline-block',
                              marginTop: '15px',
                              marginBottom: '10px',
                              marginLeft: '-15px',
                              marginRight: '20px',
                              padding: '0',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              maxWidth: taskQuestion?.fileType === 1 ? '360px' : taskQuestion?.fileType === 2 ? '100%' : '260px'
                            }}
                          >
                            {/* Conditionally render image or video based on mediaType */}
                            {taskQuestion?.fileType  == 0 ? (
                              <img
                                src={preview}
                                width="250"
                                height="150"
                                style={{ objectFit: 'cover', display: 'block', margin: '0', padding: '0' }}
                                alt="Preview"
                              />
                            ) : taskQuestion?.fileType == 1 ? (
                              <video 
                                width="350" 
                                height="240" 
                                controls 
                                style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
                              >
                                <source src={preview} />
                                Your browser does not support the video tag.
                              </video>
                            ) : taskQuestion?.fileType == 2 ? (
                              <iframe
                                src={preview}
                                title="PDF Preview"
                                style={{ width: '100%', height: '500px', display: 'block', border: 'none' }}
                              />
                            ) : null}

          {/* Close (×) button to remove file */}
          <span
            onClick={handleRemoveFile}
            style={{
              position: 'absolute',
              top: '4px',
              right: '6px',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#fff',
              backgroundColor: '#f44336',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              textAlign: 'center',
              lineHeight: '24px',
              cursor: 'pointer',
              userSelect: 'none'
            }}
            title="Remove"
          >
            ×
          </span>
        </div>
      )}
    </div>
  </div>
) : (
  ' '
)}

                </>
              )}
              <br />
              <div>
                {
                  {
                    1: (
                      <SingleChoiceQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        handleOptions={handleOptions}
                        onChoiceFocus={handleChoiceInputFocus}
                        shuffleEnabled={shuffleEnabled}
                        onToggleChoiceShuffle={handleToggleChoiceShuffleTag}
                      />
                    ),
                    2: (
                      <MultipleChoiceQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        handleOptions={handleOptions}
                        onChoiceFocus={handleChoiceInputFocus}
                        shuffleEnabled={shuffleEnabled}
                        onToggleChoiceShuffle={handleToggleChoiceShuffleTag}
                      />
                    ),
                    3: <SliderQuestion edit={taskQuestion?.isEdit} options={options} handleOptions={handleOptions} />,
                    4: (
                      <LikerTQuestion
                        options={options}
                        LikertScaleType={LikertScaleType}
                        likerTOptions={likerTOptions}
                        edit={taskQuestion?.isEdit}
                        handleOptions={handleOptions}
                        handleScaleOptions={(scaleOptions, scaleType) =>
                          handleQuestionFields('likertScaleOptions', scaleOptions, scaleType)
                        }
                        initialScaleType={taskQuestion?.LikertScaleType || taskQuestion?.scaleType}
                        customLikertError={customLikertError}
                        onChoiceFocus={handleChoiceInputFocus}
                        onLikertCustomFocus={handleLikertCustomFocus}
                        shuffleEnabled={shuffleEnabled}
                        onToggleScaleShuffle={handleToggleScaleShuffleTag}
                        onToggleChoiceShuffle={handleToggleChoiceShuffleTag}
                        scaleShuffleDisabled={isShuffleScaleDisabled}
                      />
                    ),
                    5: (
                      <TextAnswerQuestion
                        edit={taskQuestion?.isEdit}
                        // description={taskQuestion?.questionDescription}
                        characterLimit={taskQuestion?.characterLimit}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    6: (
                      <TextAnswerQuestion
                        edit={taskQuestion?.isEdit}
                        // description={taskQuestion?.questionDescription}
                        characterLimit={taskQuestion?.characterLimit}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    7: (
                      <SingleSevenQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        handleOptions={handleOptions}
                        onChoiceFocus={handleChoiceInputFocus}
                        shuffleEnabled={shuffleEnabled}
                        onToggleChoiceShuffle={handleToggleChoiceShuffleTag}
                      />
                    ),
                    8: (
                      <ImageUploadQuestion
                        edit={taskQuestion?.isEdit}
                        numberOfFiles={taskQuestion?.numberOfFiles}
                        maxFileSizeInMB={taskQuestion?.maxFileSizeInMB}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    9: (
                      <ScreenerTypeQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        savedOption={taskQuestion?.savedOption}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    10: (
                      <RecordingQuestion
                        edit={taskQuestion?.isEdit}
                        buttonName={taskQuestion?.buttonName}
                        tagList={tagList}
                        options={options}
                        handleTextFields={handleQuestionFields}
                        handleTags={handleTags}
                        handleDeleteTag={handleDeleteTag}
                      />
                    ),
                    11: (
                      <ValueQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        savedOption={taskQuestion?.savedOption}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    12: (
                      <AudioQuestion
                        edit={taskQuestion?.isEdit}
                        numberOfFiles={taskQuestion?.numberOfFiles}
                        maxFileSizeInMB={5}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    13: (
                      <VideoQuestion
                        edit={taskQuestion?.isEdit}
                        numberOfFiles={taskQuestion?.numberOfFiles}
                        maxFileSizeInMB={10}
                        handleTextFields={handleQuestionFields}
                      />
                    ),
                    14: (
                      <RankingQuestion
                        edit={taskQuestion?.isEdit}
                        options={options}
                        handleOptions={handleOptions}
                        onChoiceFocus={handleChoiceInputFocus}
                        shuffleEnabled={shuffleEnabled}
                        onToggleChoiceShuffle={handleToggleChoiceShuffleTag}
                      />
                    ),
                    15: (
                      <VideoQuestion
                        edit={taskQuestion?.isEdit}
                        numberOfFiles={taskQuestion?.numberOfFiles}
                        maxFileSizeInMB={10}
                        handleTextFields={handleQuestionFields}
                      />
                    ),

                    16: (
                      <NpsQuestion
                        options={options}
                        LikertScaleType={LikertScaleType}
                        likerTOptions={likerTOptions}
                        edit={taskQuestion?.isEdit}
                        handleOptions={handleOptions}
                        handleScaleOptions={(scaleOptions, scaleType) =>
                          handleQuestionFields('likertScaleOptions', scaleOptions, scaleType)
                        }
                        initialScaleType={taskQuestion?.LikertScaleType || taskQuestion?.scaleType}
                      />
                    )
                  }[question.questionType_ID]
                }
                {(getShuffleChoiceValidationMessage(
                  taskQuestion?.questionType_ID,
                  shuffleEnabled,
                  taskQuestion?.choiceName_array
                ) ||
                  getShuffleScaleValidationMessage(
                    taskQuestion?.questionType_ID,
                    taskQuestion?.scaleType || taskQuestion?.LikertScaleType,
                    shuffleEnabled,
                    resolveLikertScaleRows(taskQuestion)
                  )) && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        color: 'red',
                        marginTop: '4px'
                      }}
                    >
                      {getShuffleChoiceValidationMessage(
                        taskQuestion?.questionType_ID,
                        shuffleEnabled,
                        taskQuestion?.choiceName_array
                      ) ||
                        getShuffleScaleValidationMessage(
                          taskQuestion?.questionType_ID,
                          taskQuestion?.scaleType || taskQuestion?.LikertScaleType,
                          shuffleEnabled,
                          resolveLikertScaleRows(taskQuestion)
                        )}
                    </span>
                  )}
              </div>
              {/* On-screen Keyboard (choice inputs) */}
              {isChoiceKeyboardVisible && (
                <div ref={keyboardContainerRef} className="mt-2">
                  <VirtualKeyboard
                    isVisible={showKeyboard}
                    fieldKey={currentInputField}
                    value={getKeyboardValue()}
                    onChange={handleKeyboardChange}
                    onClose={handleKeyboardClose}
                  />
                </div>
              )}
            </div>
            <div className="col-md-4 col-xs-6 d-flex flex-column justify-content-start">
              {/* <DesignDropdown
                options={questionTypes}
                value={questionTypesDefaultValue}
                isDisabled={!taskQuestion?.isEdit}
                onChange={e => changeQuestionType(e)}
              /> */}
              <Tooltip
                active={showTooltipisdResponded && taskQuestion?.isResponded === 1 && taskQuestion?.isEdit}
              >{`You Can't Change Question, Response is already submitted.`}</Tooltip>

              <div
                onMouseEnter={() => setShowTooltipisResponded(true)}
                onMouseLeave={() => setShowTooltipisResponded(false)}
                onClick={() => setShowTooltipisResponded(true)}
              >
                <DesignDropdown
                  options={questionTypes}
                  value={questionTypesDefaultValue}
                  isDisabled={!taskQuestion?.isEdit || taskQuestion?.isResponded === 1}
                  onChange={e => changeQuestionType(e)}
                />
              </div>

              <div className="d-flex mt-2 mb-3">
                {showCheckbox && !taskQuestion.isEdit ? (
                  <>
                    <StyledCheckbox
                      fontSize="0.875rem"
                      className="mt-2 pb-0 "
                      outerHeight="22px"
                      outerWidth="22px"
                      borderRadius="5px"
                      border="1px solid #9EBE21"
                    >
                      <input
                        type="checkbox"
                        checked={taskQuestion?.question_ID && checkBoxRequired}
                        onChange={handleCheckboxrequired}
                        disabled={true}
                      />
                      <span className="outer">
                        <Icon className="material-icons-round inner">done</Icon>
                      </span>
                    </StyledCheckbox>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: 'lighter',
                        color: 'var(--textColor)',
                        marginTop: '0.625rem'
                      }}
                    >
                      {' Required Question'}
                    </span>
                  </>
                ) : (
                  showCheckbox && (
                    <>
                      <StyledCheckbox
                        fontSize="0.875rem"
                        className="mt-2 pb-0 "
                        outerHeight="22px"
                        outerWidth="22px"
                        borderRadius="5px"
                        border="1px solid #9EBE21"
                      >
                        <input type="checkbox" checked={checkBoxRequired} onChange={handleCheckboxrequired} />
                        <span className="outer">
                          <Icon className="material-icons-round inner">done</Icon>
                        </span>
                      </StyledCheckbox>
                      <span
                        style={{
                          fontSize: '13px',
                          fontWeight: 'lighter',
                          color: 'var(--textColor)',
                          marginTop: '0.625rem'
                        }}
                      >
                        {' '}
                        Required Question
                      </span>
                    </>
                  )
                )}
              </div>
              <div className="mt-2 mb-3">
                {showCheckbox &&
                  ([1, 2, 7, 14, 4, 16].includes(Number(taskQuestion.questionType_ID))) && (
                    <>
                      <div
                        className="d-flex"
                        style={{
                          cursor: isShuffleScaleDisabled ? 'not-allowed' : undefined,
                          opacity: isShuffleScaleDisabled ? 0.65 : undefined
                        }}
                      >
                        <StyledCheckbox
                          fontSize="0.875rem"
                          className="mt-2 pb-0"
                          outerHeight="22px"
                          outerWidth="22px"
                          borderRadius="5px"
                          border="1px solid #9EBE21"
                          style={{ cursor: isShuffleScaleDisabled ? 'not-allowed' : undefined }}
                        >
                          <input
                            type="checkbox"
                            checked={Number(shuffleEnabled) === 1}
                            onChange={handleShuffleToggle}
                            disabled={!taskQuestion.isEdit || isShuffleScaleDisabled}
                          />
                          <span className="outer">
                            <Icon className="material-icons-round inner">done</Icon>
                          </span>
                        </StyledCheckbox>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 'lighter',
                            color: 'var(--textColor)',
                            marginTop: '0.625rem',
                            cursor: isShuffleScaleDisabled ? 'not-allowed' : undefined
                          }}
                        >
                          {[4, 16].includes(Number(taskQuestion.questionType_ID))
                            ? ' Shuffle'
                            : ' Shuffle Options'}
                        </span>
                      </div>
                    </>
                  )}
              </div>
              {isInterview === false && showCheckbox && taskQuestion.question_ID !== null ? (
                <>
                  <Heading className="mt-5" type={'6'} fontSize={'0.9rem'}>
                    Question Behaviour
                  </Heading>
                  <div
                    className="d-flex"
                    title={isSectionShuffleOn ? conditionDisableMessage : undefined}
                    onClick={blockConditionToggle}
                    onMouseDown={blockConditionToggle}
                    style={{
                      cursor: isSectionShuffleOn ? 'not-allowed' : undefined,
                      opacity: isSectionShuffleOn ? 0.65 : undefined
                    }}
                  >
                    {showCheckbox && !taskQuestion.isEdit ? (
                      <>
                        <StyledCheckbox
                          fontSize="0.875rem"
                          className="mt-2 pb-0 "
                          outerHeight="22px"
                          outerWidth="22px"
                          borderRadius="5px"
                          border="1px solid #9EBE21"
                        >
                          <input
                            type="checkbox"
                            checked={taskQuestion?.question_ID && checkboxDisplay}
                            onChange={handleDisplayCheckbox}
                            disabled={true}
                          />
                          <span className="outer">
                            <Icon className="material-icons-round inner">done</Icon>
                          </span>
                        </StyledCheckbox>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 'lighter',
                            color: 'var(--textColor)',
                            marginTop: '0.625rem'
                          }}
                        >
                          {' Display '}
                        </span>
                      </>
                    ) : (
                      showCheckbox && (
                        <>
                          <StyledCheckbox
                            fontSize="0.875rem"
                            className="mt-2 pb-0 "
                            outerHeight="22px"
                            outerWidth="22px"
                            borderRadius="5px"
                            border="1px solid #9EBE21"
                            onClick={blockConditionToggle}
                            style={{
                              cursor: isSectionShuffleOn ? 'not-allowed' : undefined,
                              pointerEvents: isSectionShuffleOn ? 'none' : undefined
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!checkboxDisplay}
                              onChange={handleDisplayCheckbox}
                              disabled={isSectionShuffleOn}
                              readOnly={isSectionShuffleOn}
                            />
                            <span className="outer">
                              <Icon className="material-icons-round inner">done</Icon>
                            </span>
                          </StyledCheckbox>
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 'lighter',
                              color: 'var(--textColor)',
                              marginTop: '0.625rem',
                              cursor: isSectionShuffleOn ? 'not-allowed' : undefined
                            }}
                          >
                            {' '}
                            Display
                          </span>
                        </>
                      )
                    )}
                  </div>
                  <div
                    className="d-flex mb-3"
                    title={isSectionShuffleOn ? conditionDisableMessage : undefined}
                    onClick={blockConditionToggle}
                    onMouseDown={blockConditionToggle}
                    style={{
                      cursor: isSectionShuffleOn ? 'not-allowed' : undefined,
                      opacity: isSectionShuffleOn ? 0.65 : undefined
                    }}
                  >
                    {showCheckbox &&
                    !taskQuestion.isEdit &&
                    [1, 2, 3, 4, 5, 6, 7, 11, 14, 16].includes(taskQuestion.questionType_ID) ? (
                      <>
                        <StyledCheckbox
                          fontSize="0.875rem"
                          className="mt-2 pb-0 "
                          outerHeight="22px"
                          outerWidth="22px"
                          borderRadius="5px"
                          border="1px solid #9EBE21"
                        >
                          <input
                            type="checkbox"
                            checked={taskQuestion?.question_ID && isSkipEnabled}
                            onChange={handleSkipChange}
                            disabled={true}
                          />
                          <span className="outer">
                            <Icon className="material-icons-round inner">done</Icon>
                          </span>
                        </StyledCheckbox>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 'lighter',
                            color: 'var(--textColor)',
                            marginTop: '0.625rem'
                          }}
                        >
                          {' Skip '}
                        </span>
                      </>
                    ) : (
                      showCheckbox &&
                      taskQuestion.isEdit &&
                      isQuestionSavedForSkipLogic &&
                      [1, 2, 3, 4, 5, 6, 7, 11, 14, 16].includes(taskQuestion.questionType_ID) && (
                        <>
                          <StyledCheckbox
                            fontSize="0.875rem"
                            className="mt-2 pb-0 "
                            outerHeight="22px"
                            outerWidth="22px"
                            borderRadius="5px"
                            border="1px solid #9EBE21"
                            onClick={blockConditionToggle}
                            style={{
                              cursor: isSectionShuffleOn ? 'not-allowed' : undefined,
                              pointerEvents: isSectionShuffleOn ? 'none' : undefined
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!isSkipEnabled}
                              onChange={handleSkipChange}
                              disabled={isSectionShuffleOn}
                              readOnly={isSectionShuffleOn}
                            />
                            <span className="outer">
                              <Icon className="material-icons-round inner">done</Icon>
                            </span>
                          </StyledCheckbox>
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: 'lighter',
                              color: 'var(--textColor)',
                              marginTop: '0.625rem',
                              cursor: isSectionShuffleOn ? 'not-allowed' : undefined
                            }}
                          >
                            {' '}
                            Skip
                          </span>
                        </>
                      )
                    )}
                  </div>
                </>
              ) : (
                ''
              )}
            </div>
          </div>

          <Text type="2">
            {optionVisible ? (
              <p className="mt-2" style={{ color: 'red', fontSize: '11px' }}>
                Fill in all details
              </p>
            ) : null}
          </Text>
          <Text type="2">
            {scaletypeVisible ? (
              <p className="mt-2" style={{ color: 'red', fontSize: '11px' }}>
                Fill all scale details
              </p>
            ) : null}
          </Text>
          {skipLogicError && taskQuestion.isEdit && (
            <Text type="2">
              <p className="mt-2" style={{ color: 'red', fontSize: '11px' }}>
                {skipLogicError}
              </p>
            </Text>
          )}

          {taskQuestion?.isEdit ? (
            <>
              {taskQuestion.question_ID == null ? (
                <div
                  className="iconContainer"
                  style={{ position: 'absolute', right: '-20px', top: '-20px' }}
                  onClick={() => handleDeleteQuestion(sectionIndex, questionIndex)}
                >
                  <Icon style={{ cursor: 'pointer' }}>close</Icon>
                </div>
              ) : (
                <div
                  className="iconContainer"
                  style={{ position: 'absolute', right: '-20px', top: '-20px' }}
                  onClick={() => {
                    setAddQuestionDisable(false);
                    handleSetEditing();
                    if (refreshTask) refreshTask();
                  }}
                >
                  <Icon style={{ cursor: 'pointer' }}>close</Icon>
                </div>
              )}

              <div className="question-action d-flex align-items-center">
                <CustomButton
                  variant="outlined"
                  buttonColor="var(--sapGreen)"
                  data-virtualkeyboard-ignore="true"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={() => {
                    // Ensure the latest typed values are committed before saving.
                    
                    storeOrUpdateQuestion(
                      taskQuestionRef.current?.question_ID,
                      questionIndex,
                      taskQuestionRef.current
                    );
                    // Close keyboard after triggering save.
                    handleKeyboardClose();
                  }}
                  value={switchvalue}
                  className="mb-0"
                >
                  <Icon>done</Icon>
                  Save Changes
                </CustomButton>
              </div>
            </>
          ) : (
            <div className="question-action d-flex align-items-center">
              <div onClick={() => handleEditQuestion(sectionIndex, questionIndex)}>
                <ActionIcon>create</ActionIcon>
              </div>
              <div onClick={() => duplicateQuestion(sectionIndex, questionIndex, setCheckBoxRequired)}>
                <ActionIcon>content_copy</ActionIcon>
              </div>
              <div onClick={() => setPopup(!popup)}>
                <ActionIcon>delete</ActionIcon>
                {/* <CustomPopup
                  onClose={closeHandler}
                  showed={popup}
                  heading="Confirm delete"
                  secondheading="Are you sure you want to delete this Question?"
                  secondheading2="Once deleted, it cant be retrieved."
                >
                  <CustomButtont
                    fontSize="14px"
                    fontWeight="300"
                    color="black"
                    size="medium"
                    style={{ justifyContent: 'space-between', marginRight: '20px' }}
                    onClick={() => {
                      handleDeleteQuestion(sectionIndex, questionIndex);
                      setPopup(false);
                    }}
                  >
                    Delete
                  </CustomButtont>
                </CustomPopup> */}
                <Modal open={popup} onClose={closeHandler} center showCloseIcon={false}>
                  <div
                    class="container"

                    // style={{ maxWidth: '100%', width: '100%', height: '100%', borderRadius: '1rem' }}
                  >
                    <Heading
                      fontSize="20px"
                      fontWeight="700"
                      breakHeading={false}
                      className="mb-3"
                      style={{
                        lineHeight: '19px',

                        textAlign: 'left',

                        paddingTop: '1.5rem'
                      }}
                    >
                      Confirm delete?
                    </Heading>

                    <Heading
                      fontSize="14px"
                      fontWeight="400"
                      breakHeading={false}
                      className="mb-3 pb-0 justify-content-start"
                      style={{
                        whiteSpace: 'pre-line',

                        lineHeight: '19px',

                        textAlign: 'left'
                      }}
                    >
                      Are you sure you want to delete this Question?
                    </Heading>

                    <Heading
                      fontSize="14px"
                      fontWeight="400"
                      breakHeading={false}
                      className="mb-3 pb-0"
                      style={{
                        whiteSpace: 'pre-line',

                        lineHeight: '19px',

                        marginTop: '-18px',

                        textAlign: 'left'
                      }}
                    >
                      Once deleted, it can't be retrieved.
                    </Heading>

                    <div className="row" style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        className="ml-3"
                        size="small"
                        width="20px"
                        variant="outlined"
                        data-dismiss="modal"
                        buttonColor="white"
                        onClick={() => setPopup(false)}
                        style={{ border: '1px solid rgba(56, 69, 79, 0.67)', color: 'rgba(56, 69, 79, 0.69)' }}
                      >
                        No
                      </Button>

                      <CustomButtont
                        fontSize="14px"
                        fontWeight="300"
                        color="black"
                        size="medium"
                        style={{ justifyContent: 'space-between', marginRight: '20px' }}
                        onClick={() => {
                          handleDeleteQuestion(sectionIndex, questionIndex);

                          setPopup(false);
                        }}
                      >
                        Delete
                      </CustomButtont>
                    </div>
                  </div>
                </Modal>
              </div>
            </div>
          )}
          <br />
          <div></div>
          {enableEdit && questionmessage && (
            <div style={{
                position: 'absolute',
                bottom: '-14px',
                left: '16px',       // ← aligns to left like original
                color: popupColor,
                fontWeight: 'bold',
                backgroundColor: 'white',
                padding: '2px 12px',
                borderRadius: '4px',
                zIndex: 10,
                whiteSpace: 'nowrap'
              }}>
              {questionmessage}
            </div>
          )}
        </QuestionWrapper>
      </CustomCard>
    )
  );
}

export default DesignQuestion;
